import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation,useSearchParams } from 'react-router-dom';
import { Slider } from 'antd';
import { BackButton } from '../../components/common/BackButton';
import { RoleDropdown, AlertMessage, RowItem, VideoEditSkeleton, TranslationAdjustment } from '../../components/common';
import VolumeAdjustmentPopup from '../../components/common/VolumeAdjustmentPopup';
import './videoEdit.scss';

// 导入类型定义
type Subtitle = Series.Subtitle;

// 扩展Window接口以支持预取音频
declare global {
    interface Window {
        prefetchedTransAudios?: Map<string, HTMLAudioElement>;
    }
}
import { PlayCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { Watermark } from 'antd';
import PlayheadController from '@/components/common/Timeline/PlayheadController';

// 字幕渲染控制器 - 在rvfc帧内执行，避免阻塞主线程
const useSubtitleRenderer = (videoRef: React.RefObject<HTMLVideoElement | null>, renderSubtitleToCanvas: (time: number) => void) => {
    const lastSubtitleTimeRef = useRef<number>(0);
    const rvfcIdRef = useRef<number | null>(null);
    const rafIdRef = useRef<number | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const renderFrame = () => {
            const currentTime = video.currentTime || 0;
            // 只在时间变化较大时才重新渲染字幕
            if (Math.abs(currentTime - lastSubtitleTimeRef.current) > 0.1) {
                renderSubtitleToCanvas(currentTime);
                lastSubtitleTimeRef.current = currentTime;
            }
        };

        const startRender = () => {
            // 优先使用 requestVideoFrameCallback
            const anyVideo = video as any;
            if (typeof anyVideo.requestVideoFrameCallback === 'function') {
                const cb = (_now: number, _metadata: any) => {
                    renderFrame();
                    rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                };
                rvfcIdRef.current = anyVideo.requestVideoFrameCallback(cb);
                return;
            }
            // 降级到 rAF
            const loopRAF = () => {
                renderFrame();
                rafIdRef.current = window.requestAnimationFrame(loopRAF);
            };
            rafIdRef.current = window.requestAnimationFrame(loopRAF);
        };

        const stopRender = () => {
            if (rvfcIdRef.current != null) {
                const anyVideo2 = video as any;
                if (typeof anyVideo2.cancelVideoFrameCallback === 'function') {
                    anyVideo2.cancelVideoFrameCallback(rvfcIdRef.current);
                }
                rvfcIdRef.current = null;
            }
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };

        const onPlay = () => startRender();
        const onPause = () => stopRender();
        const onEnded = () => stopRender();

        if (!video.paused) startRender();
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('ended', onEnded);

        return () => {
            stopRender();
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('ended', onEnded);
        };
    }, [videoRef, renderSubtitleToCanvas]);
};
import { useAlert } from '../../hooks/useAlert';
import { useUserRole } from '../../hooks/useUserRole';
import { getTaskStatusApi, mergeTaskApi, getSubtitlesListApi } from '../../service/task';
import { VIDEO_CDN_PREFIX } from '../../config/video';
import Hls from 'hls.js';
import { getSeriesListApi } from "../../service/series";
import { updateSubtitleApi, createSubtitleApi, retranslateSubtitleApi, audioRegenerateApi, changeSubtitleSpeakerApi, adjustVolumeAndSpeedApi, adjustTimestampRangeApi, backTranslateApi, proofreadApi, adjustTaskVolumeApi, generateVideoApi, updatePostTextApi } from "../../service/subtitles";
import { addVideoSpeakerApi, getSpeakersByTaskApi, renameVideoSpeakerApi, getSeriesSpeakersApi, promoteSpeakerApi, deleteSpeakerApi } from "../../service/speakers";
import SubtitleDemo, { SubtitleDemoRef } from '../subtitleDemo/SubtitleDemo';
import { audioManager } from '../../utils/audioManager';
import { getLanguageName } from '../../utils/languageUtils';
import { processVideoUrl, processTransAudioUrl } from '../../utils/videoUrl';

// ================== 【必须加】全局变量：专门对付顽固音频 ==================
// 定义在组件外部的全局变量，只要赋值了，无论组件是否卸载，它都还在
// 不受 React 生命周期和异步等待的影响
let GLOBAL_PLAYING_AUDIO_ID: string | null = null;
// ================== 【必须加】结束 ==================

// 角色数据配置已移到组件内部
const VideoEdit: React.FC = () => {
    const [searchParams] = useSearchParams();
    const seriesId = searchParams.get('seriesId');
    const urlLangCode = searchParams.get('langCode');
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { alertConfig, hideAlert, showSuccess, showError } = useAlert();
    const { isAdmin, roleInfo } = useUserRole();

    // 获取当前用户信息
    const [userInfo, setUserInfo] = useState<any>(null);
    
    useEffect(() => {
        // 从localStorage获取用户信息
        const storedUserInfo = localStorage.getItem("userInfo");
        if (storedUserInfo) {
            try {
                const user = JSON.parse(storedUserInfo);
                setUserInfo(user);
            } catch (error) {
                console.error('解析用户信息失败:', error);
            }
        }
    }, []);

    // 获取显示的用户名
    const displayName = userInfo?.nickname || userInfo?.username || '用户';

    // 从location.state获取视频名称，如果没有则使用默认值
    const videoName = useMemo(() => {
        return (location.state as any)?.videoName || `视频${id || '10'}`;
    }, [location.state, id]);

    // 从location.state获取是否从纯净版进入的状态
    const isFromPure = useMemo(() => {
        return (location.state as any)?.isPure === true;
    }, [location.state]);

    // 视频数据状态
    const [videoData, setVideoData] = useState<Task.GetTaskStatusResult | null>(null);

    // 【终极修复】创建一个“特洛伊木马”式的 videoData 对象
    // 策略：
    // 1. useMemo 保证对象引用（外壳）极度稳定，只在 ID/URL 变化时才重新创建。
    //    这能欺骗 SubtitleDemo 内部的 useEffect，让它认为视频源没有变，从而绝不重载播放器。
    // 2. 使用单独的稳定 subtitles 数组（localSubtitlesForDemo），避免数组引用变化导致重载。
    const videoDataForDemo = useMemo(() => {
        if (!videoData) return null;
        // 创建一个浅拷贝，这样我们可以安全地修改它而不影响原始 state
        return { ...videoData };
    }, [
        videoData?.id,
        videoData?.video_hls_url,
        (videoData as any)?.pure_video_hls_url
    ]);

    // 【修复补充】为 localSubtitles 也创建“特洛伊木马”对象 (Stable Array)
    // 防止 subtitles 数组引用变化导致 SubtitleDemo 重载视频
    const localSubtitlesForDemo = useMemo(() => {
        return [] as Task.SubtitlesLegacy[];
    }, [id]); // 仅在 ID 变化时（切换视频）才重建

    // 将最新的 subtitles 数据同步到稳定的数组引用中
    if (localSubtitlesForDemo && videoData?.subtitles) {
        localSubtitlesForDemo.length = 0;
        videoData.subtitles.forEach(item => localSubtitlesForDemo.push(item));
    }

    // 【关键步骤】将稳定的字幕数组“注入”到稳定的 videoData 外壳中
    // 这样 videoDataForDemo 的引用地址没变，内部的 subtitles 引用也没变
    if (videoDataForDemo) {
        (videoDataForDemo as any).subtitles = localSubtitlesForDemo;
    }

    const [isLoadingVideo, setIsLoadingVideo] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    
    // 字幕更新状态
    const [updatingSubtitles, setUpdatingSubtitles] = useState<{ [key: number]: boolean }>({});
    const [subtitleUpdateErrors, setSubtitleUpdateErrors] = useState<{ [key: number]: string }>({});
    
    // 全局操作状态
    const [isGlobalRetranslating, setIsGlobalRetranslating] = useState(false);
    const [isGlobalRedubbing, setIsGlobalRedubbing] = useState(false);
    // 跟踪每个台词的翻译/配音状态
    const [subtitleProcessingStates, setSubtitleProcessingStates] = useState<Record<number, { isRetranslating: boolean; isRedubbing: boolean }>>({});
    const [showGlobalRetranslateDropdown, setShowGlobalRetranslateDropdown] = useState(false);
    const [globalCustomText, setGlobalCustomText] = useState('');
    const [isArrowHovered, setIsArrowHovered] = useState(false); // 箭头悬停状态
    const [isAdjustingSpeed, setIsAdjustingSpeed] = useState(false); // 倍速调节加载状态
    const [showBackTranslate, setShowBackTranslate] = useState(false); // 显示/隐藏回译内容
    const [backTranslateData, setBackTranslateData] = useState<Task.Subtitles[]>([]); // 回译数据
    const [isBackTranslating, setIsBackTranslating] = useState(false); // 回译加载状态
    // 背景音乐音量调节弹窗状态（backgroundVolume 初始值由接口数据决定）
    const [showBackgroundVolumePopup, setShowBackgroundVolumePopup] = useState(false);
    const [backgroundVolumePopupPosition, setBackgroundVolumePopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [backgroundVolume, setBackgroundVolume] = useState<number>(1);
    // 待同步的时间轴调节数据
    const [pendingTimelineUpdates, setPendingTimelineUpdates] = useState<Map<number, {
        audio_speed: number;
        post_start_time: string;
        post_end_time: string;
    }>>(new Map());
    
    // 历史记录面板状态 - 只允许一个面板打开
    const [openHistorySubtitleId, setOpenHistorySubtitleId] = useState<number | null>(null);
    
    // 音量映射表，存储每个字幕的最新音量设置
    const [volumeMap, setVolumeMap] = useState<Map<number, number>>(new Map());
    
    // 倍速映射表，存储每个字幕的最新倍速设置
    const [speedMap, setSpeedMap] = useState<Map<number, number>>(new Map());
    
    // 更新音量映射表的函数
    const handleUpdateVolume = useCallback((subtitleId: number, volume: number) => {
        setVolumeMap(prev => {
            const newMap = new Map(prev);
            newMap.set(subtitleId, volume);
            return newMap;
        });
        // 更新音量映射表
    }, []);

    // 更新倍速映射表的函数
    const handleUpdateSpeed = useCallback((subtitleId: number, speed: number) => {
        setSpeedMap(prev => {
            const newMap = new Map(prev);
            newMap.set(subtitleId, speed);
            return newMap;
        });
        // 更新倍速映射表
    }, []);

    // 处理历史记录面板的打开/关闭 - 确保只显示一个
    const handleOpenHistoryPanel = useCallback((subtitleId: number) => {
        setOpenHistorySubtitleId(subtitleId);
    }, []);

    const handleCloseHistoryPanel = useCallback(() => {
        setOpenHistorySubtitleId(null);
    }, []);
    
    // 预加载状态管理
    const [isPreloaded, setIsPreloaded] = useState(false);
    const [leftVideoReady, setLeftVideoReady] = useState(false);
    const [rightVideoReady, setRightVideoReady] = useState(false);
    
    // 背景音控制状态
    const [backgroundAudioEnabled, setBackgroundAudioEnabled] = useState(true);
    
    // 缩放控制状态
    const [zoomLevel, setZoomLevel] = useState(20);
    
    // 编辑页禁用浏览器缩放（键盘、菜单、触控手势）
    useEffect(() => {
        const head = document.head || document.getElementsByTagName('head')[0];
        const existingViewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
        const previousContent = existingViewport?.getAttribute('content') || '';

        // 目标 viewport 内容，明确禁止缩放
        const lockedViewportContent = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';

        let createdViewportMeta: HTMLMetaElement | null = null;
        if (existingViewport) {
            existingViewport.setAttribute('content', lockedViewportContent);
        } else {
            createdViewportMeta = document.createElement('meta');
            createdViewportMeta.setAttribute('name', 'viewport');
            createdViewportMeta.setAttribute('content', lockedViewportContent);
            head.appendChild(createdViewportMeta);
        }

        // 键盘缩放 Ctrl +/-, Ctrl + 0（含等号键、数字小键盘）
        const onKeyDown = (e: KeyboardEvent) => {
            const isCtrlOrMeta = e.ctrlKey || e.metaKey; // 兼容 macOS
            if (!isCtrlOrMeta) return;
            const key = e.key;
            const blocked = key === '+' || key === '-' || key === '=' || key === '0' || key === 'Add' || key === 'Subtract';
            if (blocked) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // 鼠标滚轮缩放（Ctrl/Meta + 滚轮）
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || (e as any).metaKey) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // 触控：多指捏合与双击缩放
        const onTouchStart = (e: TouchEvent) => {
            if (e.touches && e.touches.length > 1) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        let lastTouchEnd = 0;
        const onTouchEnd = (e: TouchEvent) => {
            const now = Date.now();
            if (now - lastTouchEnd <= 300) {
                e.preventDefault();
                e.stopPropagation();
            }
            lastTouchEnd = now;
        };

        // iOS Safari 专用：阻止手势缩放
        const onGestureStart = (e: Event) => {
            e.preventDefault();
            (e as any).stopPropagation?.();
        };

        document.addEventListener('keydown', onKeyDown, { capture: true });
        document.addEventListener('wheel', onWheel, { passive: false, capture: true });
        document.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
        document.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
        document.addEventListener('gesturestart', onGestureStart as EventListener, { passive: false, capture: true } as any);

        return () => {
            // 恢复/移除 viewport 设置
            if (existingViewport) {
                if (previousContent) {
                    existingViewport.setAttribute('content', previousContent);
                } else {
                    existingViewport.removeAttribute('content');
                }
            } else if (createdViewportMeta) {
                head.removeChild(createdViewportMeta);
            }

            document.removeEventListener('keydown', onKeyDown, { capture: true } as any);
            document.removeEventListener('wheel', onWheel as EventListener, { capture: true } as any);
            document.removeEventListener('touchstart', onTouchStart as EventListener, { capture: true } as any);
            document.removeEventListener('touchend', onTouchEnd as EventListener, { capture: true } as any);
            document.removeEventListener('gesturestart', onGestureStart as EventListener, { capture: true } as any);
        };
    }, []);

    // TimelineEditor实例引用
    const timelineEditorRef = useRef<TimelineEditor | null>(null);
    
    // 防抖定时器引用
    const zoomDebounceRef = useRef<NodeJS.Timeout | null>(null);
    // 节流定时器引用
    const zoomThrottleRef = useRef<NodeJS.Timeout | null>(null);
    // 上次缩放值引用，用于避免重复计算
    const lastZoomValueRef = useRef<number>(20);
    
    // 缩放控制处理函数（实时更新UI，延迟执行重计算）
    const handleZoomChange = (value: number) => {
        setZoomLevel(value);
        
        // 节流控制：只有在值变化超过阈值时才执行轻量级更新
        const zoomThreshold = 2; // 缩放值变化阈值
        if (Math.abs(value - lastZoomValueRef.current) >= zoomThreshold) {
            // 清除之前的节流定时器
            if (zoomThrottleRef.current) {
                clearTimeout(zoomThrottleRef.current);
            }
            
            // 设置节流定时器，限制更新频率
            zoomThrottleRef.current = setTimeout(() => {
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.updateZoomLight(value);
                    lastZoomValueRef.current = value;
                }
            }, 16); // 约60fps的更新频率
        }
    };
    
    // 拖动结束后的处理函数
    const handleZoomAfterChange = (value: number) => {
        // 清除之前的防抖定时器
        if (zoomDebounceRef.current) {
            clearTimeout(zoomDebounceRef.current);
        }
        
        // 清除节流定时器
        if (zoomThrottleRef.current) {
            clearTimeout(zoomThrottleRef.current);
        }
        
        // 设置防抖定时器，延迟执行完整的缩放操作
        zoomDebounceRef.current = setTimeout(() => {
            if (timelineEditorRef.current) {
                timelineEditorRef.current.updateZoomComplete(value);
                lastZoomValueRef.current = value;
            }
        }, 200); // 减少防抖延迟到200ms
    };
    
    const handleZoomIn = () => {
        const newValue = Math.min(100, zoomLevel + 10);
        setZoomLevel(newValue);
        if (timelineEditorRef.current) {
            timelineEditorRef.current.updateZoom(newValue);
        }
    };
    
    const handleZoomOut = () => {
        const newValue = Math.max(0, zoomLevel - 10);
        setZoomLevel(newValue);
        if (timelineEditorRef.current) {
            timelineEditorRef.current.updateZoom(newValue);
        }
    };
    
    // 安全的setVideoData函数，确保ID一致性
    const setVideoDataSafe = useCallback((newVideoData: Task.GetTaskStatusResult | null) => {
        // 设置videoData
        if (newVideoData && id) {
            const currentId = parseInt(id);
            if (newVideoData.id !== currentId) {
                // 检测到videoData ID不一致，已自动修正
                newVideoData.id = currentId;
            }
        }
        setVideoData(newVideoData);
    }, [id]);

    // 语言代码到中文名称的映射

    // 这些状态现在由每行的 RowItem 组件独立管理

    // 角色选择状态管理 - 每行独立的角色选择
    const [rowSelectedRoles, setRowSelectedRoles] = useState<{ [key: number]: string }>({
        1: '角色',
        2: '角色',
        3: '角色',
        4: '角色',
        5: '角色',
        6: '角色',
        7: '角色',
        8: '角色',
        9: '角色',
        10: '角色',
        11: '角色',
        12: '角色',
        13: '角色',
        14: '角色',
        15: '角色',
        16: '角色'
    });
    
    // 剧集音色库标记状态管理 - 记录哪些台词已添加到剧集音色库
    const [seriesVoiceLibraryMarkers, setSeriesVoiceLibraryMarkers] = useState<{ [key: number]: boolean }>({});
    // 从字幕数据中提取说话人信息
    const [roles, setRoles] = useState<Array<{ key: string; label: string }>>([]);
    
    // 剧集说话人角色数据（带说话人ID）
    const [seriesSpeakers, setSeriesSpeakers] = useState<Array<{ id: number; key: string; label: string; description: string }>>([]);

    // 角色编辑状态管理
    const [editingRoleKey, setEditingRoleKey] = useState<string | null>(null);
    const [editingRoleName, setEditingRoleName] = useState('');
    
    // 防重复调用状态
    const [isSavingRole, setIsSavingRole] = useState(false);

    // 从字幕数据中提取说话人信息（备用方案）
    const extractSpeakersFromSubtitles = (subtitles: Task.SubtitlesLegacy[]) => {
        const speakerMap = new Map<string, { key: string; label: string }>();
        
        subtitles.forEach(subtitle => {
            if (subtitle.speakers && subtitle.speakers.speaker_name) {
                const speakerName = subtitle.speakers.speaker_name;
                // 使用说话人名称作为唯一标识，避免重复
                if (!speakerMap.has(speakerName)) {
                    speakerMap.set(speakerName, {
                        key: `speaker_${speakerName}`, // 使用说话人名称作为key的一部分
                        label: speakerName
                    });
                }
            }
        });
        
        // 转换为角色数组格式
        const speakerRoles = Array.from(speakerMap.values());
        
        // 从字幕数据提取的说话人列表（已去重）
        return speakerRoles;
    };

    // 防重复调用的状态
    const [isFetchingSpeakers, setIsFetchingSpeakers] = useState(false);
    const [isFetchingSeriesSpeakers, setIsFetchingSeriesSpeakers] = useState(false);
    const [isPromotingSpeaker, setIsPromotingSpeaker] = useState(false);

    // 去重函数：确保roles数组中没有重复的角色
    const deduplicateRoles = (roles: Array<{ key: string; label: string }>) => {
        const roleMap = new Map<string, { key: string; label: string }>();
        
        roles.forEach(role => {
            // 使用label作为唯一标识，如果已存在则保留第一个
            if (!roleMap.has(role.label)) {
                roleMap.set(role.label, role);
            } else {
                // 发现重复的角色名称，已跳过
            }
        });
        
        const uniqueRoles = Array.from(roleMap.values());
        if (uniqueRoles.length !== roles.length) {
            // 角色去重完成
        }
        
        return uniqueRoles;
    };

    // 获取说话人列表
    const fetchSpeakersList = async (taskId: number) => {
        // 防止重复调用
        if (isFetchingSpeakers) {
            // 正在获取说话人列表，跳过重复调用
            return [];
        }

        try {
            setIsFetchingSpeakers(true);
            const response = await getSpeakersByTaskApi(taskId);
            
            // 处理不同的响应格式
            let speakersData = null;
            
            // 情况1: 标准格式 {success: true, data: [...]}
            if (response && typeof response === 'object' && 'success' in response && response.success && response.data && Array.isArray(response.data)) {
                speakersData = response.data;
                // 使用标准格式的说话人数据
            }
            // 情况2: 直接返回数组格式 [...]
            else if (Array.isArray(response)) {
                speakersData = response;
                // 使用直接数组格式的说话人数据
            }
            // 情况3: 其他格式，尝试从response中提取数据
            else if (response && typeof response === 'object') {
                // 尝试不同的可能字段名
                const possibleDataFields = ['data', 'speakers', 'list', 'items'];
                for (const field of possibleDataFields) {
                    if ((response as any)[field] && Array.isArray((response as any)[field])) {
                        speakersData = (response as any)[field];
                        // 从字段中提取说话人数据
                        break;
                    }
                }
            }
            
            if (speakersData && Array.isArray(speakersData) && speakersData.length > 0) {
                // 原始说话人数据列表
                
                // 将API返回的说话人数据转换为角色数组格式，并进行去重
                const speakerMap = new Map<string, { key: string; label: string }>();
                
                speakersData.forEach(speaker => {
                    const speakerName = speaker.speaker_name;
                    // 使用说话人名称作为唯一标识，避免重复
                    if (!speakerMap.has(speakerName)) {
                        speakerMap.set(speakerName, {
                            key: `speaker_${speaker.id}`,
                            label: speakerName
                        });
                    } else {
                        // 发现重复的说话人名称
                    }
                });
                
                const speakerRoles = Array.from(speakerMap.values());
                
                // 转换后的角色数据（已去重）
                
                // 使用去重函数确保没有重复
                const finalRoles = deduplicateRoles(speakerRoles);
                setRoles(finalRoles);
                // 成功设置说话人列表到roles状态
                return finalRoles;
            } else {
                // 无法从API响应中提取有效的说话人数据
                return [];
            }
        } catch (error: any) {
            console.error('❌ 获取说话人列表出错:', error);
            return [];
        } finally {
            setIsFetchingSpeakers(false);
        }
    };

    // 获取剧集说话人角色列表
    const fetchSeriesSpeakersList = async (seriesId: number, langCode?: string) => {
        // 防止重复调用
        if (isFetchingSeriesSpeakers) {
            // 获取剧集说话人角色列表，跳过重复调用
            return [];
        }

        // langCode 缺失时不调用接口
        if (!langCode) {
            return [];
        }

        try {
            setIsFetchingSeriesSpeakers(true);
            // 调用 getSeriesSpeakersApi
            const response = await getSeriesSpeakersApi(seriesId, langCode);
            
            // 处理API响应数据
            let speakersData = null;
            
            // 情况1: 标准格式 {success: true, data: [...]}
            if (response && typeof response === 'object' && 'success' in response && response.success && response.data && Array.isArray(response.data)) {
                speakersData = response.data;
            }
            // 情况2: 直接返回数组格式 [...] (根据您提供的示例数据)
            else if (Array.isArray(response)) {
                speakersData = response;
            }
            
            if (speakersData && Array.isArray(speakersData) && speakersData.length > 0) {
                // 将API返回的说话人数据转换为角色数组格式（带id）
                const speakerRoles = speakersData.map((speaker: any) => ({
                    id: speaker.id,
                    key: `series_speaker_${speaker.id}`,
                    label: speaker.speaker_name,
                    description: speaker.description || ''
                }));
                
                setSeriesSpeakers(speakerRoles);
                
                // 🍎 进入页面时，将获取的角色列表备份到 localStorage
                const storageKey = `seriesSpeakers_${seriesId}_${langCode}`;
                try {
                    localStorage.setItem(storageKey, JSON.stringify(speakerRoles));
                    console.log('🍎 角色列表已备份到本地:', speakerRoles);
                } catch (error) {
                    console.error('🍎 备份角色列表到本地失败:', error);
                }
                
                return speakerRoles;
            } else {
                // 即使为空数组，也保存到本地
                const storageKey = `seriesSpeakers_${seriesId}_${langCode}`;
                try {
                    localStorage.setItem(storageKey, JSON.stringify([]));
                    console.log('🍎 角色列表已备份到本地（空列表）');
                } catch (error) {
                    console.error('🍎 备份角色列表到本地失败:', error);
                }
                return [];
            }
        } catch (error: any) {
            return [];
        } finally {
            setIsFetchingSeriesSpeakers(false);
        }
    };

    // 进入视频编辑页后，根据 seriesId 主动拉取一次剧集说话人角色列表
    useEffect(() => {
        // 只有在已经拿到 langCode 时才请求，避免先发一次缺少 langCode 的请求
        const langCodeToUse = videoData?.langCode || videoData?.lang_code;
        if (!langCodeToUse) {
            return;
        }

        // 优先使用 URL 上的 seriesId，其次使用视频数据里的 series_id
        const rawSeriesId = seriesId || (videoData?.series_id != null ? String(videoData.series_id) : null);
        if (!rawSeriesId) {
            return;
        }
        const parsedId = parseInt(rawSeriesId, 10);
        if (Number.isNaN(parsedId)) {
            return;
        }
        
        // 视频编辑页初始化调用 fetchSeriesSpeakersList
        fetchSeriesSpeakersList(parsedId, langCodeToUse);
    }, [seriesId, videoData?.series_id, videoData?.langCode, videoData?.lang_code]);

    // 将字幕添加到剧集音色库
    const handlePromoteToSeriesSpeaker = useCallback(async (speakerId: number, subtitleId: number): Promise<void> => {
        if (isPromotingSpeaker) {
            return;
        }

        try {
            setIsPromotingSpeaker(true);
            const response = await promoteSpeakerApi(speakerId, subtitleId);

            if (response.code === 0) {
                showSuccess('已添加到剧集音色库');
            } else {
                // 业务失败也抛出错误，让调用方（RowItem）走 catch 分支，不更新本地说话人
                const msg = response.msg || '添加到剧集音色库失败';
                showError(msg);
                throw new Error(msg);
            }
        } catch (error: any) {
            // request 封装在业务错误时会把 msg/message 填到 error.message，这里直接展示
            showError(error?.message || '添加到剧集音色库失败，请重试');
            // 向上传递错误，阻止调用方执行“本地成功更新”逻辑
            throw error;
        } finally {
            setIsPromotingSpeaker(false);
        }
    }, [isPromotingSpeaker, showSuccess, showError]);

    // 删除字幕处理函数
    const handleDeleteSubtitle = useCallback((subtitleId: number) => {
        // 删除字幕
        
        // 从videoData中移除对应的字幕
        if (videoData && videoData.subtitles) {
            const updatedSubtitles = videoData.subtitles.filter(subtitle => subtitle.id !== subtitleId);
            setVideoDataSafe({
                ...videoData,
                subtitles: updatedSubtitles
            });
        }
        
        // 从角色选择状态中移除对应的记录
        setRowSelectedRoles(prev => {
            const newRoles = { ...prev };
            delete newRoles[subtitleId];
            return newRoles;
        });
        
        // 从剧集音色库标记中移除对应的记录
        setSeriesVoiceLibraryMarkers(prev => {
            const newMarkers = { ...prev };
            delete newMarkers[subtitleId];
            return newMarkers;
        });
        
        // 从更新状态中移除对应的记录
        setUpdatingSubtitles(prev => {
            const newUpdating = { ...prev };
            delete newUpdating[subtitleId];
            return newUpdating;
        });
        
        // 从错误状态中移除对应的记录
        setSubtitleUpdateErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[subtitleId];
            return newErrors;
        });
        
        showSuccess('字幕删除成功');
    }, [videoData, setVideoDataSafe, showSuccess]);
    // 新增字幕处理函数
    const handleAddSubtitle = useCallback(async (newSubtitleParams: {
        task_id: number;
        origin_text: string;
        post_start_time: string;
        post_end_time: string;
        origin_start_time: string;
        origin_end_time: string;
        speaker_id: number;
    }, isLocalInsert: boolean = false, currentSubtitleId?: number) => {
        // 新增字幕
        
        if (isLocalInsert) {
            // 本地插入：直接添加到本地数据，不请求接口
            if (videoData && videoData.subtitles) {
                // 找到当前字幕的索引，在其后插入新字幕
                let insertIndex = -1;
                let currentSubtitle = null;
                if (currentSubtitleId !== undefined) {
                    // 通过currentSubtitleId查找当前字幕
                    insertIndex = videoData.subtitles.findIndex(sub => sub.id === currentSubtitleId);
                    currentSubtitle = videoData.subtitles[insertIndex];
                    // 查找当前字幕ID
                }
                
                // 生成临时ID（使用负数避免与真实ID冲突）
                const tempId = -(Date.now() + Math.random() * 1000);
                
                const newSubtitle: Task.SubtitlesLegacy = {
                    id: tempId,
                    task_id: videoData.id,
                    origin_text: newSubtitleParams.origin_text,
                    post_text: '',
                    post_start_time: newSubtitleParams.post_start_time,
                    post_end_time: newSubtitleParams.post_end_time,
                    origin_start_time: newSubtitleParams.origin_start_time,
                    origin_end_time: newSubtitleParams.origin_end_time,
                    is_episode_voice: false,
                    origin_audio: '',
                    trans_audio: '',
                    subtitle_id: 0,
                    audio_speed: 1.0,
                    volume_gain_db: 1,
                    speakers: {
                        id: currentSubtitle?.speakers?.id || newSubtitleParams.speaker_id,
                        series_id: currentSubtitle?.speakers?.series_id || 0,
                        task_id: videoData.id,
                        speaker_name: currentSubtitle?.speakers?.speaker_name || '新说话人',
                        description: currentSubtitle?.speakers?.description || '',
                        is_series_speaker: currentSubtitle?.speakers?.is_series_speaker || false
                    },
                    voice_info: {
                        origin_text: '',
                        origin_start_time: '',
                        origin_end_time: '',
                        origin_audio: ''
                    },
                    words: []
                };
                
                // 如果找不到，则添加到末尾
                if (insertIndex === -1) {
                    insertIndex = videoData.subtitles.length - 1;
                    // 未找到当前字幕，插入到末尾
                }
                
                // 准备插入新字幕
                
                let updatedSubtitles;
                if (insertIndex >= 0) {
                    // 在当前字幕后插入
                    updatedSubtitles = [
                        ...videoData.subtitles.slice(0, insertIndex + 1),
                        newSubtitle,
                        ...videoData.subtitles.slice(insertIndex + 1)
                    ];
                } else {
                    // 如果找不到当前字幕，添加到末尾
                    updatedSubtitles = [...videoData.subtitles, newSubtitle];
                }
                
                setVideoDataSafe({
                    ...videoData,
                    subtitles: updatedSubtitles
                });
                
                // 本地插入完成
            }
        } else {
            // 接口请求：调用API创建字幕
            try {
                // 确定要使用的 subtitleId（基于哪个字幕创建新字幕）
                // 应该使用上一个字幕的真实ID（不是临时ID，不能是负值）
                if (!videoData?.subtitles || videoData.subtitles.length === 0) {
                    throw new Error('无法创建字幕：没有可用的字幕ID');
                }
                
                // 过滤出所有真实字幕（ID为正数）
                const realSubtitles = videoData.subtitles.filter(sub => sub.id > 0);
                
                if (realSubtitles.length === 0) {
                    throw new Error('无法创建字幕：没有可用的真实字幕ID');
                }
                
                // 初始化 subtitleIdToUse，默认使用最后一个真实字幕的ID
                let subtitleIdToUse: number = realSubtitles[realSubtitles.length - 1].id;
                
                if (currentSubtitleId !== undefined) {
                    // 如果提供了 currentSubtitleId（可能是临时ID或真实ID）
                    if (currentSubtitleId > 0) {
                        // 如果是真实ID，直接使用
                        subtitleIdToUse = currentSubtitleId;
                    } else {
                        // 如果是临时ID（负数），找到它在列表中的位置
                        const tempSubtitleIndex = videoData.subtitles.findIndex(sub => sub.id === currentSubtitleId);
                        
                        if (tempSubtitleIndex > 0) {
                            // 找到临时字幕前面的最后一个真实字幕
                            let found = false;
                            for (let i = tempSubtitleIndex - 1; i >= 0; i--) {
                                if (videoData.subtitles[i].id > 0) {
                                    subtitleIdToUse = videoData.subtitles[i].id;
                                    found = true;
                                    break;
                                }
                            }
                            // 如果前面没有真实字幕，使用第一个真实字幕
                            if (!found) {
                                subtitleIdToUse = realSubtitles[0].id;
                            }
                        } else {
                            // 如果临时字幕是第一个或找不到，使用第一个真实字幕
                            subtitleIdToUse = realSubtitles[0].id;
                        }
                    }
                } else {
                    // 如果没有提供 currentSubtitleId，使用最后一个真实字幕的ID
                    subtitleIdToUse = realSubtitles[realSubtitles.length - 1].id;
                }
                
                // 创建字幕使用的 subtitleId
                
                // 新接口格式：只需要 subtitleId 和 originText
                const params: Subtitles.CreateSubtitleParams = {
                    subtitleId: subtitleIdToUse,
                    originText: newSubtitleParams.origin_text
                };
                
                // 调用创建字幕API
                const response = await createSubtitleApi(params);
                // 新增字幕成功
                
                // 检查响应
                if (response.code !== 0 || !response.data) {
                    throw new Error(response.msg || '创建字幕失败');
                }
                
                // 将新接口格式（Task.Subtitles）转换为旧格式（Task.SubtitlesLegacy）
                const newSubtitleData = response.data;
                const newSubtitle: Task.SubtitlesLegacy = {
                    id: newSubtitleData.id,
                    task_id: newSubtitleData.taskId,
                    origin_text: newSubtitleData.originText,
                    post_text: newSubtitleData.postText || '',
                    // 关键点：创建字幕接口不支持传入 post 时间戳，后端返回可能会把 post 时间戳回填为 origin 时间戳
                    // 为了保持“新增片段时计算出的 post 时间戳”不被覆盖，这里优先使用前端计算的新时间戳
                    post_start_time: newSubtitleParams.post_start_time || newSubtitleData.postStartTime || '',
                    post_end_time: newSubtitleParams.post_end_time || newSubtitleData.postEndTime || '',
                    origin_start_time: newSubtitleData.originStartTime,
                    origin_end_time: newSubtitleData.originEndTime,
                    origin_post_time: newSubtitleData.originPostTime,
                    is_episode_voice: newSubtitleData.isEpisodeVoice || false,
                    origin_audio: newSubtitleData.originAudio || '',
                    trans_audio: newSubtitleData.transAudio || '',
                    subtitle_id: newSubtitleData.subtitleId || newSubtitleData.id,
                    audio_speed: newSubtitleData.audioSpeed || 1.0,
                    volume_gain_db: newSubtitleData.volumeGainDb || 1.0,
                    speakers: {
                        id: newSubtitleData.speaker.id,
                        series_id: newSubtitleData.speaker.seriesId,
                        task_id: newSubtitleData.taskId,
                        speaker_name: newSubtitleData.speaker.speakerName,
                        description: newSubtitleData.speaker.description || '',
                        is_series_speaker: newSubtitleData.speaker.isSeriesSpeaker
                    },
                    voice_info: {
                        origin_text: newSubtitleData.originText,
                        origin_start_time: newSubtitleData.originStartTime,
                        origin_end_time: newSubtitleData.originEndTime,
                        origin_audio: newSubtitleData.originAudio
                    },
                    words: newSubtitleData.words?.map(word => ({
                        id: word.id,
                        subtitle_id: word.subtitleId,
                        position: word.position,
                        origin_char: word.originChar,
                        start_ms: word.startMs,
                        end_ms: word.endMs
                    })) || []
                };

                // 编辑页：将 post 时间戳持久化到后端（proofread/纯净版不需要）
                if (!isFromPure && newSubtitle.id > 0 && newSubtitleParams.post_start_time && newSubtitleParams.post_end_time) {
                    try {
                        // API 需要 HH:mm:ss.SSS（点分隔毫秒），本地是 HH:mm:ss,SSS（逗号分隔毫秒）
                        const postStartTimeForApi = newSubtitleParams.post_start_time.replace(',', '.');
                        const postEndTimeForApi = newSubtitleParams.post_end_time.replace(',', '.');

                        await adjustTimestampRangeApi({
                            id: newSubtitle.id,
                            postStartTime: postStartTimeForApi,
                            postEndTime: postEndTimeForApi
                        });
                    } catch (e) {
                        // 不阻断新增流程，但给出提示，避免用户误以为“没生效”
                        console.error('创建字幕后持久化 post 时间戳失败:', e);
                    }
                }
                
                // 更新本地数据
                if (videoData && videoData.subtitles) {
                    
                    // 找到临时字幕并替换为真实字幕
                    // 使用更可靠的查找方式：通过时间戳和临时ID来查找
                    // 查找临时字幕
                    
                    const tempSubtitleIndex = videoData.subtitles.findIndex(sub => 
                        sub.id < 0 && // 临时ID为负数
                        sub.origin_start_time === newSubtitleParams.origin_start_time &&
                        sub.origin_end_time === newSubtitleParams.origin_end_time
                    );
                    
                    // 找到临时字幕索引
                    
                    let updatedSubtitles;
                    if (tempSubtitleIndex >= 0) {
                        // 替换临时字幕
                        updatedSubtitles = [
                            ...videoData.subtitles.slice(0, tempSubtitleIndex),
                            newSubtitle,
                            ...videoData.subtitles.slice(tempSubtitleIndex + 1)
                        ];
                    } else {
                        // 如果找不到临时字幕，添加到末尾
                        updatedSubtitles = [...videoData.subtitles, newSubtitle];
                    }
                    
                    setVideoDataSafe({
                        ...videoData,
                        subtitles: updatedSubtitles
                    });
                    
                    // 字幕替换完成
                }
                
                showSuccess('字幕新增成功');
            } catch (error: any) {
                console.error('新增字幕失败:', error);
                showError(error?.message || '新增字幕失败');
            }
        }
    }, [videoData, setVideoDataSafe, showSuccess, showError, isFromPure]);

    // 字幕拆分/合并/重新配音更新处理函数
    const handleSubtitleUpdate = useCallback((originalSubtitle: Subtitles.GetSubtitleResponse, newSubtitle: Subtitles.GetSubtitleResponse | null) => {
        // 在进行任何基于 videoData 的更新前，先同步待更新的时间轴数据
        // 场景：用户拖动台词后未播放，直接执行重新配音/合并等操作，如果不先同步，
        // 将会使用旧的字幕时间戳，从而把拖动后的位置「拉回去」
        if (timelineEditorRef.current && pendingTimelineUpdates.size > 0) {
            timelineEditorRef.current.syncPendingUpdatesToVideoData(pendingTimelineUpdates);
        }
        
        if (videoData && videoData.subtitles) {
            // 对于合并操作，我们需要找到当前点击的字幕（第一个字幕）
            // 因为合并API返回的是合并后的新字幕，但我们需要更新的是当前点击的那个字幕
            let originalIndex = -1;
            
            if (newSubtitle === null) {
                // 可能是合并操作或重新配音操作
                const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                if (currentSubtitleId) {
                    // 合并操作：使用传递的当前字幕ID
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === currentSubtitleId);
                } else {
                    // 重新配音操作：使用originalSubtitle的id
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                }
            } else {
                // 拆分操作或更换音色操作：使用传递的原始字幕ID来查找
                const originalSubtitleId = (originalSubtitle as any).originalSubtitleId;
                if (originalSubtitleId) {
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitleId);
                } else if (originalSubtitle.id) {
                    // 更换音色操作：使用originalSubtitle.id来查找
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                } else {
                    originalIndex = -1;
                }
            }
            
            if (originalIndex !== -1) {
                const updatedSubtitles = [...videoData.subtitles];
                
                if (newSubtitle === null) {
                    // 判断是合并操作还是重新配音操作
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    
                    if (currentSubtitleId) {
                        // 合并操作：只更新原始字幕，删除下一个字幕
                        // 更新原始字幕，使用合并后的数据，但保留原有的原始音频和音量数据（如果 API 返回空值）
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        updatedSubtitles[originalIndex] = {
                            ...currentSubtitle,
                            id: originalSubtitle.id, // 使用合并后的新ID
                            origin_text: originalSubtitle.origin_text || currentSubtitle.origin_text,
                            // 合并操作后，清空 post_text，因为需要重新翻译合并后的原文
                            post_text: '',
                            post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                            post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                            origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                            origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time,
                            words: originalSubtitle.words || currentSubtitle.words || [],
                            // 更新其他可能变化的字段（只更新非空值）
                            task_id: originalSubtitle.task_id || currentSubtitle.task_id,
                            is_episode_voice: originalSubtitle.is_episode_voice !== undefined ? originalSubtitle.is_episode_voice : currentSubtitle.is_episode_voice,
                            // 保留原有的原始音频数据，除非 API 明确返回新值
                            origin_audio: originalSubtitle.origin_audio || currentSubtitle.origin_audio,
                            // 合并操作后，清空 trans_audio，因为需要重新生成翻译音频
                            trans_audio: '',
                            subtitle_id: originalSubtitle.subtitle_id || currentSubtitle.subtitle_id,
                            // 保留原有的语速和音量数据
                            audio_speed: originalSubtitle.audio_speed || currentSubtitle.audio_speed || 1.0,
                            volume_gain_db: (originalSubtitle.volume_gain_db !== undefined && originalSubtitle.volume_gain_db !== null && originalSubtitle.volume_gain_db !== 0) 
                                ? originalSubtitle.volume_gain_db 
                                : currentSubtitle.volume_gain_db,
                            speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                            voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info
                        };
                        
                        // 删除下一个字幕（如果存在）
                        if (originalIndex + 1 < updatedSubtitles.length) {
                            updatedSubtitles.splice(originalIndex + 1, 1);
                        }
                    } else {
                        // 判断是重新翻译还是重新配音
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        const isRetranslate = originalSubtitle.post_text && 
                            originalSubtitle.post_text !== currentSubtitle.post_text;
                        
                        if (isRetranslate) {
                            // 重新翻译操作：更新翻译文本和时间戳，并将 trans_audio 置为空
                            // 处理重新翻译操作，更新翻译文本和时间戳，清空 trans_audio
                            updatedSubtitles[originalIndex] = {
                                ...currentSubtitle,
                                post_text: originalSubtitle.post_text || currentSubtitle.post_text || '',
                                post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                                post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                                origin_text: originalSubtitle.origin_text || currentSubtitle.origin_text,
                                origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                                origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time,
                                words: originalSubtitle.words || currentSubtitle.words || [],
                                // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                trans_audio: '',
                                // 更新其他可能变化的字段
                                speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                                voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info
                            };
                        } else {
                            // 重新配音操作：更新当前台词的配音相关字段和时间戳
                            // 处理重新配音操作，更新当前字幕的音频相关数据和时间戳
                            updatedSubtitles[originalIndex] = {
                                ...currentSubtitle,
                                // 更新目标台词音频和可能的 voice_info/speakers 等配音相关字段
                                trans_audio: originalSubtitle.trans_audio || currentSubtitle.trans_audio,
                                speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                                voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info,
                                // 同步API返回的时间戳（如果存在）
                                post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                                post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                                origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                                origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time
                            };
                        }
                    }
                } else {
                    // 拆分操作或更换音色操作
                    // 判断是否是拆分操作（通过检查newSubtitle.id是否与originalSubtitle.id不同）
                    const isSplitOperation = newSubtitle.id !== originalSubtitle.id;
                    
                    if (isSplitOperation) {
                        // 拆分操作：更新原始字幕并插入新字幕
                        
                        // 直接使用API返回的完整数据替换原始字幕
                        updatedSubtitles[originalIndex] = {
                            id: originalSubtitle.id,
                            task_id: originalSubtitle.task_id,
                            origin_text: originalSubtitle.origin_text,
                            post_text: originalSubtitle.post_text || '',
                            post_start_time: originalSubtitle.post_start_time,
                            post_end_time: originalSubtitle.post_end_time,
                            origin_start_time: originalSubtitle.origin_start_time,
                            origin_end_time: originalSubtitle.origin_end_time,
                            is_episode_voice: originalSubtitle.is_episode_voice,
                            origin_audio: originalSubtitle.origin_audio,
                            trans_audio: originalSubtitle.trans_audio || '',
                            subtitle_id: originalSubtitle.subtitle_id,
                            audio_speed: originalSubtitle.audio_speed,
                            volume_gain_db: originalSubtitle.volume_gain_db,
                            speakers: originalSubtitle.speakers,
                            voice_info: originalSubtitle.voice_info,
                            words: originalSubtitle.words || []
                        };
                        
                        // 在原始字幕后面插入新字幕
                        const newSubtitleData: Task.SubtitlesLegacy = {
                            id: newSubtitle.id,
                            task_id: newSubtitle.task_id,
                            origin_text: newSubtitle.origin_text || '',
                            post_text: newSubtitle.post_text || '',
                            post_start_time: newSubtitle.post_start_time,
                            post_end_time: newSubtitle.post_end_time,
                            origin_start_time: newSubtitle.origin_start_time,
                            origin_end_time: newSubtitle.origin_end_time,
                            is_episode_voice: newSubtitle.is_episode_voice,
                            origin_audio: newSubtitle.origin_audio,
                            trans_audio: newSubtitle.trans_audio || '',
                            subtitle_id: newSubtitle.subtitle_id,
                            audio_speed: newSubtitle.audio_speed,
                            volume_gain_db: newSubtitle.volume_gain_db,
                            speakers: newSubtitle.speakers,
                            voice_info: newSubtitle.voice_info,
                            words: newSubtitle.words || []
                        };
                        
                        // 在原始字幕后面插入新字幕
                        updatedSubtitles.splice(originalIndex + 1, 0, newSubtitleData);
                    } else {
                        // 更换音色操作：直接使用newSubtitle替换原始字幕
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        updatedSubtitles[originalIndex] = {
                            ...currentSubtitle,
                            // 更新音色相关的字段
                            origin_audio: newSubtitle.origin_audio || currentSubtitle.origin_audio,
                            voice_info: newSubtitle.voice_info || currentSubtitle.voice_info,
                            speakers: newSubtitle.speakers || currentSubtitle.speakers,
                            subtitle_id: newSubtitle.subtitle_id || currentSubtitle.subtitle_id,
                            // 保留其他字段不变
                            id: currentSubtitle.id,
                            task_id: currentSubtitle.task_id,
                            origin_text: currentSubtitle.origin_text,
                            post_text: currentSubtitle.post_text,
                            post_start_time: currentSubtitle.post_start_time,
                            post_end_time: currentSubtitle.post_end_time,
                            origin_start_time: currentSubtitle.origin_start_time,
                            origin_end_time: currentSubtitle.origin_end_time,
                            is_episode_voice: currentSubtitle.is_episode_voice,
                            trans_audio: currentSubtitle.trans_audio,
                            audio_speed: currentSubtitle.audio_speed,
                            volume_gain_db: currentSubtitle.volume_gain_db,
                            words: currentSubtitle.words || []
                        };
                        // 更换音色操作：已更新本地字幕数据
                    }
                }
                
                // 更新videoData - 确保创建新的对象引用
                const newVideoData: Task.GetTaskStatusResult = {
                    ...videoData,
                    subtitles: updatedSubtitles
                } as Task.GetTaskStatusResult;
                
                
                // 强制更新videoData
                setVideoDataSafe(newVideoData);
                
                // 如果是重新翻译或重新配音操作，同步更新 targetText 和 sourceText
                if (newSubtitle === null) {
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    if (!currentSubtitleId) {
                        // 判断是重新翻译还是重新配音
                        const originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                        if (originalIndex !== -1) {
                            const currentSubtitle = videoData.subtitles[originalIndex];
                            const isRetranslate = originalSubtitle.post_text && 
                                originalSubtitle.post_text !== currentSubtitle.post_text;
                            const operationType = (originalSubtitle as any).operationType;
                            const isRedub = operationType === 'redub';
                            
                            if (isRetranslate) {
                                // 更新 targetText
                                setTargetText(prev => prev.map(item => 
                                    item.id === originalSubtitle.id 
                                        ? { ...item, text: originalSubtitle.post_text || '' }
                                        : item
                                ));
                                
                                // 更新 sourceText（如果需要）
                                if (originalSubtitle.origin_text) {
                                    setSourcesText((prev: Array<{ id: number; text: string; timeRange: string }>) => 
                                        prev.map((item: { id: number; text: string; timeRange: string }) => 
                                            item.id === originalSubtitle.id 
                                                ? { ...item, text: originalSubtitle.origin_text }
                                                : item
                                        )
                                    );
                                }
                                
                                // 重新翻译后，已同步更新 targetText 和 sourceText
                            } else if (isRedub) {
                                // 重新配音操作：同步更新时间戳到 targetText 和 sourceText
                                const newPostTimeRange = originalSubtitle.post_start_time && originalSubtitle.post_end_time
                                    ? `${originalSubtitle.post_start_time} → ${originalSubtitle.post_end_time}`
                                    : null;
                                const newOriginTimeRange = originalSubtitle.origin_start_time && originalSubtitle.origin_end_time
                                    ? `${originalSubtitle.origin_start_time} → ${originalSubtitle.origin_end_time}`
                                    : null;
                                
                                // 更新 targetText 的时间戳
                                if (newPostTimeRange) {
                                    setTargetText(prev => prev.map(item => 
                                        item.id === originalSubtitle.id 
                                            ? { ...item, timeRange: newPostTimeRange }
                                            : item
                                    ));
                                }
                                
                                // 更新 sourceText 的时间戳
                                if (newOriginTimeRange) {
                                    setSourcesText((prev: Array<{ id: number; text: string; timeRange: string }>) => 
                                        prev.map((item: { id: number; text: string; timeRange: string }) => 
                                            item.id === originalSubtitle.id 
                                                ? { ...item, timeRange: newOriginTimeRange }
                                                : item
                                        )
                                    );
                                }
                                
                                // 同步更新时间轴（不刷新视频）
                                if (timelineEditorRef.current && (newPostTimeRange || newOriginTimeRange)) {
                                    const updatedSubtitle = updatedSubtitles[originalIndex];
                                    if (updatedSubtitle) {
                                        timelineEditorRef.current.updateSegment(updatedSubtitle.id, updatedSubtitle);
                                    }
                                }
                            }
                        }
                    }
                }
                
                // 拆分字幕之后：输出时间轴所有数据的字段和值
                // 拆分字幕之后 - 时间轴所有数据已更新
                // 拆分字幕之后的数据已更新
                
                // 强制清空哈希值，确保字幕同步useEffect能够重新计算
                setLastSubtitlesHash('');
                
                // 根据操作类型显示不同的成功消息
                if (newSubtitle === null) {
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    if (currentSubtitleId) {
                        showSuccess('字幕合并成功');
                    } else {
                        // 根据操作类型显示提示信息（不再对比文本内容）
                        const operationType = (originalSubtitle as any).operationType;
                        if (operationType === 'retranslate') {
                            showSuccess('重新翻译成功');
                        } else if (operationType === 'redub') {
                            showSuccess('重新配音成功');
                        } else {
                            // 如果没有操作类型标识，回退到旧逻辑（兼容性）
                            const originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                            if (originalIndex !== -1) {
                                const currentSubtitle = videoData.subtitles[originalIndex];
                                const isRetranslate = originalSubtitle.post_text && 
                                    originalSubtitle.post_text !== currentSubtitle.post_text;
                                if (isRetranslate) {
                                    showSuccess('重新翻译成功');
                                } else {
                                    showSuccess('重新配音成功');
                                }
                            } else {
                                showSuccess('操作成功');
                            }
                        }
                    }
                } else {
                    showSuccess('字幕拆分成功');
                }
            } else {
                showError('字幕拆分失败：未找到原始字幕');
            }
        } else {
            showError('字幕拆分失败：数据异常');
        }
    }, [videoData, setVideoDataSafe, showSuccess, showError, pendingTimelineUpdates, timelineEditorRef]);

    // 获取下一个字幕ID的函数
    const handleGetNextSubtitleId = useCallback((currentId: number) => {
        if (!videoData?.subtitles) {
            return null;
        }
        
        // 找到当前字幕在数组中的索引
        const currentIndex = videoData.subtitles.findIndex(sub => sub.id === currentId);
        if (currentIndex === -1 || currentIndex === videoData.subtitles.length - 1) {
            return null; // 没有找到当前字幕或已经是最后一个
        }
        
        // 获取下一个字幕的ID
        const nextSubtitle = videoData.subtitles[currentIndex + 1];
        return nextSubtitle.id;
    }, [videoData]);

    // 源语言台词数据
    const [sourcesText, setSourcesText] = useState([
        {
            id: 1,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 2,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 3,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 4,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 5,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 6,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 7,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 8,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 9,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 10,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 11,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 12,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 13,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 14,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 15,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
        {
            id: 16,
            text: "好好说话，有事好商量",
            timeRange: "00:00:22,566 → 00:00:24,666",
        },
    ]);

    // 目标语言台词数据 - 从API数据动态生成
    const [targetText, setTargetText] = useState<Array<{ id: number; text: string; timeRange: string }>>([
        {
            id: 1,
            text: "ちゃんと話そう、何かあれば相談しよう",
            timeRange: "00:00:22,566 → 00:00:24,666",
        }
    ]);

    // 【修复补充】为 targetText 也创建"特洛伊木马"对象
    // 原因：时间轴拖拽、重新翻译等操作会更新 targetText 状态（生成新数组）。
    // 如果直接传给 SubtitleDemo，会导致 props 变化从而触发组件重置/视频重载。
    // 但是，为了确保字幕能够实时更新，我们需要在 targetText 内容变化时创建新引用
    // 使用 JSON.stringify 来检测内容变化，但只在关键字段变化时才重建
    const targetTextForDemo = useMemo(() => {
        // 创建一个新的数组，包含 targetText 的所有数据
        // 这样当 targetText 内容变化时，会创建新引用，触发 SubtitleDemo 重新解析字幕
        return targetText ? [...targetText] : [];
    }, [id, targetText]); // 当 ID 或 targetText 内容变化时重建

    // 获取下一个台词信息的函数（优先使用 targetText 中的最新时间戳）
    const handleGetNextSubtitle = useCallback((currentId: number) => {
        if (!videoData?.subtitles) {
            return null;
        }
        
        // 找到当前字幕在数组中的索引
        const currentIndex = videoData.subtitles.findIndex(sub => sub.id === currentId);
        if (currentIndex === -1 || currentIndex === videoData.subtitles.length - 1) {
            return null; // 没有找到当前字幕或已经是最后一个
        }
        
        // 获取下一个字幕
        const nextSubtitle = videoData.subtitles[currentIndex + 1];
        
        // 优先使用 targetText 中的最新时间戳（如果存在）
        const nextTargetData = targetText.find(item => item.id === nextSubtitle.id);
        let nextPostStartTime = nextSubtitle.post_start_time || nextSubtitle.origin_start_time;
        let nextPostEndTime = nextSubtitle.post_end_time || nextSubtitle.origin_end_time;
        
        if (nextTargetData && nextTargetData.timeRange) {
            // 从 targetText 中解析时间戳
            const timeRangeParts = nextTargetData.timeRange.split(' → ');
            if (timeRangeParts.length === 2) {
                nextPostStartTime = timeRangeParts[0].trim();
                nextPostEndTime = timeRangeParts[1].trim();
            }
        }
        
        return {
            // 原台词时间戳
            originStartTime: nextSubtitle.origin_start_time,
            originEndTime: nextSubtitle.origin_end_time,
            // 目标台词时间戳（优先使用 targetText 中的最新时间戳）
            postStartTime: nextPostStartTime,
            postEndTime: nextPostEndTime,
            // 兼容旧版本
            startTime: nextSubtitle.origin_start_time,
            endTime: nextSubtitle.origin_end_time
        };
    }, [videoData, targetText]);

    // 获取完整时间轴数据的回调函数
    const handleGetFullTimelineData = useCallback(() => {
        // 获取完整时间轴数据
        // 完整时间轴数据已生成
        
        return {
            videoDataSubtitles: videoData?.subtitles,
            sourcesText,
            targetText
        };
    }, [videoData, sourcesText, targetText]);

    // 切换轨道的回调函数
    const handleSwitchTrack = useCallback((trackType: 'translated' | 'original') => {
        if (timelineEditorRef.current) {
            const previousTrack = timelineEditorRef.current.activeTrack;
            timelineEditorRef.current.setActiveTrack(trackType);
            
            // 切换轨道时，停止/开始相应的音频
            if (previousTrack === 'translated' && trackType === 'original') {
                // 从"翻译后"切换到"原视频"：先完全停止翻译音频，再开始原人声音频
                stopTransAudio();
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                // 清除当前播放的字幕ID
                currentPlayingSubtitleIdRef.current = null;
                
                // 如果正在播放，初始化并播放原人声音频
                // 注意：isPlaying 不在依赖中，使用 sourceVideoRef.current 判断播放状态
                // 如果是点击台词导致的跳转，不自动播放
                const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                if (isCurrentlyPlaying && !isSubtitleClickSeekRef.current && videoData?.vocals_hls_url) {
                    playAudioTracks(); // 重新初始化音频轨道
                }
            } else if (previousTrack === 'original' && trackType === 'translated') {
                // 从"原视频"切换到"翻译后"：先完全停止原人声音频，再开始翻译音频
                if (vocalsAudioRef.current) {
                    vocalsAudioRef.current.pause();
                    vocalsAudioRef.current.currentTime = 0;
                }
                if (vocalsHlsRef.current) {
                    vocalsHlsRef.current.destroy();
                    vocalsHlsRef.current = null;
                }
                // 如果是点击台词导致的跳转，不自动播放翻译音频
                // 这里不需要额外处理，因为 playTransAudioByTime 中已经检查了 isSubtitleClickSeekRef
            }
        }
    }, [videoData]);

    // 播放头移动的回调函数（优化版本）
    // 用于处理时间轴空白处点击：播放状态下跳转并播放，暂停状态下跳转并静默
    const handlePlayheadMove = useCallback((time: number) => {
        // 【核心修复】：如果是点击台词导致的跳转，不自动播放（完全静默）
        if (isSubtitleClickSeekRef.current) {
            // 只执行跳转，不播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.currentTime = time;
                setCurrentTime(time);
            }
            if (subtitleDemoRef.current) {
                subtitleDemoRef.current.seekToTime(time);
            }
            // 同步音频进度（但不播放）
            if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                backgroundAudioRef.current.currentTime = time;
            }
            if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                vocalsAudioRef.current.currentTime = time;
            }
            return; // 直接返回，不执行后续播放逻辑
        }
        
        // 同步左侧视频播放进度
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = time;
            setCurrentTime(time);
        }
        
        // 同步右侧视频播放进度
        if (subtitleDemoRef.current) {
            subtitleDemoRef.current.seekToTime(time);
        }
        
        // 同步背景音播放进度到黑色刻度线位置（无论是否在播放都要同步）
        if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
            backgroundAudioRef.current.currentTime = time;
            // 时间轴同步：背景音已同步到黑色刻度线位置
        }
        
        // 同步原人声音频播放进度到黑色刻度线位置（如果选中"原视频"轨道）
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
        if (currentActiveTrack === 'original' && vocalsAudioRef.current && vocalsAudioRef.current.src) {
            vocalsAudioRef.current.currentTime = time;
            // 时间轴同步：原人声音频已同步到黑色刻度线位置
        }
        
        // 【核心修复】：根据播放状态决定是否播放音频
        // 播放状态下：跳转并继续播放
        // 暂停状态下：只跳转，不播放（静默）
        // 使用 sourceVideoRef 判断播放状态，避免依赖项问题
        const currentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
        if (currentlyPlaying) {
            // 播放状态下，确保视频继续播放
            if (sourceVideoRef.current && sourceVideoRef.current.paused) {
                sourceVideoRef.current.play().catch(error => {
                    console.error('恢复视频播放失败:', error);
                });
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.play(); } catch {}
            }
            if (backgroundAudioRef.current && backgroundAudioRef.current.src && backgroundAudioRef.current.paused) {
                backgroundAudioRef.current.play().catch(error => {
                    console.error('恢复背景音播放失败:', error);
                });
            }
            if (currentActiveTrack === 'original' && vocalsAudioRef.current && vocalsAudioRef.current.src && vocalsAudioRef.current.paused) {
                vocalsAudioRef.current.play().catch(error => {
                    console.error('恢复人声音频播放失败:', error);
                });
            }
            // 强制同步翻译音频（播放状态下）
            if (playTransAudioByTimeRef.current) {
                playTransAudioByTimeRef.current(time, { forceReplay: true });
            }
        } else {
            // 暂停状态下，设置拦截标记，确保不自动播放
            isSubtitleClickSeekRef.current = true;
            // 确保视频保持暂停
            if (sourceVideoRef.current && !sourceVideoRef.current.paused) {
                sourceVideoRef.current.pause();
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.pause(); } catch {}
            }
            // 停止所有音频
            stopTransAudio();
            stopOriginAudio();
            stopCurrentPlayingInstance();
            if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
                backgroundAudioRef.current.pause();
            }
            if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
                vocalsAudioRef.current.pause();
            }
            // 短暂延迟后清除标记，允许后续的正常操作
            setTimeout(() => {
                isSubtitleClickSeekRef.current = false;
            }, 500);
        }
        
        // 更新进度条显示（缓存DOM元素）
        if (duration > 0) {
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar) {
                const progressPercent = (time / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
        }
        
        // 高亮对应的目标台词
        updateTargetSubtitleHighlight(time);
    }, []);

    // 获取新增台词最新数据的辅助函数
    const getLatestSubtitleData = (subtitleId: number, field: 'origin_text' | 'post_text', newText: string) => {
        // 从videoData.subtitles中获取基础数据
        const subtitle = videoData?.subtitles?.find(sub => sub.id === subtitleId);
        if (!subtitle) return null;
        
        // 从状态中获取最新的编辑数据
        const currentTargetData = targetText.find(item => item.id === subtitleId);
        const currentSourceData = sourcesText.find(item => item.id === subtitleId);
        
        // 构建最新的数据
        const latestData = {
            ...subtitle,
            origin_text: field === 'origin_text' ? newText : (currentSourceData?.text || subtitle.origin_text || ''),
            post_text: field === 'post_text' ? newText : (currentTargetData?.text || subtitle.post_text || '')
        };
        
        // 获取最新字幕数据
        
        return latestData;
    };

    // 构建字幕更新参数的辅助函数
    const buildSubtitleUpdateParams = (subtitleId: number, field: 'origin_text' | 'post_text', newText: string): Subtitles.UpdateSubtitleParams | null => {
        if (!videoData?.subtitles) {
            // 视频数据或字幕数据不存在
            return null;
        }
        
        const subtitle = videoData.subtitles.find(sub => sub.id === subtitleId);
        if (!subtitle) {
            // 未找到指定ID的字幕数据
            return null;
        }
        
        // 获取最新的字幕数据
        const latestData = getLatestSubtitleData(subtitleId, field, newText);
        if (!latestData) {
            // 获取最新字幕数据失败
            return null;
        }
        
        return {
            task_id: videoData.id,
            // 使用最新的数据
            origin_text: latestData.origin_text,
            post_text: latestData.post_text,
            post_start_time: latestData.post_start_time || latestData.origin_start_time || '00:00:00,000',
            post_end_time: latestData.post_end_time || latestData.origin_end_time || '00:00:01,000',
            origin_start_time: latestData.origin_start_time || '00:00:00,000',
            origin_end_time: latestData.origin_end_time || '00:00:01,000',
            is_episode_voice: latestData.is_episode_voice || false,
            trans_audio: latestData.trans_audio || '',
            subtitle_id: latestData.subtitle_id || latestData.id,
            score: 98.9509, // 默认权重
            audio_speed: latestData.audio_speed || 1,
            id: latestData.id,
            speaker_id: latestData.speakers?.id || 0
        };
    };

    // 源语言数据更新处理
    const handleSourceDataChange = async (newData: { id: number; text: string; timeRange: string }) => {
        // 检查台词是否有变化
        const currentSourceData = sourcesText.find(item => item.id === newData.id);
        if (currentSourceData && currentSourceData.text === newData.text) {
            // 左侧台词没有变化，跳过API调用
            return;
        }
        
        // 先更新本地状态
        setSourcesText(prev => prev.map(item => item.id === newData.id ? newData : item));
        
        // 调用API更新（新接口只支持更新originText）
        setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: true }));
        setSubtitleUpdateErrors(prev => ({ ...prev, [newData.id]: '' }));
        
        try {
            const response = await updateSubtitleApi({
                id: newData.id,
                originText: newData.text
            });
            // 更新台词API响应已接收
            
            if (response.code === 0 && response.data) {
                // 同步更新videoData中的origin_text和post_text
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    const updatedSubtitles = prev.subtitles.map(subtitle => {
                        if (subtitle.id === newData.id) {
                            return {
                                ...subtitle,
                                origin_text: response.data.originText || newData.text,
                                post_text: '' // 修改源台词后，清空对应的目标台词
                            };
                        }
                        return subtitle;
                    });
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });
                
                // 更新时间轴显示 - 使用API返回的完整数据
                if (timelineEditorRef.current && response.data) {
                    // 获取原始字幕数据以保留speakers信息
                    const originalSubtitle = videoData?.subtitles?.find(sub => sub.id === newData.id);
                    
                    // 将API返回的Task.Subtitles格式转换为Task.SubtitlesLegacy格式
                    const subtitleForTimeline: Task.SubtitlesLegacy = {
                        id: response.data.id,
                        task_id: response.data.taskId,
                        origin_text: response.data.originText,
                        post_text: response.data.postText || '',
                        post_start_time: response.data.postStartTime || '',
                        post_end_time: response.data.postEndTime || '',
                        origin_start_time: response.data.originStartTime,
                        origin_end_time: response.data.originEndTime,
                        is_episode_voice: response.data.isEpisodeVoice || false,
                        origin_audio: response.data.originAudio,
                        trans_audio: response.data.transAudio || '',
                        subtitle_id: response.data.subtitleId || response.data.id,
                        audio_speed: response.data.audioSpeed || 1.0,
                        volume_gain_db: response.data.volumeGainDb || 1.0,
                        speakers: response.data.speaker ? {
                            id: response.data.speaker.id,
                            series_id: response.data.speaker.seriesId,
                            task_id: response.data.taskId,
                            speaker_name: response.data.speaker.speakerName,
                            description: response.data.speaker.description || '',
                            is_series_speaker: response.data.speaker.isSeriesSpeaker
                        } : (originalSubtitle?.speakers || {} as any),
                        voice_info: originalSubtitle?.voice_info || {} as any
                    };
                    
                    timelineEditorRef.current.updateSegment(newData.id, subtitleForTimeline);
                }
                
                // 更新sourcesText状态
                setSourcesText(prev => prev.map(item => 
                    item.id === newData.id ? { ...item, text: newData.text } : item
                ));
                
                // 修改源台词后，清空对应的目标台词
                setTargetText(prev => prev.map(item => 
                    item.id === newData.id ? { ...item, text: '' } : item
                ));
                
                // 更新sourceText数据和时间轴显示
                
                showSuccess('台词更新成功');
            } else {
                throw new Error(response.msg || '更新失败');
            }
        } catch (error: any) {
            console.error('更新台词失败:', error);
            setSubtitleUpdateErrors(prev => ({ 
                ...prev, 
                [newData.id]: error?.message || '更新失败' 
            }));
            showError('台词更新失败');
        } finally {
            setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: false }));
        }
    };

    // 更新videoData中的语速数据
    const handleUpdateVideoDataSpeed = (subtitleId: number, audioSpeed: number) => {
        setVideoData(prev => {
            if (!prev?.subtitles) return prev;
            
            const updatedSubtitles = prev.subtitles.map(subtitle => {
                if (subtitle.id === subtitleId) {
                    return {
                        ...subtitle,
                        audio_speed: audioSpeed
                    };
                }
                return subtitle;
            });
            
            // 更新videoData中的语速数据
            
            return {
                ...prev,
                subtitles: updatedSubtitles
            };
        });
    };
    // 目标语言数据更新处理
    const handleTargetDataChange = async (newData: { id: number; text: string; timeRange: string }) => {
        // 检查台词是否有变化
        const currentTargetData = targetText.find(item => item.id === newData.id);
        const hasTextChange = currentTargetData && currentTargetData.text !== newData.text;
        const hasTimeChange = currentTargetData && currentTargetData.timeRange !== newData.timeRange;
        
        if (!hasTextChange && !hasTimeChange) {
            // 右侧台词和时间戳都没有变化，跳过API调用
            return;
        }
        
        // 先更新本地状态
        setTargetText(prev => prev.map(item => item.id === newData.id ? newData : item));
        
        // 如果有时间戳变化，同步更新videoData.subtitles
        if (hasTimeChange && videoData?.subtitles) {
            const [newStartTime, newEndTime] = newData.timeRange.split(' → ');
            setVideoData(prev => {
                if (!prev?.subtitles) return prev;
                
                const updatedSubtitles = prev.subtitles.map(subtitle => {
                    if (subtitle.id === newData.id) {
                        return {
                            ...subtitle,
                            post_start_time: newStartTime,
                            post_end_time: newEndTime
                        };
                    }
                    return subtitle;
                });
                
                // 同步更新时间轴字幕数据
                
                return {
                    ...prev,
                    subtitles: updatedSubtitles
                };
            });
        }
        
        // 如果有文本变化，调用新接口仅更新 post_text
        if (hasTextChange) {
            try {
                setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: true }));

                // 调用后端接口更新目标语言文本
                const response = await updatePostTextApi({
                    id: newData.id,
                    postText: newData.text
                });

                if (response.code !== 0) {
                    throw new Error(response.msg || '更新失败');
                }

                // updatePostTextApi 更新成功

                // 同步更新本地 targetText 状态
                setTargetText(prev => prev.map(item =>
                    item.id === newData.id ? { ...item, text: newData.text } : item
                ));

                // 同步更新 videoData.subtitles 中的 post_text
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    const updatedSubtitles = prev.subtitles.map(subtitle =>
                        subtitle.id === newData.id
                            ? { ...subtitle, post_text: newData.text }
                            : subtitle
                    );
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });

                // 更新时间轴显示（使用最新数据刷新对应片段）
                if (videoData?.subtitles && timelineEditorRef.current) {
                    const latestData = getLatestSubtitleData(newData.id, 'post_text', newData.text);
                    if (latestData) {
                        timelineEditorRef.current.updateSegment(newData.id, latestData);
                    }
                }

                // 已同步更新 targetText、videoData.subtitles 和时间轴显示

                showSuccess('台词更新成功');
            } catch (error: any) {
                console.error('❌ 更新目标语言台词失败:', error);
                setSubtitleUpdateErrors(prev => ({
                    ...prev,
                    [newData.id]: error?.message || '更新失败'
                }));
                showError(error?.message || '更新目标语言台词失败');
            } finally {
                setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: false }));
            }
        }
    };

    // 角色选择处理 - 每行独立选择
    const handleRoleSelect = async (roleName: string, rowId: number, speakerId?: string, isSeriesVoiceLibrary?: boolean) => {
        setRowSelectedRoles(prev => ({
            ...prev,
            [rowId]: roleName
        }));
        
        // 如果角色名称为空，清除剧集音色库标记（移出音色库的情况）
        if (!roleName) {
            setSeriesVoiceLibraryMarkers(prev => {
                const newMarkers = { ...prev };
                delete newMarkers[rowId];
                // 已清除剧集音色库标记
                return newMarkers;
            });
        }
        
        // 如果是剧集音色库操作，只更新本地状态，不调用API和更新videoData
        if (isSeriesVoiceLibrary) {
            // 剧集音色库操作 - 仅更新本地状态
            
            // 更新剧集音色库标记状态
            setSeriesVoiceLibraryMarkers(prev => ({
                ...prev,
                [rowId]: true
            }));
            
            // 已更新剧集音色库标记状态
            return;
        }
        
        // 输出说话人信息到控制台
        if (speakerId) {
            // 台词行选择说话人
            
            try {
                const numericSpeakerId = parseInt(speakerId, 10);
                
                // 检查当前字幕的说话人ID是否和选择的相同，如果相同则不调用接口
                const currentSubtitle = videoData?.subtitles?.find(sub => sub.id === rowId);
                const currentSpeakerId = currentSubtitle?.speakers?.id;
                
                if (currentSpeakerId === numericSpeakerId) {
                    // 说话人未变化，跳过接口调用
                    return;
                }
                
                // 正在调用 changeSubtitleSpeakerApi 修改说话人

                const response = await changeSubtitleSpeakerApi(rowId, numericSpeakerId);

                if (response.code === 0) {
                    // 修改字幕说话人成功

                    // 更新本地数据
                    if (videoData?.subtitles) {
                        const updatedSubtitles = videoData.subtitles.map(sub => 
                            sub.id === rowId 
                                ? { 
                                    ...sub, 
                                    speakers: { 
                                        ...sub.speakers, 
                                        id: numericSpeakerId,
                                        speaker_name: roleName 
                                    } 
                                }
                                : sub
                        );
                        
                        setVideoData(prev => prev ? { ...prev, subtitles: updatedSubtitles } : null);
                    }
                    
                    showSuccess(`说话人已更新为"${roleName}"`);
                } else {
                    console.error('❌ 修改字幕说话人失败:', response);
                    showError(response.msg || '说话人更新失败');
                }
            } catch (error: any) {
                console.error('❌ 修改字幕说话人接口调用出错:', error);
                showError(error?.message || '说话人更新失败');
            }
        } else {
            // 台词行选择角色（非API创建的说话人，无speaker_id）
        }
    };

    // 新建说话人处理（创建后自动将当前台词切换为该说话人）
    const handleNewSpeaker = async (rowId?: number) => {
        if (!videoData) {
            showError('视频数据不存在，无法创建说话人');
            return;
        }

        try {
            // 生成默认的说话人名称（按顺序）
            // 从现有角色中找出最大的角色序号
            let maxRoleNumber = 0;
            roles.forEach(role => {
                const match = role.label.match(/^角色(\d+)$/);
                if (match) {
                    const number = parseInt(match[1]);
                    if (number > maxRoleNumber) {
                        maxRoleNumber = number;
                    }
                }
            });
            
            // 生成下一个角色序号
            const nextRoleNumber = maxRoleNumber + 1;
            const defaultSpeakerName = `角色${nextRoleNumber}`;
            
            // 生成新说话人名称
            
            // 调用API创建说话人（使用新接口）
            const response = await addVideoSpeakerApi(videoData.id, defaultSpeakerName);
            
            if (response && response.code === 0) {
                // 说话人创建成功
                
                // 创建成功后，重新获取说话人列表以确保数据同步
                const updatedRoles = await fetchSpeakersList(videoData.id);
                
                // 从更新后的列表中查找新创建的说话人（通过名称匹配）
                const newRole = updatedRoles.find(role => role.label === defaultSpeakerName);
                
                if (newRole) {
                    // 设置编辑状态
                    setEditingRoleKey(newRole.key);
                    setEditingRoleName(newRole.label);
                    showSuccess(`创建说话人"${defaultSpeakerName}"成功，可以修改名称`);

                    // 如果传入了当前台词ID，则自动把该台词的说话人切换为新建的说话人
                    if (rowId) {
                        // 从 role.key 中解析 speakerId（通常格式如 'speaker_123'）
                        let speakerId: string | undefined;
                        if (typeof newRole.key === 'string' && newRole.key.includes('_')) {
                            const parts = newRole.key.split('_');
                            if (parts.length === 2) {
                                speakerId = parts[1];
                            }
                        }

                        // 自动应用新建说话人到当前台词

                        // 更新当前行的角色选择（UI 显示）
                        setRowSelectedRoles(prev => ({
                            ...prev,
                            [rowId]: newRole.label
                        }));

                        // 如果解析出了 speakerId，则调用现有的角色选择逻辑，更新后端与本地状态
                        if (speakerId) {
                            await handleRoleSelect(newRole.label, rowId, speakerId);
                        } else {
                            // 未能从新建说话人中解析 speakerId，跳过字幕说话人更新
                        }
                    }
                } else {
                    // 如果找不到，说明可能创建失败或名称已存在，重新获取列表
                    // 新创建的角色未在列表中，重新获取列表
                    await fetchSpeakersList(videoData.id);
                    showSuccess(`创建说话人"${defaultSpeakerName}"成功`);
                }
            } else {
                showError(response.msg || '创建说话人失败');
            }
        } catch (error: any) {
            console.error('创建说话人失败:', error);
            showError(error?.message || '创建说话人失败');
        }
    };

    /** 说话人下拉中点击「删除」：仅支持 key 为 speaker_<id> 或 series_speaker_<id> 的说话人；删除后使用该说话人的台词显示为「未知说话人」 */
    const handleDeleteRole = useCallback(async (roleKey: string) => {
        const match = roleKey.match(/^(?:speaker_|series_speaker_)(\d+)$/);
        if (!match) {
            showError('仅支持删除已同步的说话人');
            return;
        }
        const role = roles.find(r => r.key === roleKey);
        const deletedLabel = role?.label ?? '';
        const speakerId = parseInt(match[1], 10);
        try {
            await deleteSpeakerApi(speakerId);
            setRoles(prev => prev.filter(r => r.key !== roleKey));
            // 使用该说话人的台词行改为显示「未知说话人」
            setRowSelectedRoles(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(idStr => {
                    const id = Number(idStr);
                    if (prev[id] === deletedLabel) next[id] = '未知说话人';
                });
                return next;
            });
            showSuccess('已删除该说话人');
        } catch (e: any) {
            console.error('删除说话人失败:', e);
            showError(e?.message || '删除失败，请重试');
        }
    }, [roles]);

    // 开始编辑角色
    const handleStartEditRole = (roleKey: string, currentName: string) => {
        setEditingRoleKey(roleKey);
        setEditingRoleName(currentName);
    };

    // 保存角色编辑
    const handleSaveRoleEdit = useCallback(async (roleKey: string): Promise<void> => {
        // 防重复调用
        if (isSavingRole) {
            // 正在保存角色编辑，跳过重复调用
            return;
        }

        if (editingRoleName.trim()) {
            const currentRole = roles.find(r => r.key === roleKey);
            const newName = editingRoleName.trim();

            // 如果名称没有变化，直接返回，不调用接口
            if (currentRole && currentRole.label === newName) {
                // 说话人名称未变化，跳过接口调用
                setEditingRoleKey(null);
                setEditingRoleName('');
                return;
            }

            // 只有当名称真正发生变化时才更新和提示
            if (currentRole && currentRole.label !== newName) {
                try {
                    setIsSavingRole(true);
                    // 如果是通过API创建的说话人（key格式为speaker_xxx），则调用API更新
                    if (roleKey.startsWith('speaker_')) {
                        const speakerId = parseInt(roleKey.replace('speaker_', ''));

                        const response = await renameVideoSpeakerApi(speakerId, newName);
                        
                        if (response && response.code === 0) {
                            // 说话人重命名API调用成功，直接更新本地状态
                            
                            // 直接更新本地状态，不进行验证
                            setRoles(prev => prev.map(role => 
                                role.key === roleKey ? { ...role, label: newName } : role
                            ));
                            
                            // 如果编辑的是当前选中的角色，也要更新rowSelectedRoles中对应的角色
                            Object.keys(rowSelectedRoles).forEach(rowId => {
                                if (rowSelectedRoles[parseInt(rowId)] === currentRole.label) {
                                    setRowSelectedRoles(prev => ({
                                        ...prev,
                                        [parseInt(rowId)]: newName
                                    }));
                                }
                            });

                            showSuccess(`说话人名称修改成功: "${currentRole.label}" → "${newName}"`);
                        } else {
                            console.error('❌ 说话人重命名API调用失败:', response);
                            showError(response.msg || '说话人名称修改失败');
                            throw new Error(response.msg || '说话人名称修改失败');
                        }
                    } else {
                        // 对于非API创建的说话人，只更新本地状态
                        const updatedRoles = roles.map(role =>
                            role.key === roleKey
                                ? { ...role, label: newName }
                                : role
                        );
                        setRoles(updatedRoles);

                        // 如果编辑的是当前选中的角色，也要更新rowSelectedRoles中对应的角色
                        Object.keys(rowSelectedRoles).forEach(rowId => {
                            if (rowSelectedRoles[parseInt(rowId)] === currentRole.label) {
                                setRowSelectedRoles(prev => ({
                                    ...prev,
                                    [parseInt(rowId)]: newName
                                }));
                            }
                        });

                        showSuccess('角色名称修改成功');
                    }
                } catch (error: any) {
                    showError(error?.message || '说话人名称修改失败');
                    throw error; // 重新抛出错误，让调用者知道保存失败
                } finally {
                    setIsSavingRole(false);
                }
            }

            // 仅当当前仍在该角色编辑状态时才清空，避免“先 blur 保存再点击另一项编辑”时清空新编辑状态
            setEditingRoleKey(prev => {
                if (prev === roleKey) {
                    setEditingRoleName('');
                    return null;
                }
                return prev;
            });
        }
    }, [isSavingRole, editingRoleName, roles, rowSelectedRoles, showSuccess, showError]);

    // 取消角色编辑
    const handleCancelRoleEdit = () => {
        setEditingRoleKey(null);
        setEditingRoleName('');
    };

    // 视频播放控制状态
    const END_EPSILON = 0.03; // 结束判定容差（秒）
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    
    // 右侧视频控制状态
    const [rightVideoIsPlaying, setRightVideoIsPlaying] = useState(false);
    const [rightVideoCurrentTime, setRightVideoCurrentTime] = useState(0);
    const [rightVideoDuration, setRightVideoDuration] = useState(0);
    // 标记是否为用户手动操作
    const userInteractionRef = useRef(false);
    // 台词播放状态
    const [subtitlePlayEndTime, setSubtitlePlayEndTime] = useState<number | null>(null);
    const [justFinishedSubtitlePlay, setJustFinishedSubtitlePlay] = useState(false);
    const subtitlePlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 音频就绪状态跟踪
    const [audioReadyState, setAudioReadyState] = useState<{
        backgroundReady: boolean;
        vocalsReady: boolean;
    }>({ backgroundReady: false, vocalsReady: false });
    const audioReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 当前高亮的目标台词ID
    const [highlightedSubtitleId, setHighlightedSubtitleId] = useState<number | null>(null);
    // 左/右视频原始分辨率与宽高比
    const [leftVideoAspect, setLeftVideoAspect] = useState<{ width: number; height: number; ratio: number; simplified: string } | null>(null);
    const [rightVideoAspect, setRightVideoAspect] = useState<{ width: number; height: number; ratio: number; simplified: string } | null>(null);
    const [volume, setVolume] = useState(1);
    const sourceVideoRef = useRef<HTMLVideoElement>(null);
    const targetVideoRef = useRef<HTMLVideoElement>(null);
    const subtitleDemoRef = useRef<SubtitleDemoRef>(null);
    const textListRef = useRef<HTMLDivElement>(null);
    const subtitleCanvasRef = useRef<HTMLCanvasElement>(null);
    const hlsRef = useRef<any>(null);
    const currentVideoUrlRef = useRef<string>('');
    
    // 视频同步相关
    const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastSyncTimeRef = useRef<number>(0);
    const syncThreshold = 0.05; // 50毫秒的同步阈值
    const lastAudioCheckTimeRef = useRef<number>(0); // 上次检查音频播放的时间
    const lastAudioCheckVideoTimeRef = useRef<number>(0); // 记录视频时间，用于检测 Seek 跳变
    const isSubtitleClickSeekRef = useRef<boolean>(false); // 标记是否是点击台词导致的跳转（不自动播放）
    // 上下键防抖：避免连发/手抖导致连跳多句
    const lastVideoEditKeyTimeRef = useRef<number>(0);
    // 当前播放的字幕ID（状态控制，避免重复播放）
    const currentPlayingSubtitleIdRef = useRef<number | null>(null);
    
    // 全局唯一播放实例管理
    type PlayingInstanceType = 'origin' | 'target' | null;
    interface PlayingInstance {
        type: PlayingInstanceType;
        audioId?: string; // 翻译后台词的 audioId
        subtitleId?: number; // 字幕ID
        timeRange?: string; // 时间范围
    }
    const currentPlayingInstanceRef = useRef<PlayingInstance | null>(null);
    
    // 停止当前播放实例的统一函数
    const stopCurrentPlayingInstance = useCallback(() => {
        const instance = currentPlayingInstanceRef.current;
        if (!instance) {
            return;
        }
        
        // 全局播放管理：停止当前播放实例
        
        if (instance.type === 'origin') {
            // 停止原台词播放
            if (originAudioRef.current) {
                originAudioRef.current.pause();
                originAudioRef.current.currentTime = 0;
                originAudioRef.current.src = '';
            }
            // 停止视频播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.pause(); } catch {}
            }
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
        } else if (instance.type === 'target') {
            // 停止翻译后台词播放
            if (instance.audioId) {
                // 使用 AudioManager 播放的情况
                try {
                    audioManager.stopSound(instance.audioId);
                    audioManager.destroySound(instance.audioId);
                } catch (error) {
                    console.error('停止 AudioManager 音频时出错:', error);
                }
            } else {
                // 回退模式：使用 transAudioRef 播放的情况
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    transAudioRef.current.src = '';
                }
            }
            // 停止视频播放
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.pause(); } catch {}
            }
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            setCurrentTransAudio(null);
            if (instance.subtitleId !== undefined) {
                currentPlayingSubtitleIdRef.current = null;
            }
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
        }
        
        // 停止背景音
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        
        // 清除当前播放实例记录
        currentPlayingInstanceRef.current = null;
    }, []);

    // 确保背景音的 Web Audio 增益节点已初始化（在用户交互后调用）
    const ensureBackgroundGainNode = useCallback(async () => {
        if (!backgroundAudioRef.current) {
            return;
        }

        // 创建或恢复 AudioContext
        let context = backgroundAudioContextRef.current;
        if (!context) {
            context = new (window.AudioContext || (window as any).webkitAudioContext)();
            backgroundAudioContextRef.current = context;
        } else if (context.state === 'suspended') {
            try {
                await context.resume();
            } catch (e) {
                console.error('恢复 AudioContext 失败:', e);
            }
        }

        // 已有 source 和 gain，则直接返回
        if (backgroundSourceNodeRef.current && backgroundGainNodeRef.current) {
            return;
        }

        try {
            const source = context.createMediaElementSource(backgroundAudioRef.current);
            const gainNode = context.createGain();
            // 初始增益为 1.0，具体数值由 backgroundVolume 控制
            gainNode.gain.value = backgroundVolume;

            source.connect(gainNode);
            gainNode.connect(context.destination);

            backgroundSourceNodeRef.current = source;
            backgroundGainNodeRef.current = gainNode;
            // 背景音 Web Audio 增益节点已初始化
        } catch (error) {
            console.error('初始化背景音 Web Audio 放大器失败:', error);
        }
    }, [backgroundVolume]);
    
    // 音频播放引用
    const backgroundAudioRef = useRef<HTMLAudioElement>(null);
    const originAudioRef = useRef<HTMLAudioElement>(null); // 原始台词音频
    const transAudioRef = useRef<HTMLAudioElement>(null); // 翻译音频
    const vocalsAudioRef = useRef<HTMLAudioElement>(null); // 原人声音频（vocals_hls_url）
    const backgroundHlsRef = useRef<any>(null);
    const vocalsHlsRef = useRef<any>(null); // 原人声音频的HLS实例
    // 背景音 Web Audio 放大器（GainNode）相关引用
    const backgroundAudioContextRef = useRef<AudioContext | null>(null);
    const backgroundGainNodeRef = useRef<GainNode | null>(null);
    const backgroundSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
    const audioPlayRequestedRef = useRef<boolean>(false);
    
    // 当前播放的音频状态
    const [currentSubtitleAudio, setCurrentSubtitleAudio] = useState<string | null>(null);
    const [currentTransAudio, setCurrentTransAudio] = useState<string | null>(null);

    // 等待左侧视频 seek 到指定时间
    const waitForLeftSeek = (targetTime: number, epsilon: number = 0.02, timeoutMs: number = 1500) => {
        return new Promise<void>((resolve) => {
            const video = sourceVideoRef.current;
            if (!video) { resolve(); return; }

            const done = () => resolve();
            const start = performance.now();

            const check = () => {
                if (!sourceVideoRef.current) { resolve(); return; }
                const ct = sourceVideoRef.current.currentTime || 0;
                if (Math.abs(ct - targetTime) <= epsilon) { done(); return; }
                if (performance.now() - start > timeoutMs) { done(); return; }
                requestAnimationFrame(check);
            };

            const onSeeked = () => {
                try { video.removeEventListener('seeked', onSeeked); } catch {}
                done();
            };
            try { video.addEventListener('seeked', onSeeked, { once: true } as any); } catch { /* ignore */ }
            check();
        });
    };

    // 等待右侧视频 seek 到指定时间
    const waitForRightSeek = (targetTime: number, epsilon: number = 0.02, timeoutMs: number = 1500) => {
        return new Promise<void>((resolve) => {
            const right = subtitleDemoRef.current;
            if (!right) { resolve(); return; }
            const start = performance.now();
            const check = () => {
                const ct = (right.getCurrentTime && right.getCurrentTime()) || 0;
                if (Math.abs(ct - targetTime) <= epsilon) { resolve(); return; }
                if (performance.now() - start > timeoutMs) { resolve(); return; }
                requestAnimationFrame(check);
            };
            check();
        });
    };

    const waitBothSeeked = async (targetTime: number) => {
        await Promise.all([waitForLeftSeek(targetTime), waitForRightSeek(targetTime)]);
    };

    // 毫秒级视频同步函数
    const startMillisecondSync = () => {
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
        }
        
        syncIntervalRef.current = setInterval(() => {
            if (!sourceVideoRef.current || !subtitleDemoRef.current || !isPlaying) {
                return;
            }
            
            const leftTime = sourceVideoRef.current.currentTime;
            const rightTime = subtitleDemoRef.current.getCurrentTime();
            const timeDiff = Math.abs(leftTime - rightTime);
            
            // 如果时间差超过阈值，进行同步
            if (timeDiff > syncThreshold) {
                // 毫秒级同步
                
                // 以左侧视频为基准，同步右侧视频
                if (subtitleDemoRef.current.seekToTime) {
                    subtitleDemoRef.current.seekToTime(leftTime);
                }
                
                lastSyncTimeRef.current = Date.now();
            }
        }, 16); // 约60fps的同步频率
    };
    
    const stopMillisecondSync = () => {
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
            syncIntervalRef.current = null;
        }
    };
    
    // 精确同步启动函数
    const startPreciseSync = async (startTime: number) => {
        // 先停止之前的同步
        stopMillisecondSync();
        
        // 同时跳转到精确时间点，但不开始播放
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = startTime;
            sourceVideoRef.current.pause(); // 确保左侧视频暂停
        }
        
        if (subtitleDemoRef.current) {
            subtitleDemoRef.current.seekToTime(startTime);
            subtitleDemoRef.current.pause(); // 确保右侧视频暂停
        }
        
        // 等待两个视频都到达目标时间点
        await new Promise<void>((resolve) => {
            const checkSync = () => {
                const leftTime = sourceVideoRef.current?.currentTime || 0;
                const rightTime = subtitleDemoRef.current?.getCurrentTime() || 0;
                const leftDiff = Math.abs(leftTime - startTime);
                const rightDiff = Math.abs(rightTime - startTime);
                
                if (leftDiff < 0.01 && rightDiff < 0.01) {
                    // 精确同步完成
                    resolve();
                } else {
                    setTimeout(checkSync, 5); // 5ms检查一次
                }
            };
            checkSync();
        });
        
        // 启动毫秒级同步
        startMillisecondSync();
    };

    // 这些函数现在由每行的 RowItem 组件独立处理

    // 播放原始台词音频
    const playOriginAudio = (timeRange: string) => {
        if (!videoData || !videoData.subtitles) {
            // 没有字幕数据，无法播放原始音频
            return;
        }

        // 查找对应的字幕数据
        const matchingSubtitle = videoData.subtitles.find(subtitle => {
            const subtitleTimeRange = `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`;
            return subtitleTimeRange === timeRange;
        });

        if (!matchingSubtitle || !matchingSubtitle.origin_audio) {
            // 未找到对应的原始音频
            return;
        }

        // 构建原始音频URL（支持绝对路径和相对路径）
        const originAudioUrl = processVideoUrl(matchingSubtitle.origin_audio);
        // 播放原始台词音频

        // 创建或更新音频元素
        if (!originAudioRef.current) {
            originAudioRef.current = new Audio();
        }

        const originAudio = originAudioRef.current;
        
        // 设置音频源
        if (originAudio.src !== originAudioUrl) {
            originAudio.src = originAudioUrl;
        }

        // 获取语速设置
        // 如果是原始音频origin_audio，不应用倍速
        const audioSpeed = 1.0; // 原始音频不应用倍速

        // 设置音频属性
        originAudio.volume = 1.0;
        originAudio.currentTime = 0;
        // 原始音频不应用倍速
        originAudio.playbackRate = audioSpeed;

        // 等待音频加载完成后再播放
        const playAudioWhenReady = () => {
            let hasPlayed = false; // 防止重复播放的标志
            
            const playAudio = () => {
                if (hasPlayed) return; // 如果已经播放过，直接返回
                hasPlayed = true;
                
                // 确保播放速率为台词数据中的audio_speed值
                originAudio.playbackRate = audioSpeed;
                
                originAudio.play().then(() => {
                    // 原始台词音频播放成功
                }).catch(error => {
                    console.error('原始台词音频播放失败:', error);
                });
            };
            
            if (originAudio.readyState >= 2) { // HAVE_CURRENT_DATA
                playAudio();
            } else {
                // 等待音频加载
                const handleCanPlay = () => {
                    originAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                };
                originAudio.addEventListener('canplay', handleCanPlay);
                
                // 超时保护，如果5秒内没有加载完成，强制播放
                setTimeout(() => {
                    originAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                }, 5000);
            }
        };

        playAudioWhenReady();
    };

    // 停止原始台词音频
    const stopOriginAudio = () => {
        if (originAudioRef.current) {
            originAudioRef.current.pause();
            originAudioRef.current.currentTime = 0;
            // 原始台词音频已停止
        }
    };

    // 切换背景音控制
    const toggleBackgroundAudio = useCallback(() => {
        // 1. 【关键】在点击瞬间强制唤醒音频引擎
        // 这解决了"关闭再打开后进度条动但没声音"的问题
        if (backgroundAudioContextRef.current?.state === 'suspended') {
            backgroundAudioContextRef.current.resume().then(() => {
                console.log('AudioContext 已手动唤醒');
            });
        }

        // 2. 正常的切换状态
        setBackgroundAudioEnabled(prev => !prev);
    }, []);

    // ✅ 监听背景音开关状态，处理播放/暂停
    useEffect(() => {
        const bgAudio = backgroundAudioRef.current;
        const mainVideo = sourceVideoRef.current;

        if (!bgAudio || !mainVideo) return;

        const handleAudioStateChange = async () => {
            if (backgroundAudioEnabled) {
                // 1. 打开时：确保 Web Audio 节点已连接
                if (ensureBackgroundGainNode) {
                    await ensureBackgroundGainNode();
                }

                // 2. 同步时间（非常重要，否则音画不同步）
                bgAudio.currentTime = mainVideo.currentTime;

                // 3. 只有当主视频正在播放时，背景音才自动播放
                if (!mainVideo.paused) {
                    try {
                        await bgAudio.play();
                        console.log('背景音已恢复播放');
                    } catch (error) {
                        console.error('背景音恢复播放失败:', error);
                    }
                }
            } else {
                // 4. 关闭时：直接暂停
                bgAudio.pause();
            }
        };

        handleAudioStateChange();
    }, [backgroundAudioEnabled, ensureBackgroundGainNode]);

    // ✅ 补充：实时监听音量变化，确保 Web Audio 增益值正确
    useEffect(() => {
        if (backgroundGainNodeRef.current && backgroundAudioEnabled) {
            // 确保 backgroundVolume 是有效数值
            const volume = Number.isFinite(backgroundVolume) ? backgroundVolume : 1.0;
            // 立即应用音量
            backgroundGainNodeRef.current.gain.setValueAtTime(volume, backgroundAudioContextRef.current?.currentTime || 0);
        }
    }, [backgroundVolume, backgroundAudioEnabled]);

    // Timeline编辑器类
    class TimelineEditor {
        isPlaying: boolean;
        currentTime: number;
        totalTime: number;
        zoomLevel: number;
        audioEnabled: boolean;
        segments: any[];
        activeTrack: string;
        onZoomChange?: (value: number) => void;
        onPlayheadMove?: (time: number) => void;
        lastScale: number;
        playheadAnimationId?: number; // 用于requestAnimationFrame的ID
        updateCount?: number; // 用于调试的更新计数器
        private videoData?: any; // 保存videoData用于访问字幕数据
        // 移除复杂的动画状态管理，使用简化的缓动动画
        // 用于保存 resize 相关的监听器引用，防止重复添加
        private resizeMouseMoveHandler?: (e: MouseEvent) => void;
        private resizeMouseUpHandler?: () => void;
        private isResizeApiCalling: boolean = false; // 防止重复调用 API
        private resizeDebounceTimer?: number; // 防抖定时器
        // 用于保存 drag 相关的监听器引用，防止重复添加
        private dragMouseDownHandler?: (e: MouseEvent) => void;
        private dragMouseMoveHandler?: (e: MouseEvent) => void;
        private dragMouseUpHandler?: () => void;
        private isDragApiCalling: boolean = false; // 防止拖动时重复调用 API
        private dragApiRequestRef: Promise<void> | null = null; // 保存正在进行的拖动 API 请求
        // 用于保存滚动事件监听器的清理函数
        scrollListenerCleanup?: () => void;
        // 用于设置倍速调节加载状态的回调函数
        private setIsAdjustingSpeed?: (loading: boolean) => void;
        // z-index 管理：用于处理重叠片段的层级
        private maxZIndex: number = 1; // 当前最高的 z-index 值
        // 用于保存 playhead 相关的监听器引用
        private playheadMouseDownHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseDownHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseMoveHandler?: (e: MouseEvent) => void;
        private playheadDocumentMouseUpHandler?: () => void;
        private playheadDocumentClickHandler?: (e: MouseEvent) => void;
        // 用于保存 scrollbar 相关的监听器引用
        private scrollbarThumbMouseDownHandler?: (e: MouseEvent) => void;
        private scrollbarDocumentMouseMoveHandler?: (e: MouseEvent) => void;
        private scrollbarDocumentMouseUpHandler?: (e: MouseEvent) => void;
        private scrollbarTrackMouseDownHandler?: (e: MouseEvent) => void;
        private scrollbarTimelineScrollHandler?: () => void;
        private scrollbarTimelineContent?: HTMLElement | null;
        
        constructor(onZoomChange?: (value: number) => void, onPlayheadMove?: (time: number) => void, videoData?: any, setIsAdjustingSpeed?: (loading: boolean) => void) {
            this.videoData = videoData;
            this.setIsAdjustingSpeed = setIsAdjustingSpeed;
            this.isPlaying = false;
            this.currentTime = 0;
            this.totalTime = this.calculateTotalTime(videoData); // 动态计算总时间
            this.zoomLevel = 25; // 默认缩放级别设为10%
            this.audioEnabled = false;
            this.segments = [];
            this.activeTrack = 'translated'; // 'translated' or 'original'
            this.onZoomChange = onZoomChange;
            this.onPlayheadMove = onPlayheadMove;
            this.lastScale = 1; // 初始化上次缩放比例
            this.maxZIndex = 1; // 初始化 z-index 计数器
            
            this.init();
        }
        
        // 更新videoData引用
        updateVideoData(videoData: any) {
            this.videoData = videoData;
        }
        
        init() {
            this.bindEvents();
            this.generateTimeMarks();
            // 如果存在翻译后轨道，则默认激活翻译后轨道；否则（例如纯净版模式隐藏翻译后轨道）激活原视频轨道
            const translatedTrack = document.getElementById('translatedTrack');
            const defaultTrack: 'translated' | 'original' = translatedTrack ? 'translated' : 'original';
            this.setActiveTrack(defaultTrack);
            this.initPlayhead();
            this.initSegmentDrag(); // 初始化字幕拖拽功能
            
            // 应用初始缩放，确保时间轴宽度正确
            this.applyZoom();
        }
        
        // 初始化字幕片段
        initSubtitles(subtitles: Task.SubtitlesLegacy[]) {
            if (subtitles && subtitles.length > 0) {
                this.generateSegmentsFromSubtitles(subtitles);
            }
        }
        
        // 更新特定字幕片段的时间戳和文本
        updateSegment(subtitleId: number, subtitle: Task.SubtitlesLegacy) {
            const segments = document.querySelectorAll(`[data-subtitle-id="${subtitleId}"]`);
            
            if (segments.length === 0) return;
            
            // 解析时间戳字符串为秒数
            const parseTimeToSeconds = (timeString: string): number => {
                const timeStr = timeString.replace(',', '.');
                const parts = timeStr.split(':');
                
                if (parts.length === 3) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);
                    const seconds = parseFloat(parts[2]);
                    return hours * 3600 + minutes * 60 + seconds;
                } else if (parts.length === 2) {
                    const minutes = parseInt(parts[0], 10);
                    const seconds = parseFloat(parts[1]);
                    return minutes * 60 + seconds;
                }
                
                return 0;
            };
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const track = element.closest('.track');
                
                if (track?.id === 'translatedTrack') {
                    // 更新翻译后轨道片段
                    const start = parseTimeToSeconds(subtitle.post_start_time || subtitle.origin_start_time);
                    const end = parseTimeToSeconds(subtitle.post_end_time || subtitle.origin_end_time);
                    const text = subtitle.post_text || ''; // 只使用post_text，不要回退到origin_text
                    
                    element.dataset.start = start.toString();
                    element.dataset.end = end.toString();
                    element.querySelector('span')!.textContent = text;
                } else if (track?.id === 'originalTrack') {
                    // 更新原视频轨道片段
                    const start = parseTimeToSeconds(subtitle.origin_start_time);
                    const end = parseTimeToSeconds(subtitle.origin_end_time);
                    const text = subtitle.origin_text || '';
                    
                    element.dataset.start = start.toString();
                    element.dataset.end = end.toString();
                    element.querySelector('span')!.textContent = text;
                }
            });
            
            // 重新定位片段
            this.positionSegments();
        }
        
        // 计算时间轴总时长
        calculateTotalTime(videoData?: any) {
            // 开始计算时间轴总时长
            
            // 获取字幕数据（用于计算最大结束时间）
            let subtitles: any[] = [];
            if (videoData && videoData.subtitles) {
                subtitles = videoData.subtitles;
                // 使用传入的videoData
            } else {
                // 从全局videoData中获取字幕数据
                const videoDataElement = document.querySelector('[data-video-data]');
                if (videoDataElement) {
                    try {
                        const data = JSON.parse(videoDataElement.getAttribute('data-video-data') || '{}');
                        subtitles = data.subtitles || [];
                        // 从DOM获取videoData
                    } catch (error) {
                        console.error('解析videoData时出错:', error);
                    }
                } else {
                    // 未找到videoData元素
                }
            }
            
            // 计算字幕的最大结束时间
            let maxEndTime = 0;
            if (subtitles.length > 0) {
                subtitles.forEach((subtitle: any, index: number) => {
                    // 优先使用post_end_time，如果没有则使用origin_end_time
                    const endTimeStr = subtitle.post_end_time || subtitle.origin_end_time || '00:00:00,000';
                    
                    // 将时间字符串转换为秒数 (格式: HH:MM:SS,mmm)
                    const timeParts = endTimeStr.split(':');
                    const hours = parseInt(timeParts[0] || '0');
                    const minutes = parseInt(timeParts[1] || '0');
                    const secondsParts = timeParts[2] ? timeParts[2].split(',') : ['0', '0'];
                    const seconds = parseInt(secondsParts[0] || '0');
                    const milliseconds = parseInt(secondsParts[1] || '0');
                    const endTime = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
                    
                    if (endTime > maxEndTime) {
                        maxEndTime = endTime;
                    }
                });
            }
            
            // 优先使用视频的实际时长（不向上取整）
            const videoElement = document.querySelector('video') as HTMLVideoElement;
            if (videoElement && videoElement.duration && !isNaN(videoElement.duration)) {
                const videoDuration = videoElement.duration;
                // 使用视频实际时长
                // 使用视频实际时长作为上限，确保不超过视频实际时长（不向上取整）
                // 如果字幕结束时间超过视频时长，使用视频实际时长；否则使用视频实际时长（因为视频时长已经包含了所有内容）
                const totalTime = Math.max(20, videoDuration);
                // 计算得到的总时长
                return totalTime;
            }
            
            // 如果无法获取视频时长，则从videoData中获取
            if (videoData && videoData.duration) {
                const videoDuration = videoData.duration;
                // 使用videoData中的时长
                // 使用videoData中的时长，不向上取整
                const totalTime = Math.max(20, videoDuration);
                // 计算得到的总时长
                return totalTime;
            }
            
            // 最后才使用字幕数据计算（作为备选方案）
            if (subtitles.length === 0) {
                // 没有字幕数据，使用默认20秒
                return 20; // 默认20秒
            }
            
            // 使用字幕结束时间，但不添加额外的缓冲时间，也不向上取整
            const totalTime = Math.max(20, maxEndTime);
            // 使用字幕数据计算的总时长
            return totalTime;
        }
        
        bindEvents() {
            // 轨道标签点击事件 - 更新为新的左侧面板选择器
            document.querySelectorAll('.timeline-left-panel .track-label').forEach((label, index) => {
                label.addEventListener('click', () => {
                    const trackType = index === 0 ? 'translated' : 'original';
                    this.setActiveTrack(trackType);
                });
            });
            
            // 滚动条事件
            this.initScrollbar();
            
            // 片段点击事件
            document.querySelectorAll('.segment').forEach(segment => {
                segment.addEventListener('click', (e) => {
                    const target = e.target as HTMLElement;
                    const targetSegment = target.closest('.segment') as HTMLElement;
                    if (targetSegment) {
                        // 如果点击的不是当前激活轨道的片段，先切换轨道
                        const track = targetSegment.closest('.track') as HTMLElement;
                        const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
                        
                        if (this.activeTrack !== trackType) {
                            this.setActiveTrack(trackType);
                        }
                        
                        this.selectSegment(targetSegment);
                    }
                });
            });
            
            // 字幕拖拽调节功能现在在initSegmentDrag中处理
            
            // 轨道点击事件
            this.initTrackClick();
            
            // 缩放控制事件
            this.initZoomControls();
        }
        
        generateTimeMarks() {
            const timeMarksContainer = document.getElementById('timeMarks');
            if (!timeMarksContainer) return;
            
            // 检查是否需要重新生成时间标记（避免不必要的DOM操作）
            const currentScale = 0.8 + (this.zoomLevel / 100) * 1.2;
            if (this.lastScale && Math.abs(currentScale - this.lastScale) < 0.05) {
                return; // 缩放变化很小，不需要重新生成
            }
            
            // 使用DocumentFragment减少DOM操作
            const fragment = document.createDocumentFragment();
            
            const startTime = 0;
            const endTime = this.totalTime; // 使用动态计算的总时长
            const totalRange = endTime - startTime;
            
            // 根据缩放级别动态调整刻度间隔，减少DOM元素数量
            let majorInterval, minorInterval, microInterval;
            if (currentScale < 1.0) {
                // 缩小状态：减少刻度密度
                majorInterval = 5; // 主刻度间隔5秒
                minorInterval = 1; // 次刻度间隔1秒
                microInterval = 0.2; // 微刻度间隔0.2秒
            } else if (currentScale > 1.5) {
                // 放大状态：增加刻度密度
                majorInterval = 0.5; // 主刻度间隔0.5秒
                minorInterval = 0.1; // 次刻度间隔0.1秒
                microInterval = 0.02; // 微刻度间隔0.02秒
            } else {
                // 正常状态：固定刻度间隔
                majorInterval = 1; // 主刻度间隔1秒
                minorInterval = 0.1; // 次刻度间隔0.1秒
                microInterval = 0.01; // 微刻度间隔0.01秒
            }
            
            // 生成主刻度
            for (let time = startTime; time <= endTime; time += majorInterval) {
                const mark = document.createElement('div');
                mark.className = 'time-mark major';
                mark.style.left = `${(time / totalRange) * 100}%`;
                
                // 00:00.0的刻度不显示数字
                if (time > 0) {
                    mark.setAttribute('data-time', this.formatTime(time));
                }
                
                fragment.appendChild(mark);
            }
            
            // 生成次刻度
            for (let time = startTime; time <= endTime; time += minorInterval) {
                if (time % majorInterval !== 0) {
                    const mark = document.createElement('div');
                    mark.className = 'time-mark minor';
                    mark.style.left = `${(time / totalRange) * 100}%`;
                    fragment.appendChild(mark);
                }
            }
            
            // 生成微刻度（仅在缩放级别较高时显示）
            if (this.zoomLevel > 70) { // 当缩放级别超过70%时显示微刻度
                for (let time = startTime; time <= endTime; time += microInterval) {
                    if (time % minorInterval !== 0) { // 跳过次刻度
                        const mark = document.createElement('div');
                        mark.className = 'time-mark micro';
                        mark.style.left = `${(time / totalRange) * 100}%`;
                        fragment.appendChild(mark);
                    }
                }
            }
            
            // 一次性更新DOM
            timeMarksContainer.innerHTML = '';
            timeMarksContainer.appendChild(fragment);
        }
        
        // 根据字幕数据生成时间轴片段
        generateSegmentsFromSubtitles(subtitles: Task.SubtitlesLegacy[]) {
            const translatedTrack = document.getElementById('translatedTrack');
            const originalTrack = document.getElementById('originalTrack');
            
            // 如果连原视频轨道都不存在，则无法生成片段
            if (!originalTrack) return;
            
            // 重置 z-index 计数器（重新生成片段时）
            this.maxZIndex = 1;
            
            // 清空现有片段
            if (translatedTrack) {
                translatedTrack.querySelector('.track-content')!.innerHTML = '';
            }
            originalTrack.querySelector('.track-content')!.innerHTML = '';
            
            // 解析时间戳字符串为秒数
            const parseTimeToSeconds = (timeString: string): number => {
                const timeStr = timeString.replace(',', '.');
                const parts = timeStr.split(':');
                
                if (parts.length === 3) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);
                    const seconds = parseFloat(parts[2]);
                    return hours * 3600 + minutes * 60 + seconds;
                } else if (parts.length === 2) {
                    const minutes = parseInt(parts[0], 10);
                    const seconds = parseFloat(parts[1]);
                    return minutes * 60 + seconds;
                }
                
                return 0;
            };
            
            // 为每个字幕创建片段
            subtitles.forEach((subtitle, index) => {
                // 如果存在“翻译后”轨道，则创建翻译后片段；纯净版模式下可省略
                if (translatedTrack) {
                    // 翻译后轨道片段（使用post_text和post时间戳）
                    const translatedStart = parseTimeToSeconds(subtitle.post_start_time || subtitle.origin_start_time);
                    const translatedEnd = parseTimeToSeconds(subtitle.post_end_time || subtitle.origin_end_time);
                    const translatedText = subtitle.post_text || ''; // 只使用post_text，不要回退到origin_text
                    
                    const translatedSegment = document.createElement('div');
                    translatedSegment.className = 'segment';
                    translatedSegment.dataset.start = translatedStart.toString();
                    translatedSegment.dataset.end = translatedEnd.toString();
                    translatedSegment.dataset.subtitleId = subtitle.id.toString();
                    // 存储原始数据用于倍速计算
                    translatedSegment.dataset.originalStart = translatedStart.toString();
                    translatedSegment.dataset.originalEnd = translatedEnd.toString();
                    translatedSegment.dataset.originalSpeed = (subtitle.audio_speed || 1.0).toString();
                    // 初始化 z-index（基础值，点击时会提升）
                    (translatedSegment as HTMLElement).style.zIndex = '1';
                    
                    const translatedSpan = document.createElement('span');
                    translatedSpan.textContent = translatedText;
                    translatedSegment.appendChild(translatedSpan);
                    
                    translatedTrack.querySelector('.track-content')!.appendChild(translatedSegment);
                }
                
                // 原视频轨道片段（使用origin_text和origin时间戳）
                const originalStart = parseTimeToSeconds(subtitle.origin_start_time);
                const originalEnd = parseTimeToSeconds(subtitle.origin_end_time);
                const originalText = subtitle.origin_text || '';
                
                const originalSegment = document.createElement('div');
                originalSegment.className = 'segment';
                originalSegment.dataset.start = originalStart.toString();
                originalSegment.dataset.end = originalEnd.toString();
                originalSegment.dataset.subtitleId = subtitle.id.toString();
                // 初始化 z-index（基础值，点击时会提升）
                (originalSegment as HTMLElement).style.zIndex = '1';
                
                const originalSpan = document.createElement('span');
                originalSpan.textContent = originalText;
                originalSegment.appendChild(originalSpan);
                
                originalTrack.querySelector('.track-content')!.appendChild(originalSegment);
            });
            
            // 重新绑定片段点击事件
            this.bindSegmentEvents();
            
            // 重新定位片段
            this.positionSegments();
            
            // 重新初始化拖拽功能（因为DOM元素已更新）
            this.initSegmentDrag();
        }
        
        // 绑定片段点击事件
        bindSegmentEvents() {
            document.querySelectorAll('.segment').forEach(segment => {
                segment.addEventListener('click', (e) => {
                    e.stopPropagation();
                    
                    // 如果点击的不是当前激活轨道的片段，先切换轨道
                    const track = segment.closest('.track') as HTMLElement;
                    const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
                    
                    if (this.activeTrack !== trackType) {
                        this.setActiveTrack(trackType);
                    }
                    
                    this.selectSegment(segment as HTMLElement);
                });
            });
        }
        
        // 初始化字幕片段拖拽功能
        initSegmentDrag() {
            // 如果已经绑定过事件，先移除旧的监听器
            if (this.dragMouseDownHandler) {
                document.removeEventListener('mousedown', this.dragMouseDownHandler);
            }
            if (this.dragMouseMoveHandler) {
                document.removeEventListener('mousemove', this.dragMouseMoveHandler);
            }
            if (this.dragMouseUpHandler) {
                document.removeEventListener('mouseup', this.dragMouseUpHandler);
            }
            
            let isDragging = false;
            let currentSegment: HTMLElement | null = null;
            let dragStartX = 0;
            let isDragApiTriggered = false; // 标记本次拖动是否已触发API
            let dragStartTime = 0;
            let dragStartY = 0; // 添加Y坐标，用于计算拖动距离
            let hasMoved = false; // 添加标志位，判断是否真的发生了拖动
            let animationFrameId: number | null = null;
            
            this.dragMouseDownHandler = (e: MouseEvent) => {
                // 查找最近的字幕元素
                const segment = (e.target as HTMLElement).closest('.segment') as HTMLElement;
                if (!segment) return;

                const originalTrack = document.getElementById('originalTrack');
                const translatedTrack = document.getElementById('translatedTrack');
                const isInOriginalTrack = !!originalTrack && originalTrack.contains(segment);
                const isInTranslatedTrack = !!translatedTrack && translatedTrack.contains(segment);

                // 原视频轨道不允许拖拽和调节
                if (isInOriginalTrack) return;

                // 翻译轨道：未选中时先自动选中，再进入拖拽/调节逻辑，实现“自动选中、无需抬起”的连续拖动
                if (isInTranslatedTrack && !segment.classList.contains('active')) {
                    this.setActiveTrack('translated');
                    this.selectSegment(segment);
                }

                // 仅翻译轨道片段参与拖拽/调节（含刚选中的）
                if (!isInTranslatedTrack) return;

                const rect = segment.getBoundingClientRect();
                const clickX = e.clientX - rect.left;

                // 判断点击位置是否在左右调节区域
                if (clickX < 10) {
                    this.initSegmentResize(segment, 'left', e);
                } else if (clickX > rect.width - 10) {
                    this.initSegmentResize(segment, 'right', e);
                } else {
                    // 中间区域：准备拖动整个片段（移动鼠标即开始拖动，无需抬起）
                    currentSegment = segment;
                    dragStartX = e.clientX;
                    dragStartY = e.clientY;
                    dragStartTime = parseFloat(segment.dataset.start || '0');
                    hasMoved = false;
                    isDragApiTriggered = false;

                    (currentSegment as any).originalStart = dragStartTime;
                    (currentSegment as any).originalEnd = parseFloat(segment.dataset.end || '0');

                    e.preventDefault();
                    document.body.style.userSelect = 'none';
                    document.body.style.cursor = 'grab';
                }
            };
            
            document.addEventListener('mousedown', this.dragMouseDownHandler);
            
            // 使用 requestAnimationFrame 优化拖拽性能
            this.dragMouseMoveHandler = (e: MouseEvent) => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
                
                animationFrameId = requestAnimationFrame(() => {
                    if (currentSegment && !isDragging) {
                        // 检查是否真的发生了拖动（移动距离超过3px）
                        const deltaX = Math.abs(e.clientX - dragStartX);
                        const deltaY = Math.abs(e.clientY - dragStartY);
                        const moveDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                        
                        if (moveDistance > 3) {
                            // 移动距离超过阈值，开始真正的拖动
                            isDragging = true;
                            hasMoved = true;
                            document.body.style.cursor = 'grabbing';
                        }
                    }
                    
                    if (isDragging && currentSegment) {
                        // 拖动整个字幕
                        const trackContent = currentSegment.closest('.track-content') as HTMLElement;
                        const trackRect = trackContent.getBoundingClientRect();
                        const deltaX = e.clientX - dragStartX;
                        const deltaTime = (deltaX / trackRect.width) * this.totalTime;
                        
                        // 使用记录的原始时间，确保宽度不变
                        const originalStart = (currentSegment as any).originalStart;
                        const originalEnd = (currentSegment as any).originalEnd;
                        const duration = originalEnd - originalStart; // 保持原始宽度
                        
                        let newStart = originalStart + deltaTime;
                        let newEnd = newStart + duration; // 确保宽度不变
                        
                        // 边界检查
                        newStart = Math.max(0, Math.min(newStart, this.totalTime - duration));
                        newEnd = newStart + duration; // 重新计算结束时间以保持宽度
                        
                        // 获取同一轨道上的所有字幕（排除当前字幕）
                        const allSegments = Array.from(trackContent.querySelectorAll('.segment'))
                            .filter(seg => seg !== currentSegment)
                            .map(seg => ({
                                element: seg,
                                start: parseFloat((seg as HTMLElement).dataset.start || '0'),
                                end: parseFloat((seg as HTMLElement).dataset.end || '0')
                            }))
                            .sort((a, b) => a.start - b.start);
                        
                        // 检查与其他字幕的重叠，如果重叠则调整位置
                        let finalStart = newStart;
                        let finalEnd = newEnd;
                        
                        for (const segment of allSegments) {
                            if (finalStart < segment.end && finalEnd > segment.start) {
                                // 有重叠，选择最近的合法位置
                                const leftGap = segment.start - finalEnd;
                                const rightGap = finalStart - segment.end;
                                
                                if (leftGap >= 0) {
                                    // 可以放在左侧
                                    finalStart = segment.start - duration;
                                    finalEnd = segment.start;
                                } else if (rightGap >= 0) {
                                    // 可以放在右侧
                                    finalStart = segment.end;
                                    finalEnd = segment.end + duration;
                                } else {
                                    // 重叠，选择距离最近的位置
                                    const center = (segment.start + segment.end) / 2;
                                    if (newStart < center) {
                                        finalStart = segment.start - duration;
                                        finalEnd = segment.start;
                                    } else {
                                        finalStart = segment.end;
                                        finalEnd = segment.end + duration;
                                    }
                                }
                                
                                // 重新检查边界
                                finalStart = Math.max(0, Math.min(finalStart, this.totalTime - duration));
                                finalEnd = finalStart + duration;
                            }
                        }
                        
                        // 始终更新位置，确保跟随鼠标
                        currentSegment.dataset.start = finalStart.toFixed(3);
                        currentSegment.dataset.end = finalEnd.toFixed(3);
                        
                        // 直接更新当前字幕的位置，避免重新计算所有字幕
                        const leftPercent = (finalStart / this.totalTime) * 100;
                        const widthPercent = (duration / this.totalTime) * 100;
                        currentSegment.style.left = `${leftPercent}%`;
                        currentSegment.style.width = `${widthPercent}%`;
                    }
                });
            };
            
            document.addEventListener('mousemove', this.dragMouseMoveHandler);
            
            this.dragMouseUpHandler = () => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                
                // 只有当真正发生了拖动（hasMoved为true）且本次拖动还未触发API时才调用API
                if (isDragging && currentSegment && hasMoved && !isDragApiTriggered) {
                    // 标记已触发，防止重复调用
                    isDragApiTriggered = true;
                    
                    // 如果有正在进行的拖动API请求，先取消它（使用最新的拖动结果）
                    if (this.isDragApiCalling && this.dragApiRequestRef) {
                        console.log('取消之前的拖动API请求，使用最新的拖动结果');
                        // 不等待之前的请求完成，直接使用最新的拖动结果
                    }
                    
                    // 拖动结束后重新计算所有字幕位置，确保一致性
                    this.positionSegments();
                    
                    // 输出拖拽后的时间戳到控制台
                    const startTime = parseFloat(currentSegment.dataset.start || '0');
                    const endTime = parseFloat(currentSegment.dataset.end || '0');
                    const segmentText = currentSegment.querySelector('span')?.textContent || '';
                    const subtitleId = parseInt(currentSegment.dataset.subtitleId || '0');
                    
                    // 字幕片段拖拽完成
                    
                    // 调用API更新时间戳并同步到右侧台词区域（只在鼠标松开时触发一次）
                    this.updateSubtitleTimestamps(subtitleId, startTime, endTime).finally(() => {
                        // API调用完成后重置标记，允许下次拖动
                        isDragApiTriggered = false;
                    });
                }
                
                isDragging = false;
                currentSegment = null;
                hasMoved = false;
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            };
            
            document.addEventListener('mouseup', this.dragMouseUpHandler);
        }
        
        // 初始化字幕片段边界调整功能
        initSegmentResize(segment: HTMLElement, resizeType: 'left' | 'right', e: MouseEvent) {
            // 检查是否在原视频轨道中
            const originalTrack = document.getElementById('originalTrack');
            const isInOriginalTrack = originalTrack && originalTrack.contains(segment);
            
            // 原视频轨道的台词不允许调节边界
            if (isInOriginalTrack) {
                // 原视频轨道的台词不允许调节边界
                return;
            }
            
            // 先移除旧的监听器，防止重复添加
            if (this.resizeMouseMoveHandler) {
                document.removeEventListener('mousemove', this.resizeMouseMoveHandler);
            }
            if (this.resizeMouseUpHandler) {
                document.removeEventListener('mouseup', this.resizeMouseUpHandler);
            }
            
            // 清除之前的防抖定时器
            if (this.resizeDebounceTimer) {
                clearTimeout(this.resizeDebounceTimer);
                this.resizeDebounceTimer = undefined;
            }
            
            let isResizing = true;
            let animationFrameId: number | null = null;
            const currentSegment = segment;
            const currentResizeType = resizeType;
            
            const handleMouseMove = (e: MouseEvent) => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
                
                animationFrameId = requestAnimationFrame(() => {
                    if (isResizing) {
                        const trackContent = currentSegment.closest('.track-content') as HTMLElement;
                        const trackRect = trackContent.getBoundingClientRect();
                        const clickX = e.clientX - trackRect.left;
                        const percentage = Math.max(0, Math.min(1, clickX / trackRect.width));
                        
                        const newTime = percentage * this.totalTime;
                        
                        // 获取同一轨道上的所有字幕
                        const allSegments = Array.from(trackContent.querySelectorAll('.segment'))
                            .map(seg => ({
                                element: seg,
                                start: parseFloat((seg as HTMLElement).dataset.start || '0'),
                                end: parseFloat((seg as HTMLElement).dataset.end || '0')
                            }))
                            .sort((a, b) => a.start - b.start);
                        
                        const currentIndex = allSegments.findIndex(seg => seg.element === currentSegment);
                        
                        if (currentResizeType === 'left') {
                            const endTime = parseFloat(currentSegment.dataset.end || '0');
                            let newStart = Math.min(newTime, endTime - 0.001); // 最小0.001秒
                            
                            // 检查与左侧字幕的重叠
                            if (currentIndex > 0) {
                                const leftSegment = allSegments[currentIndex - 1];
                                newStart = Math.max(newStart, leftSegment.end);
                            }
                            
                            // 检查倍速限制（0.5到2.0之间）
                            const originalStart = parseFloat(currentSegment.dataset.originalStart || currentSegment.dataset.start || '0');
                            const originalEnd = parseFloat(currentSegment.dataset.originalEnd || currentSegment.dataset.end || '0');
                            const originalDuration = originalEnd - originalStart;
                            const newDuration = endTime - newStart;
                            const newSpeed = (originalDuration * (parseFloat(currentSegment.dataset.originalSpeed || '1.0'))) / newDuration;
                            
                            if (newSpeed < 0.5 || newSpeed > 2.0) {
                                // 倍速超出范围，不允许拖拽
                                return;
                            }
                            
                            currentSegment.dataset.start = newStart.toFixed(3);
                        } else if (currentResizeType === 'right') {
                            const startTime = parseFloat(currentSegment.dataset.start || '0');
                            let newEnd = Math.max(newTime, startTime + 0.001); // 最小0.001秒
                            
                            // 检查与右侧字幕的重叠
                            if (currentIndex < allSegments.length - 1) {
                                const rightSegment = allSegments[currentIndex + 1];
                                newEnd = Math.min(newEnd, rightSegment.start);
                            }
                            
                            // 检查倍速限制（0.5到2.0之间）
                            const originalStart = parseFloat(currentSegment.dataset.originalStart || currentSegment.dataset.start || '0');
                            const originalEnd = parseFloat(currentSegment.dataset.originalEnd || currentSegment.dataset.end || '0');
                            const originalDuration = originalEnd - originalStart;
                            const newDuration = newEnd - startTime;
                            const newSpeed = (originalDuration * (parseFloat(currentSegment.dataset.originalSpeed || '1.0'))) / newDuration;
                            
                            if (newSpeed < 0.5 || newSpeed > 2.0) {
                                // 倍速超出范围，不允许拖拽
                                return;
                            }
                            
                            currentSegment.dataset.end = newEnd.toFixed(3);
                        }
                        
                        this.positionSegments();
                    }
                });
            };
            
            // 保存监听器引用
            this.resizeMouseMoveHandler = handleMouseMove;
            
            const handleMouseUp = () => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                
                // 移除监听器
                if (this.resizeMouseMoveHandler) {
                    document.removeEventListener('mousemove', this.resizeMouseMoveHandler);
                    this.resizeMouseMoveHandler = undefined;
                }
                if (this.resizeMouseUpHandler) {
                    document.removeEventListener('mouseup', this.resizeMouseUpHandler);
                    this.resizeMouseUpHandler = undefined;
                }
                
                // 清除防抖定时器
                if (this.resizeDebounceTimer) {
                    clearTimeout(this.resizeDebounceTimer);
                }
                
                if (isResizing) {
                    // 使用防抖机制，延迟调用 API，避免频繁调用
                    this.resizeDebounceTimer = window.setTimeout(() => {
                        // 检查是否正在调用 API，防止重复调用
                        if (this.isResizeApiCalling) {
                            // API 调用正在进行中，跳过本次调用
                            return;
                        }
                        
                        // 输出调整后的时间戳到控制台
                        const startTime = parseFloat(currentSegment.dataset.start || '0');
                        const endTime = parseFloat(currentSegment.dataset.end || '0');
                        const segmentText = currentSegment.querySelector('span')?.textContent || '';
                        const subtitleId = parseInt(currentSegment.dataset.subtitleId || '0');
                        
                        // 字幕片段边界调整完成
                        // 结束时间已记录
                        
                        // 设置标志位，防止重复调用
                        this.isResizeApiCalling = true;
                        
                        // 计算新的倍速值并调用API
                        this.calculateAndUpdateSpeed(currentSegment, subtitleId, startTime, endTime)
                            .finally(() => {
                                // API 调用完成后重置标志位
                                this.isResizeApiCalling = false;
                            });
                    }, 300); // 300ms 防抖延迟
                }
                
                isResizing = false;
            };
            
            // 保存监听器引用
            this.resizeMouseUpHandler = handleMouseUp;
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        
        // 格式化时间显示
        formatTime(seconds: number): string {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = (seconds % 60).toFixed(3);
            return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.padStart(6, '0')}`;
        }
        
        // 计算并更新倍速值
        async calculateAndUpdateSpeed(segment: HTMLElement, subtitleId: number, newStartTime: number, newEndTime: number) {
            try {
                // 显示加载遮罩
                if (this.setIsAdjustingSpeed) {
                    this.setIsAdjustingSpeed(true);
                }
                
                // 优先从接口返回的 origin_post_time 获取"最原始音频时长"（单位：秒，三位小数）
                let originDuration = 0;
                if (this.videoData?.subtitles) {
                    const subtitle = this.videoData.subtitles.find((sub: any) => sub.id === subtitleId);
                    if (subtitle) {
                        if (typeof subtitle.origin_post_time === 'number' && !Number.isNaN(subtitle.origin_post_time)) {
                            originDuration = subtitle.origin_post_time;
                        } else if (subtitle.origin_start_time && subtitle.origin_end_time) {
                            // 兼容老数据：如果没有 origin_post_time，则使用 origin_start_time 和 origin_end_time 计算
                            // 解析时间格式 "00:00:01,850" 为秒数
                            const parseTimeToSeconds = (timeStr: string): number => {
                                const [time, milliseconds] = timeStr.split(',');
                                const [hours, minutes, seconds] = time.split(':').map(Number);
                                const ms = parseInt(milliseconds);
                                return hours * 3600 + minutes * 60 + seconds + ms / 1000;
                            };
                            const originStart = parseTimeToSeconds(subtitle.origin_start_time);
                            const originEnd = parseTimeToSeconds(subtitle.origin_end_time);
                            originDuration = originEnd - originStart;
                        }
                    }
                }
                
                // 如果依然无法获取“最原始音频时长”，回退到使用 dataset 中的原始时间戳
                if (originDuration === 0) {
                    const originalStart = parseFloat(segment.dataset.originalStart || '0');
                    const originalEnd = parseFloat(segment.dataset.originalEnd || '0');
                    originDuration = originalEnd - originalStart;
                }
                
                // 计算新的时长
                const newDuration = newEndTime - newStartTime;
                
                // 根据新公式计算倍速值：新倍速 = origin_post_time / 新时长
                const newSpeed = originDuration / newDuration;
                
                // 保留三位小数
                const roundedSpeed = Math.round(newSpeed * 1000) / 1000;
                
                // 倍速计算完成
                
                // 检查倍速是否在有效范围内
                if (roundedSpeed < 0.5 || roundedSpeed > 2.0) {
                    console.warn('倍速超出范围，无法更新:', roundedSpeed);
                    return;
                }
                
                // 格式化时间戳为API需要的格式
                const formatTimeForApi = (seconds: number): string => {
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    const secs = Math.floor(seconds % 60);
                    const ms = Math.round((seconds % 1) * 1000);
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
                };
                
                const postStartTime = formatTimeForApi(newStartTime);
                const postEndTime = formatTimeForApi(newEndTime);
                
                // 获取当前字幕的volumeGainDb，如果没有则默认为1.0
                const currentSubtitle = videoData?.subtitles?.find(sub => sub.id === subtitleId);
                const currentVolumeGainDb = currentSubtitle?.volume_gain_db ?? 1.0;
                
                // 调用统一接口，applyToAll固定为false（调节倍速时）
                // 传入时间戳信息，避免"数据已被修改"错误
                const response = await adjustVolumeAndSpeedApi({
                    subtitleId: subtitleId,
                    volumeGainDb: currentVolumeGainDb,  // 保持当前音量不变
                    audioSpeed: roundedSpeed,  // 新的倍速值
                    applyToAll: false,  // 调节倍速时固定为false
                    postStartTime: postStartTime,  // 传入新的开始时间戳
                    postEndTime: postEndTime  // 传入新的结束时间戳
                });
                
                if (response.code === 0 && response.data && response.data.length > 0) {
                    const updatedSubtitle = response.data[0];
                    // 时间轴调节倍速成功
                    
                    // 同步更新volumeGainDb和audioSpeed到本地数据
                    const updatedVolumeGainDb = updatedSubtitle.volumeGainDb ?? currentVolumeGainDb;
                    const updatedAudioSpeed = updatedSubtitle.audioSpeed ?? roundedSpeed;
                    
                    // 更新segment的原始倍速数据
                    segment.dataset.originalSpeed = roundedSpeed.toString();
                    
                    // 直接更新targetText中的时间范围，不触发videoData更新
                    setTargetText(prev => prev.map(item => {
                        if (item.id === subtitleId) {
                            return {
                                ...item,
                                timeRange: `${postStartTime} → ${postEndTime}`
                            };
                        }
                        return item;
                    }));
                    
                    // 采用"改哪个动哪个"的原则，将更新数据存储到待同步队列
                    setPendingTimelineUpdates(prev => {
                        const newMap = new Map(prev);
                        newMap.set(subtitleId, {
                            audio_speed: updatedAudioSpeed,
                            post_start_time: postStartTime,
                            post_end_time: postEndTime
                        });
                        return newMap;
                    });
                    
                    // 时间轴调节完成，数据已更新到segment和targetText，待同步到videoData
                    
                    // 立即更新videoData中的audio_speed和volume_gain_db，确保下次播放时使用新值
                    setVideoData(prev => {
                        if (!prev?.subtitles) return prev;
                        
                        const updatedSubtitles = prev.subtitles.map(subtitle => {
                            if (subtitle.id === subtitleId) {
                                return {
                                    ...subtitle,
                                    audio_speed: updatedAudioSpeed,
                                    volume_gain_db: updatedVolumeGainDb,
                                    post_start_time: postStartTime,
                                    post_end_time: postEndTime
                                };
                            }
                            return subtitle;
                        });
                        
                        return {
                            ...prev,
                            subtitles: updatedSubtitles
                        };
                    });
                    
                    // 如果当前正在播放该字幕的音频，实时更新语速
                    const audioId = `target_audio_${subtitleId}`;
                    
                    // 检查AudioManager播放的音频（通过playTargetSubtitleAudio播放）
                    if (audioManager.isPlaying(audioId)) {
                        audioManager.setPlaybackRate(audioId, updatedAudioSpeed);
                        // 时间轴调节：实时更新AudioManager音频语速
                    }
                    
                    // 检查原生Audio播放的翻译音频（通过playTransAudioByTime自动播放）
                    if (transAudioRef.current && !transAudioRef.current.paused && currentPlayingSubtitleIdRef.current === subtitleId) {
                        transAudioRef.current.playbackRate = updatedAudioSpeed;
                        // 时间轴调节：实时更新原生Audio翻译音频语速
                    }
                    
                    // 检查全局播放实例（通过playTargetSubtitleAudio播放的回退模式）
                    if (currentPlayingInstanceRef.current && 
                        currentPlayingInstanceRef.current.type === 'target' && 
                        currentPlayingInstanceRef.current.subtitleId === subtitleId &&
                        transAudioRef.current && 
                        !transAudioRef.current.paused) {
                        transAudioRef.current.playbackRate = roundedSpeed;
                        // 时间轴调节：实时更新全局播放实例音频语速
                    }
                }
            } catch (error: any) {
                console.error('时间轴调节倍速失败:', error);
                
                // 检查是否是"数据已被修改"错误
                const errorMessage = error?.message || error?.detail || error?.error || '';
                if (errorMessage.includes('数据已被修改') || errorMessage.includes('请刷新后重试')) {
                    console.warn('数据已被修改，建议刷新页面后重试');
                    // 可以在这里添加用户提示，比如使用 showError 或 message
                    // 但这里没有 showError 的上下文，所以只在控制台输出
                } else {
                    // 其他错误也输出到控制台
                    console.error('时间轴调节倍速失败，错误详情:', {
                        message: errorMessage,
                        status: error?.status,
                        response: error?.response
                    });
                }
            } finally {
                // 隐藏加载遮罩
                if (this.setIsAdjustingSpeed) {
                    this.setIsAdjustingSpeed(false);
                }
            }
        }
        // 更新字幕时间戳（拖动字幕时调用，只在鼠标松开时触发）
        async updateSubtitleTimestamps(subtitleId: number, newStartTime: number, newEndTime: number) {
            // 如果已经有请求在进行中，取消之前的请求，使用最新的拖动结果
            if (this.isDragApiCalling && this.dragApiRequestRef) {
                console.log('取消之前的拖动API请求，使用最新的拖动结果');
                // 标记之前的请求为已取消（不等待完成）
                this.isDragApiCalling = false;
                this.dragApiRequestRef = null;
            }
            
            // 标记请求开始
            this.isDragApiCalling = true;
            
            // 创建请求Promise并保存
            const requestPromise = (async () => {
                try {
                    // 格式化时间戳为API需要的格式（使用点分隔毫秒：HH:mm:ss.SSS）
                    const formatTimeForApi = (seconds: number): string => {
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = Math.floor(seconds % 60);
                        const ms = Math.round((seconds % 1) * 1000);
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
                    };
                    
                    const postStartTime = formatTimeForApi(newStartTime);
                    const postEndTime = formatTimeForApi(newEndTime);
                    
                    // 拖动字幕：调用API更新时间戳
                    
                    // 调用新接口：/video/subtitles/adjust-timestamp-range
                    const response = await adjustTimestampRangeApi({
                        id: subtitleId,
                        postStartTime: postStartTime,
                        postEndTime: postEndTime
                    });
                    
                    if (response.code === 0 && response.data) {
                        // 拖动字幕：API调用成功
                        
                        // 将用户传入的postStartTime和postEndTime同步到本地数据
                        // 注意：需要转换为本地使用的格式（逗号分隔毫秒：HH:mm:ss,SSS）
                        const formatTimeForLocal = (timeStr: string): string => {
                            return timeStr.replace('.', ',');
                        };
                        
                        const localPostStartTime = formatTimeForLocal(postStartTime);
                        const localPostEndTime = formatTimeForLocal(postEndTime);
                        
                        // 1. 直接更新右侧台词区域的时间范围
                        setTargetText(prev => prev.map(item => {
                            if (item.id === subtitleId) {
                                return {
                                    ...item,
                                    timeRange: `${localPostStartTime} → ${localPostEndTime}`
                                };
                            }
                            return item;
                        }));

                        // 2. 立即同步到 videoData.subtitles，避免后续 useEffect 用旧时间戳覆盖拖动结果
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;

                            const updatedSubtitles = prev.subtitles.map(subtitle => {
                                if (subtitle.id === subtitleId) {
                                    return {
                                        ...subtitle,
                                        post_start_time: localPostStartTime,
                                        post_end_time: localPostEndTime
                                    };
                                }
                                return subtitle;
                            });

                            return {
                                ...prev,
                                subtitles: updatedSubtitles
                            };
                        });

                        // 3. 更新/清理待同步队列（只保留当前最新结果，防止脏数据）
                        setPendingTimelineUpdates(prev => {
                            const newMap = new Map(prev);
                            newMap.set(subtitleId, {
                                audio_speed: prev.get(subtitleId)?.audio_speed || 1.0, // 保持原有的倍速值
                                post_start_time: localPostStartTime,
                                post_end_time: localPostEndTime
                            });
                            return newMap;
                        });
                    }
                } catch (error) {
                    console.error('拖动字幕：API调用失败', error);
                } finally {
                    // 请求完成，清除标记
                    this.isDragApiCalling = false;
                    this.dragApiRequestRef = null;
                }
            })();
            
            // 保存请求引用
            this.dragApiRequestRef = requestPromise;
            
            // 等待请求完成
            await requestPromise;
        }
        
        // 更新数据（避免频繁销毁重建）
        updateData(newVideoData: any) {
            this.videoData = newVideoData;
            this.totalTime = this.calculateTotalTime(newVideoData);
            // 重新渲染刻度和片段，但不重新绑定 document 事件
            this.generateTimeMarks();
            if (newVideoData?.subtitles) {
                this.generateSegmentsFromSubtitles(newVideoData.subtitles);
            }
        }
        
        // 销毁方法：清理所有资源和事件监听器
        destroy() {
            console.log('清理 TimelineEditor 资源和监听器');
            
            // 移除拖拽相关
            if (this.dragMouseDownHandler) {
                document.removeEventListener('mousedown', this.dragMouseDownHandler);
            }
            if (this.dragMouseMoveHandler) {
                document.removeEventListener('mousemove', this.dragMouseMoveHandler);
            }
            if (this.dragMouseUpHandler) {
                document.removeEventListener('mouseup', this.dragMouseUpHandler);
            }
            
            // 移除缩放/调节边界相关
            if (this.resizeMouseMoveHandler) {
                document.removeEventListener('mousemove', this.resizeMouseMoveHandler);
            }
            if (this.resizeMouseUpHandler) {
                document.removeEventListener('mouseup', this.resizeMouseUpHandler);
            }
            
            // 移除播放头相关
            const playhead = document.getElementById('playhead');
            if (playhead && this.playheadMouseDownHandler) {
                playhead.removeEventListener('mousedown', this.playheadMouseDownHandler);
            }
            if (this.playheadDocumentMouseDownHandler) {
                document.removeEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            }
            if (this.playheadDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            }
            if (this.playheadDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            }
            if (this.playheadDocumentClickHandler) {
                document.removeEventListener('click', this.playheadDocumentClickHandler);
            }
            
            // 移除滚动条相关
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track');
            if (scrollbarThumb && this.scrollbarThumbMouseDownHandler) {
                scrollbarThumb.removeEventListener('mousedown', this.scrollbarThumbMouseDownHandler);
            }
            if (this.scrollbarDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.scrollbarDocumentMouseMoveHandler);
            }
            if (this.scrollbarDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.scrollbarDocumentMouseUpHandler);
            }
            if (scrollbarTrack && this.scrollbarTrackMouseDownHandler) {
                (scrollbarTrack as HTMLElement).removeEventListener('mousedown', this.scrollbarTrackMouseDownHandler);
            }
            if (this.scrollbarTimelineContent && this.scrollbarTimelineScrollHandler) {
                this.scrollbarTimelineContent.removeEventListener('scroll', this.scrollbarTimelineScrollHandler);
            }
            
            // 清理定时器
            if (this.resizeDebounceTimer) {
                clearTimeout(this.resizeDebounceTimer);
            }
            if (this.playheadAnimationId) {
                cancelAnimationFrame(this.playheadAnimationId);
            }
            
            // 执行传入的滚动清理
            if (this.scrollListenerCleanup) {
                this.scrollListenerCleanup();
            }
            
            // 重置所有引用
            this.dragMouseDownHandler = undefined;
            this.dragMouseMoveHandler = undefined;
            this.dragMouseUpHandler = undefined;
            this.resizeMouseMoveHandler = undefined;
            this.resizeMouseUpHandler = undefined;
            this.playheadMouseDownHandler = undefined;
            this.playheadDocumentMouseDownHandler = undefined;
            this.playheadDocumentMouseMoveHandler = undefined;
            this.playheadDocumentMouseUpHandler = undefined;
            this.playheadDocumentClickHandler = undefined;
            this.scrollbarThumbMouseDownHandler = undefined;
            this.scrollbarDocumentMouseMoveHandler = undefined;
            this.scrollbarDocumentMouseUpHandler = undefined;
            this.scrollbarTrackMouseDownHandler = undefined;
            this.scrollbarTimelineScrollHandler = undefined;
            this.scrollbarTimelineContent = null;
        }
        
        // 同步待更新的时间轴数据到videoData
        syncPendingUpdatesToVideoData(pendingUpdates: Map<number, { audio_speed: number; post_start_time: string; post_end_time: string; }>) {
            if (pendingUpdates.size === 0) return;
            
            // 同步待更新的时间轴数据到videoData
            
            setVideoData(prev => {
                if (!prev?.subtitles) return prev;
                
                const updatedSubtitles = prev.subtitles.map(subtitle => {
                    const pendingUpdate = pendingUpdates.get(subtitle.id);
                    if (pendingUpdate) {
                        return {
                            ...subtitle,
                            audio_speed: pendingUpdate.audio_speed,
                            post_start_time: pendingUpdate.post_start_time,
                            post_end_time: pendingUpdate.post_end_time
                        };
                    }
                    return subtitle;
                });
                
                return {
                    ...prev,
                    subtitles: updatedSubtitles
                };
            });
            
            // 清空待同步队列
            setPendingTimelineUpdates(new Map());
        }
        
        positionSegments() {
            const segments = document.querySelectorAll('.segment');
            const totalTime = this.totalTime; // 使用动态计算的总时长
            
            // 根据缩放级别调整位置计算
            const scale = 0.8 + (this.zoomLevel / 100) * 1.2;
            
            // 批量更新样式，减少重排
            const updates: Array<{ element: HTMLElement; left: string; width: string }> = [];
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const start = parseFloat(element.dataset.start || '0');
                const end = parseFloat(element.dataset.end || '0');
                const duration = end - start;
                
                // 使用缩放后的宽度计算位置
                const leftPercent = (start / totalTime) * 100;
                const widthPercent = (duration / totalTime) * 100;
                
                updates.push({
                    element,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`
                });
            });
            
            // 批量应用样式更新
            requestAnimationFrame(() => {
                updates.forEach(({ element, left, width }) => {
                    element.style.left = left;
                    element.style.width = width;
                });
            });
        }
        
        // 轻量级字幕定位（用于拖动时的实时反馈）
        positionSegmentsLight() {
            const segments = document.querySelectorAll('.segment');
            const totalTime = this.totalTime; // 使用动态计算的总时长
            
            // 批量更新样式，减少重排和重绘
            const updates: Array<{ element: HTMLElement; left: string; width: string }> = [];
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const start = parseFloat(element.dataset.start || '0');
                const end = parseFloat(element.dataset.end || '0');
                const duration = end - start;
                
                // 使用缩放后的宽度计算位置
                const leftPercent = (start / totalTime) * 100;
                const widthPercent = (duration / totalTime) * 100;
                
                updates.push({
                    element,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`
                });
            });
            
            // 批量应用样式更新
            updates.forEach(({ element, left, width }) => {
                element.style.left = left;
                element.style.width = width;
            });
        }
        
        
        setActiveTrack(trackType: string) {
            this.activeTrack = trackType;
            
            // 更新轨道标签状态 - 更新为新的左侧面板选择器
            const trackLabels = document.querySelectorAll('.timeline-left-panel .track-label');
            if (trackLabels.length === 1) {
                // 纯净版模式：只有“原视频”一条轨道标签，始终高亮
                trackLabels[0].classList.add('active');
            } else {
                trackLabels.forEach((label, index) => {
                    const isActive = (trackType === 'translated' && index === 0) || 
                                   (trackType === 'original' && index === 1);
                    label.classList.toggle('active', isActive);
                });
            }
            
            // 更新轨道状态
            const translatedTrack = document.getElementById('translatedTrack');
            const originalTrack = document.getElementById('originalTrack');
            
            if (translatedTrack && originalTrack) {
                // 移除所有轨道的active和disabled类
                translatedTrack.classList.remove('active', 'disabled');
                originalTrack.classList.remove('active', 'disabled');
                
                // 设置激活轨道的状态
                if (trackType === 'translated') {
                    translatedTrack.classList.add('active');
                    originalTrack.classList.add('disabled'); // 非激活轨道显示为禁用状态
                } else {
                    originalTrack.classList.add('active');
                    translatedTrack.classList.add('disabled'); // 非激活轨道显示为禁用状态
                }
                
                // 更新字幕状态
                const translatedSegments = translatedTrack.querySelectorAll('.segment');
                const originalSegments = originalTrack.querySelectorAll('.segment');
                
                if (trackType === 'translated') {
                    translatedSegments.forEach(segment => segment.classList.remove('inactive'));
                    originalSegments.forEach(segment => segment.classList.add('inactive'));
                } else {
                    originalSegments.forEach(segment => segment.classList.remove('inactive'));
                    translatedSegments.forEach(segment => segment.classList.add('inactive'));
                }
            }
            
            // 清除所有片段的选中状态
            document.querySelectorAll('.segment').forEach(s => s.classList.remove('active'));
        }
        
        // 检测片段是否与其他片段重叠
        private checkOverlappingSegments(segment: HTMLElement): boolean {
            const track = segment.closest('.track') as HTMLElement;
            if (!track) return false;
            
            const segmentStart = parseFloat(segment.dataset.start || '0');
            const segmentEnd = parseFloat(segment.dataset.end || '0');
            
            // 获取同一轨道内的所有片段
            const allSegmentsInTrack = track.querySelectorAll('.segment');
            
            // 检查是否有其他片段与当前片段重叠
            for (const otherSegment of allSegmentsInTrack) {
                if (otherSegment === segment) continue; // 跳过自己
                
                const otherStart = parseFloat((otherSegment as HTMLElement).dataset.start || '0');
                const otherEnd = parseFloat((otherSegment as HTMLElement).dataset.end || '0');
                
                // 检测时间范围是否重叠：两个时间段有交集
                if (segmentStart < otherEnd && segmentEnd > otherStart) {
                    return true; // 有重叠
                }
            }
            
            return false; // 无重叠
        }
        
        selectSegment(segment: HTMLElement) {
            const track = segment.closest('.track') as HTMLElement;
            const trackType = track?.id === 'translatedTrack' ? 'translated' : 'original';
            
            this.setActiveTrack(trackType);
            
            // 移除其他片段的选中状态
            document.querySelectorAll('.segment').forEach(s => s.classList.remove('active'));
            
            // 选中当前片段
            segment.classList.add('active');
            
            // 检测是否有重叠，如果有则将被点击的片段置顶
            if (this.checkOverlappingSegments(segment)) {
                // 增加 z-index 计数器，并将被点击的片段置顶
                this.maxZIndex += 1;
                (segment as HTMLElement).style.zIndex = this.maxZIndex.toString();
                
                // 确保其他重叠的片段保持较低的 z-index（可选，让它们保持原有层级）
                // 这里我们只提升被点击的片段，其他片段保持原样
            }
            
            const startTime = parseFloat(segment.dataset.start || '0');
            this.currentTime = startTime;
            this.updatePlayheadPosition();
            
            // 派发事件用于外层自动播放
            try {
                window.dispatchEvent(new CustomEvent('timeline:segmentSelected', { 
                    detail: { 
                        time: startTime,
                        trackType: trackType 
                    } 
                }));
            } catch (e) {
                // 安全兜底
                console.warn('派发时间轴片段选择事件失败:', e);
            }
            
            // 选中片段
        }
        
        initPlayhead() {
            const playhead = document.getElementById('playhead');
            if (!playhead) return;
            
            let isDraggingPlayhead = false;
            
            // 性能优化：缓存DOM元素和计算值
            let cachedTracksContainer: HTMLElement | null = null;
            let containerRect: DOMRect | null = null;
            let lastUpdateTime = 0;
            const UPDATE_THROTTLE = 16; // 约60fps，减少更新频率
            
            // 如果已经绑定过事件，先移除旧的监听器
            if (this.playheadMouseDownHandler) {
                playhead.removeEventListener('mousedown', this.playheadMouseDownHandler);
            }
            if (this.playheadDocumentMouseDownHandler) {
                document.removeEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            }
            if (this.playheadDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            }
            if (this.playheadDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            }
            if (this.playheadDocumentClickHandler) {
                document.removeEventListener('click', this.playheadDocumentClickHandler);
            }
            
            // 播放头拖拽开始
            this.playheadMouseDownHandler = (e: MouseEvent) => {
                isDraggingPlayhead = true;
                e.preventDefault();
                e.stopPropagation();
                // 阻止文字选择
                document.body.style.userSelect = 'none';
                
                // 缓存DOM元素，避免重复查询
                cachedTracksContainer = document.querySelector('.tracks-container') as HTMLElement;
                if (cachedTracksContainer) {
                    containerRect = cachedTracksContainer.getBoundingClientRect();
                }
                // 播放头拖拽开始
            };
            playhead.addEventListener('mousedown', this.playheadMouseDownHandler);
            
            // 时间轴点击和拖拽处理
            this.playheadDocumentMouseDownHandler = (e: MouseEvent) => {
                // 如果点击的是字幕片段，不处理
                if ((e.target as HTMLElement).closest('.segment')) {
                    return;
                }
                
                // 如果点击的是轨道内容区域或时间尺子
                const trackContent = (e.target as HTMLElement).closest('.track-content');
                const timeRuler = (e.target as HTMLElement).closest('.time-ruler');
                const tracksContainer = (e.target as HTMLElement).closest('.tracks-container');
                
                if (trackContent || timeRuler || tracksContainer) {
                    const targetElement = trackContent || timeRuler || tracksContainer;
                    if (!targetElement) return;
                    
                    // 阻止事件冒泡，确保点击事件处理正确
                    e.stopPropagation();
                    
                    const rect = targetElement.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                    
                    // 立即跳转到点击位置
                    this.currentTime = percentage * this.totalTime;
                    // 立即同步视频和音频（不等待mouseup）
                    this.updatePlayheadPosition();
                    
                    // 立即开始拖拽跟随（鼠标按下瞬间就开始跟随）
                    isDraggingPlayhead = true;
                    // 阻止文字选择
                    document.body.style.userSelect = 'none';
                    
                    // 缓存DOM元素
                    cachedTracksContainer = document.querySelector('.tracks-container') as HTMLElement;
                    if (cachedTracksContainer) {
                        containerRect = cachedTracksContainer.getBoundingClientRect();
                    }
                    // 时间轴拖拽开始
                }
            };
            document.addEventListener('mousedown', this.playheadDocumentMouseDownHandler);
            
            // 全局鼠标移动事件（优化性能）
            this.playheadDocumentMouseMoveHandler = (e: MouseEvent) => {
                if (isDraggingPlayhead) {
                    e.preventDefault();
                    
                    // 节流：限制更新频率，提高性能
                    const now = Date.now();
                    if (now - lastUpdateTime < UPDATE_THROTTLE) {
                        return;
                    }
                    lastUpdateTime = now;
                    
                    if (cachedTracksContainer && containerRect) {
                        const clickX = e.clientX - containerRect.left;
                        const percentage = Math.max(0, Math.min(1, clickX / containerRect.width));
                        
                        // 实时更新播放头位置
                        this.currentTime = percentage * this.totalTime;
                        this.updatePlayheadPositionOptimized();
                        
                        // 拖动播放头时，同步滚动轨道内容
                        const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
                        if (timelineContent) {
                            const containerWidth = cachedTracksContainer.clientWidth;
                            const translateXPx = (this.currentTime / this.totalTime) * containerWidth;
                            
                            // 计算播放头在滚动容器中的绝对位置
                            const scrollWidth = timelineContent.scrollWidth;
                            const clientWidth = timelineContent.clientWidth;
                            const currentScrollLeft = timelineContent.scrollLeft;
                            
                            // 计算播放头相对于可视区域的偏移
                            const playheadRelativePos = translateXPx - currentScrollLeft;
                            
                            // 如果播放头超出可视区域右侧，向右滚动（留50px的缓冲区域）
                            if (playheadRelativePos > clientWidth - 50) {
                                const targetScrollLeft = translateXPx - clientWidth + 50;
                                timelineContent.scrollLeft = Math.min(targetScrollLeft, scrollWidth - clientWidth);
                                // 同步更新滑动条位置（滚动时只更新位置，不重新计算宽度）
                                this.updateScrollbarPosition();
                            }
                            // 如果播放头超出可视区域左侧，向左滚动（留50px的缓冲区域）
                            else if (playheadRelativePos < 50) {
                                const targetScrollLeft = translateXPx - 50;
                                timelineContent.scrollLeft = Math.max(0, targetScrollLeft);
                                // 同步更新滑动条位置（滚动时只更新位置，不重新计算宽度）
                                this.updateScrollbarPosition();
                            }
                        }
                    }
                }
            };
            document.addEventListener('mousemove', this.playheadDocumentMouseMoveHandler);
            
            // 全局鼠标释放事件
            this.playheadDocumentMouseUpHandler = () => {
                if (isDraggingPlayhead) {
                    // 时间轴拖拽结束
                    
                    // 拖拽结束时进行完整的同步（确保最终位置准确）
                    if (this.onPlayheadMove) {
                        this.onPlayheadMove(this.currentTime);
                    }
                }
                
                isDraggingPlayhead = false;
                
                // 清理缓存
                cachedTracksContainer = null;
                containerRect = null;
                
                // 恢复文字选择功能
                document.body.style.userSelect = '';
            };
            document.addEventListener('mouseup', this.playheadDocumentMouseUpHandler);
            
            // 添加 click 事件处理，确保快速点击时也能立即跳转
            this.playheadDocumentClickHandler = (e: MouseEvent) => {
                // 如果点击的是字幕片段，不处理（由 segment 的 click 事件处理）
                if ((e.target as HTMLElement).closest('.segment')) {
                    return;
                }
                
                // 如果点击的是轨道内容区域、时间尺子或轨道容器
                const trackContent = (e.target as HTMLElement).closest('.track-content');
                const timeRuler = (e.target as HTMLElement).closest('.time-ruler');
                const tracksContainer = (e.target as HTMLElement).closest('.tracks-container');
                
                if (trackContent || timeRuler || tracksContainer) {
                    const targetElement = trackContent || timeRuler || tracksContainer;
                    if (!targetElement) return;
                    
                    const rect = targetElement.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                    
                    // 立即跳转到点击位置
                    this.currentTime = percentage * this.totalTime;
                    this.updatePlayheadPosition();
                }
            };
            document.addEventListener('click', this.playheadDocumentClickHandler);
        }
        
        updatePlayheadPosition() {
            const playhead = document.getElementById('playhead');
            if (!playhead) return;

            const container = document.querySelector('.tracks-container') as HTMLElement | null;
            const containerWidth = container?.clientWidth || playhead.parentElement?.clientWidth || 0;
            if (containerWidth <= 0 || this.totalTime <= 0) return;

            const translateXPx = (this.currentTime / this.totalTime) * containerWidth;
            const transformValue = `translateX(${translateXPx.toFixed(2)}px)`;

            if (playhead.style.transform !== transformValue) {
                playhead.style.transform = transformValue;
            }

            // 自动滚动：让播放头保持在可视区域内
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (timelineContent) {
                const scrollLeft = timelineContent.scrollLeft;
                const scrollWidth = timelineContent.scrollWidth;
                const clientWidth = timelineContent.clientWidth;
                
                // 计算播放头在滚动容器中的绝对位置（translateXPx 就是相对于 tracks-container 的位置，也就是在 timeline-content 中的绝对位置）
                const playheadAbsolutePos = translateXPx;
                
                // 计算播放头相对于可视区域的偏移
                const playheadRelativePos = playheadAbsolutePos - scrollLeft;
                
                // 如果播放头超出可视区域右侧，向右滚动（留50px的缓冲区域）
                if (playheadRelativePos > clientWidth - 50) {
                    const targetScrollLeft = Math.min(playheadAbsolutePos - clientWidth + 50, scrollWidth - clientWidth);
                    timelineContent.scrollTo({
                        left: targetScrollLeft,
                        behavior: 'smooth'
                    });
                }
                // 如果播放头超出可视区域左侧，向左滚动（留50px的缓冲区域）
                else if (playheadRelativePos < 50) {
                    const targetScrollLeft = Math.max(0, playheadAbsolutePos - 50);
                    timelineContent.scrollTo({
                        left: targetScrollLeft,
                        behavior: 'smooth'
                    });
                }
                
                // 注意：滚动条位置会通过滚动事件监听器自动同步更新，无需手动调用
                // 但为了确保初始状态正确，仍然调用一次（滚动事件会在滚动过程中持续更新）
                this.updateScrollbar();
            } else {
                // 如果没有 timelineContent，也要尝试更新滚动条（可能DOM还没准备好）
                this.updateScrollbar();
            }

            // 高亮当前播放时间对应的片段
            this.highlightCurrentSegments();

            // 同步视频播放进度（通过回调函数）
            if (this.onPlayheadMove) {
                this.onPlayheadMove(this.currentTime);
            }
        }
        
        // 优化的播放头位置更新（拖拽时使用，只更新位置，不进行其他操作）
        updatePlayheadPositionOptimized() {
            const playhead = document.getElementById('playhead');
            if (!playhead) return;

            const container = document.querySelector('.tracks-container') as HTMLElement | null;
            const containerWidth = container?.clientWidth || playhead.parentElement?.clientWidth || 0;
            if (containerWidth <= 0 || this.totalTime <= 0) return;

            // 1. 直接使用视频当前时间计算目标位置
            const videoTime = this.currentTime;
            const targetPosition = (videoTime / this.totalTime) * containerWidth;

            // 2. 直接更新播放头位置（拖动时不需要缓动动画，需要立即响应）
            playhead.style.transform = `translateX(${targetPosition.toFixed(2)}px)`;

            // 拖拽过程中不进行片段高亮和视频同步，只在拖拽结束时进行
        }
        
        // 高亮当前播放时间对应的片段（优化版本）
        highlightCurrentSegments() {
            const segments = document.querySelectorAll('.segment');
            
            // 使用DocumentFragment减少重排
            const fragment = document.createDocumentFragment();
            
            segments.forEach(segment => {
                const element = segment as HTMLElement;
                const start = parseFloat(element.dataset.start || '0');
                const end = parseFloat(element.dataset.end || '0');
                
                const isPlaying = this.currentTime >= start && this.currentTime <= end;
                const hasPlayingClass = element.classList.contains('playing');
                
                // 只在状态改变时更新DOM
                if (isPlaying && !hasPlayingClass) {
                    element.classList.add('playing');
                } else if (!isPlaying && hasPlayingClass) {
                    element.classList.remove('playing');
                }
            });
        }
        
        initScrollbar() {
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track');
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            
            if (!scrollbarThumb || !scrollbarTrack || !timelineContent) return;
            
            // 保存 timelineContent 引用，用于 destroy 时移除监听器
            this.scrollbarTimelineContent = timelineContent;
            
            // 如果已经绑定过事件，先移除旧的监听器
            if (this.scrollbarThumbMouseDownHandler && scrollbarThumb) {
                scrollbarThumb.removeEventListener('mousedown', this.scrollbarThumbMouseDownHandler);
            }
            if (this.scrollbarDocumentMouseMoveHandler) {
                document.removeEventListener('mousemove', this.scrollbarDocumentMouseMoveHandler);
            }
            if (this.scrollbarDocumentMouseUpHandler) {
                document.removeEventListener('mouseup', this.scrollbarDocumentMouseUpHandler);
            }
            if (this.scrollbarTrackMouseDownHandler && scrollbarTrack) {
                (scrollbarTrack as HTMLElement).removeEventListener('mousedown', this.scrollbarTrackMouseDownHandler);
            }
            if (this.scrollbarTimelineScrollHandler && timelineContent) {
                timelineContent.removeEventListener('scroll', this.scrollbarTimelineScrollHandler);
            }
            
            // 根据内容宽度初始化滚动条尺寸
            this.updateScrollbar();
            
            let isDragging = false;
            let thumbOffsetX = 0; // 按下时鼠标相对于滑块的位置
            let trackWidth = 0;
            let thumbWidth = 0;
            let maxScroll = 0;
            let trackLeft = 0; // 轨道相对于视口的左边界
            
            const updateScrollPosition = (clientX: number) => {
                if (!trackWidth || !thumbWidth || !maxScroll) return;

                // 根据鼠标在轨道上的绝对位置直接计算滑块位置（避免增量计算导致的延迟）
                const mouseX = clientX - trackLeft;
                const maxThumbTravel = trackWidth - thumbWidth;
                if (maxThumbTravel <= 0) return;

                // 计算滑块应该移动到的位置（考虑按下时鼠标在滑块上的偏移）
                let targetThumbLeft = mouseX - thumbOffsetX;
                targetThumbLeft = Math.max(0, Math.min(maxThumbTravel, targetThumbLeft));

                // 根据滑块位置直接计算scrollLeft（同步更新，无延迟）
                const percentage = maxThumbTravel === 0 ? 0 : targetThumbLeft / maxThumbTravel;
                const newScrollLeft = percentage * maxScroll;

                // 同步更新滑块位置和内容滚动位置（直接设置，避免任何延迟）
                scrollbarThumb.style.left = `${targetThumbLeft}px`;
                // 直接设置scrollLeft，不使用scrollTo（避免平滑动画导致的延迟）
                timelineContent.scrollLeft = newScrollLeft;
            };
            
            this.scrollbarThumbMouseDownHandler = (e: MouseEvent) => {
                isDragging = true;
                e.preventDefault();
                e.stopPropagation();
                
                // 缓存初始值，避免重复计算
                const trackRect = scrollbarTrack.getBoundingClientRect();
                const thumbRect = scrollbarThumb.getBoundingClientRect();
                
                // 记录按下时鼠标相对于滑块的位置（这样拖动时滑块会跟随鼠标）
                thumbOffsetX = e.clientX - thumbRect.left;
                trackLeft = trackRect.left;
                trackWidth = trackRect.width;
                thumbWidth = scrollbarThumb.offsetWidth;
                maxScroll = Math.max(0, timelineContent.scrollWidth - timelineContent.clientWidth);
                
                // 添加拖拽状态样式
                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'grabbing';
                
                // 禁用平滑滚动，避免拖动时的延迟追赶现象
                timelineContent.style.scrollBehavior = 'auto';
            };
            scrollbarThumb.addEventListener('mousedown', this.scrollbarThumbMouseDownHandler);
            
            // 使用 passive: false 确保可以调用 preventDefault，并立即执行更新
            this.scrollbarDocumentMouseMoveHandler = (e: MouseEvent) => {
                if (isDragging) {
                    e.preventDefault();
                    // 直接同步更新，不使用任何异步机制
                    updateScrollPosition(e.clientX);
                }
            };
            document.addEventListener('mousemove', this.scrollbarDocumentMouseMoveHandler, { passive: false });
            
            this.scrollbarDocumentMouseUpHandler = (e: MouseEvent) => {
                if (isDragging) {
                    isDragging = false;
                    e.preventDefault();

                    // 恢复样式
                    document.body.style.userSelect = '';
                    document.body.style.cursor = '';
                    
                    // 恢复平滑滚动（仅在非拖动时使用）
                    timelineContent.style.scrollBehavior = '';
                }
            };
            document.addEventListener('mouseup', this.scrollbarDocumentMouseUpHandler);
            
            // 添加滚动条轨道点击事件：点击轨道时按比例平滑移动，而不是瞬间跳转
            this.scrollbarTrackMouseDownHandler = (e: MouseEvent) => {
                // 如果点击的是滑块本身，交给拖拽逻辑处理
                if (e.target === scrollbarThumb) return;
                
                const mouseEvent = e as MouseEvent;
                const trackRect = scrollbarTrack.getBoundingClientRect();
                const clickX = mouseEvent.clientX - trackRect.left;
                const trackWidthLocal = trackRect.width;
                const thumbWidthLocal = scrollbarThumb.offsetWidth;
                const maxScrollLocal = Math.max(0, timelineContent.scrollWidth - timelineContent.clientWidth);
                
                if (trackWidthLocal <= 0 || maxScrollLocal <= 0) return;
                
                // 计算点击位置对应的目标滚动比例（保持滑块中心对齐点击位置）
                const maxThumbTravel = trackWidthLocal - thumbWidthLocal;
                const targetThumbLeft = Math.max(
                    0,
                    Math.min(maxThumbTravel, clickX - thumbWidthLocal / 2)
                );
                const percentage = maxThumbTravel === 0 ? 0 : targetThumbLeft / maxThumbTravel;
                const targetScrollLeft = percentage * maxScrollLocal;
                
                scrollbarThumb.style.left = `${targetThumbLeft}px`;
                timelineContent.scrollLeft = targetScrollLeft;
            };
            (scrollbarTrack as HTMLElement).addEventListener('mousedown', this.scrollbarTrackMouseDownHandler);
            
            // 监听时间轴滚动事件，实时同步滑动条位置
            // 这样当时间轴自动滚动（切页）时，滑动条也会跟随
            this.scrollbarTimelineScrollHandler = () => {
                // 使用 requestAnimationFrame 确保在滚动过程中实时更新
                // 滚动时只更新位置，不重新计算宽度（性能优化）
                requestAnimationFrame(() => {
                    this.updateScrollbarPosition();
                });
            };
            
            timelineContent.addEventListener('scroll', this.scrollbarTimelineScrollHandler, { passive: true });
            
            // 保存清理函数，以便在需要时移除监听器
            this.scrollListenerCleanup = () => {
                if (this.scrollbarTimelineScrollHandler && timelineContent) {
                    timelineContent.removeEventListener('scroll', this.scrollbarTimelineScrollHandler);
                }
            };
        }
        
        updateScrollbar() {
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track') as HTMLElement;
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement | null;
            
            if (!scrollbarThumb || !scrollbarTrack || !timelineContent) return;
            
            const trackWidth = scrollbarTrack.offsetWidth;
            const scrollWidth = timelineContent.scrollWidth;
            const clientWidth = timelineContent.clientWidth;
            
            if (trackWidth <= 0 || scrollWidth <= 0 || clientWidth <= 0) return;
            
            // 根据可视区域与总宽度的比例动态计算滑块宽度
            let visiblePercentage = clientWidth / scrollWidth;
            visiblePercentage = Math.max(0, Math.min(1, visiblePercentage));
            // 限制最大占比，避免滑块占满整个轨道（即使内容不够长也保留拖动空间）
            const maxVisiblePercentage = 0.15; // 最多占轨道 15%，让滚动条更短更易操作
            if (visiblePercentage > maxVisiblePercentage) {
                visiblePercentage = maxVisiblePercentage;
            }
            
            // 设置一个最小宽度，避免片段很多时滑块太小难以拖动
            const minThumbPx = 20;
            let thumbWidth = trackWidth * visiblePercentage;
            if (thumbWidth < minThumbPx) {
                thumbWidth = minThumbPx;
            }
            
            scrollbarThumb.style.width = `${thumbWidth}px`;
            
            // 初始时让滑块与当前滚动位置保持一致
            const maxScroll = Math.max(0, scrollWidth - clientWidth);
            const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
            const percentage = maxScroll === 0 ? 0 : timelineContent.scrollLeft / maxScroll;
            scrollbarThumb.style.left = `${percentage * maxThumbTravel}px`;
        }
        
        // 仅更新滚动条位置（不重新计算宽度），用于滚动时的实时更新
        updateScrollbarPosition() {
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track') as HTMLElement;
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement | null;
            
            if (!scrollbarThumb || !scrollbarTrack || !timelineContent) return;
            
            const trackWidth = scrollbarTrack.offsetWidth;
            const scrollWidth = timelineContent.scrollWidth;
            const clientWidth = timelineContent.clientWidth;
            const thumbWidth = scrollbarThumb.offsetWidth;
            
            if (trackWidth <= 0 || scrollWidth <= 0 || clientWidth <= 0 || thumbWidth <= 0) return;
            
            // 只更新位置，不重新计算宽度
            const maxScroll = Math.max(0, scrollWidth - clientWidth);
            const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
            const percentage = maxScroll === 0 ? 0 : timelineContent.scrollLeft / maxScroll;
            scrollbarThumb.style.left = `${percentage * maxThumbTravel}px`;
        }
        
        
        initTrackClick() {
            // 轨道点击事件处理
        }
        
        // 初始化缩放控制（现在通过React状态管理，不需要DOM事件监听）
        initZoomControls() {
            // 缩放控制现在通过React状态管理，不需要直接操作DOM
            // 这个方法保留用于其他可能的初始化逻辑
        }
        
        // 更新缩放
        updateZoom(value: number) {
            this.zoomLevel = value;
            this.applyZoom();
            // 通知React组件更新状态
            if (this.onZoomChange) {
                this.onZoomChange(value);
            }
        }
        
        // 轻量级缩放更新（只更新宽度，不重新生成时间标记）
        updateZoomLight(value: number) {
            this.zoomLevel = value;
            this.applyZoomLight();
        }
        
        // 完整缩放更新（包含时间标记重新生成）
        updateZoomComplete(value: number) {
            this.zoomLevel = value;
            this.applyZoomComplete();
        }
        
        // 放大
        zoomIn() {
            const newValue = Math.min(100, this.zoomLevel + 10);
            this.updateZoom(newValue);
        }
        
        // 缩小
        zoomOut() {
            const newValue = Math.max(0, this.zoomLevel - 10);
            this.updateZoom(newValue);
        }
        
        // 应用缩放（优化版本）
        applyZoom() {
            // 计算缩放比例：0.8x 到 2x 缩放
            const scale = 0.8 + (this.zoomLevel / 100) * 1.2;
            
            // 只缩放时间尺子和轨道容器的宽度，不影响字幕内容
            const timeRuler = document.querySelector('.time-ruler') as HTMLElement;
            const tracksContainer = document.querySelector('.tracks-container') as HTMLElement;
            
            if (timeRuler && tracksContainer) {
                // 根据总时长动态计算基础宽度（每秒钟200px）
                const baseWidth = this.totalTime * 200;
                const newWidth = baseWidth * scale;
                
                // 使用requestAnimationFrame优化DOM更新
                requestAnimationFrame(() => {
                    timeRuler.style.minWidth = `${newWidth}px`;
                    tracksContainer.style.minWidth = `${newWidth}px`;
                    
                    // 只在缩放级别变化较大时才重新生成时间标记
                    const scaleChange = Math.abs(scale - (this.lastScale || 1));
                    if (scaleChange > 0.1) {
                        this.generateTimeMarks();
                        this.lastScale = scale;
                    }
                    
                    // 重新定位字幕
                    this.positionSegments();
                    
                    // 更新滚动条宽度和位置（在DOM更新后）
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                    });
                });
            }
        }
        
        // 轻量级缩放应用（只更新宽度，用于拖动时的实时反馈）
        applyZoomLight() {
            const startTime = performance.now();
            const scale = 0.8 + (this.zoomLevel / 100) * 1.2;
            const timeRuler = document.querySelector('.time-ruler') as HTMLElement;
            const tracksContainer = document.querySelector('.tracks-container') as HTMLElement;
            
            if (timeRuler && tracksContainer) {
                // 根据总时长动态计算基础宽度（每秒钟200px）
                const baseWidth = this.totalTime * 200;
                const newWidth = baseWidth * scale;
                
                // 使用requestAnimationFrame优化DOM更新，避免阻塞主线程
                requestAnimationFrame(() => {
                    timeRuler.style.minWidth = `${newWidth}px`;
                    tracksContainer.style.minWidth = `${newWidth}px`;
                    
                    // 只重新定位字幕，不重新生成时间标记
                    this.positionSegmentsLight();
                    
                    // 更新滚动条宽度和位置（在DOM更新后）
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                    });
                    
                    // 性能监控
                    const endTime = performance.now();
                    if (endTime - startTime > 16) { // 超过一帧的时间
                        console.warn(`缩放操作耗时过长: ${(endTime - startTime).toFixed(2)}ms`);
                    }
                });
            }
        }
        
        // 完整缩放应用（包含时间标记重新生成，用于拖动结束后）
        applyZoomComplete() {
            const scale = 0.8 + (this.zoomLevel / 100) * 1.2;
            const timeRuler = document.querySelector('.time-ruler') as HTMLElement;
            const tracksContainer = document.querySelector('.tracks-container') as HTMLElement;
            
            if (timeRuler && tracksContainer) {
                // 根据总时长动态计算基础宽度（每秒钟200px）
                const baseWidth = this.totalTime * 200;
                const newWidth = baseWidth * scale;
                
                // 使用requestAnimationFrame优化DOM更新
                requestAnimationFrame(() => {
                    timeRuler.style.minWidth = `${newWidth}px`;
                    tracksContainer.style.minWidth = `${newWidth}px`;
                    
                    // 重新生成时间标记
                    this.generateTimeMarks();
                    this.lastScale = scale;
                    
                    // 重新定位字幕
                    this.positionSegments();
                    
                    // 更新滚动条宽度和位置（在DOM更新后）
                    requestAnimationFrame(() => {
                        this.updateScrollbar();
                    });
                });
            }
        }
        
        // 将时间轴滚动到指定时间位置
        scrollToTime(timeInSeconds: number) {
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (!timelineContent) return;
            
            // 计算目标时间在时间轴上的位置百分比
            const timePercentage = timeInSeconds / this.totalTime;
            
            // 计算需要滚动的距离
            const maxScroll = timelineContent.scrollWidth - timelineContent.clientWidth;
            const targetScrollLeft = timePercentage * maxScroll;
            
            // 立即滚动到目标位置（避免平滑滚动带来的延迟导致音画不同步）
            timelineContent.scrollTo({
                left: targetScrollLeft,
                behavior: 'auto'
            });
            
            // 同时更新滚动条位置
            const scrollbarThumb = document.getElementById('scrollbarThumb');
            const scrollbarTrack = document.querySelector('.timeline-right-panel .scrollbar-track') as HTMLElement;
            
            if (scrollbarThumb && scrollbarTrack) {
                const trackWidth = scrollbarTrack.offsetWidth;
                const thumbWidth = scrollbarThumb.offsetWidth;
                const thumbPosition = timePercentage * (trackWidth - thumbWidth);
                
                scrollbarThumb.style.left = `${thumbPosition}px`;
            }
            
            // 时间轴滚动到指定时间
        }

        // 将时间轴滚动到使指定台词片段水平居中的位置（用于上下键/点击台词切换时）
        scrollToSegmentCenter(subtitleId: number) {
            const timelineContent = document.querySelector('.timeline-right-panel .timeline-content') as HTMLElement;
            if (!timelineContent) return;

            const translatedTrack = document.getElementById('translatedTrack');
            const originalTrack = document.getElementById('originalTrack');
            const track =
                this.activeTrack === 'translated' && translatedTrack
                    ? translatedTrack
                    : originalTrack;
            if (!track) return;

            const segment = track.querySelector(`[data-subtitle-id="${subtitleId}"]`) as HTMLElement;
            if (!segment) return;

            const containerRect = timelineContent.getBoundingClientRect();
            const segmentRect = segment.getBoundingClientRect();
            const segmentLeftInContent = segmentRect.left - containerRect.left + timelineContent.scrollLeft;
            const segmentWidth = segmentRect.width;
            const containerWidth = timelineContent.clientWidth;
            const maxScroll = Math.max(0, timelineContent.scrollWidth - containerWidth);

            let targetScrollLeft = segmentLeftInContent - containerWidth / 2 + segmentWidth / 2;
            targetScrollLeft = Math.max(0, Math.min(maxScroll, targetScrollLeft));

            timelineContent.scrollLeft = targetScrollLeft;
            this.updateScrollbarPosition();
        }
    }

    // 初始化Timeline编辑器（仅在视频ID变化时重新创建，内容变化通过updateData同步）
    useEffect(() => {
        if (videoData?.id && !isLoadingVideo) {
            // 如果已存在旧实例，先执行销毁
            if (timelineEditorRef.current) {
                timelineEditorRef.current.destroy();
            }
            
            // 延迟初始化，确保DOM已渲染
            const timer = setTimeout(() => {
                timelineEditorRef.current = new TimelineEditor(handleZoomChange, handlePlayheadMove, videoData, setIsAdjustingSpeed);
                
                // 如果有字幕数据，初始化字幕片段
                if (videoData.subtitles && videoData.subtitles.length > 0) {
                    timelineEditorRef.current.initSubtitles(videoData.subtitles);
                }
                
                // 延迟更新滚动条，确保DOM完全渲染和缩放应用完成
                setTimeout(() => {
                    timelineEditorRef.current?.updateScrollbar();
                }, 200);
            }, 100);

            return () => {
                clearTimeout(timer);
                // 清理防抖定时器
                if (zoomDebounceRef.current) {
                    clearTimeout(zoomDebounceRef.current);
                }
                // 清理节流定时器
                if (zoomThrottleRef.current) {
                    clearTimeout(zoomThrottleRef.current);
                }
                // 【关键】卸载或重新初始化前清理
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.destroy();
                }
            };
        }
    }, [videoData?.id, isLoadingVideo]); // 优化依赖项：仅在ID变化时重新实例，内容变化通过updateData同步
    
    // 当videoData内容变化时（非ID变化），通过updateData方法更新，避免重新创建实例
    // 注意：这个useEffect会在videoData的任何变化时触发，包括字幕更新
    // 但不会重新创建TimelineEditor实例，只是更新数据
    useEffect(() => {
        if (timelineEditorRef.current && videoData && !isLoadingVideo && videoData.id) {
            // 只有当TimelineEditor已经初始化后才更新数据
            timelineEditorRef.current.updateData(videoData);
        }
    }, [videoData, isLoadingVideo]);
    // 解析时间戳字符串为秒数（工具函数）
    const parseTimeToSecondsHelper = useCallback((timeString: string): number => {
        try {
            // 支持格式: "00:00:22,566" 或 "00:00:22.566"
            const timeStr = timeString.replace(',', '.');
            const parts = timeStr.split(':');
            
            if (parts.length === 3) {
                const hours = parseInt(parts[0], 10) || 0;
                const minutes = parseInt(parts[1], 10) || 0;
                const seconds = parseFloat(parts[2]) || 0;
                return hours * 3600 + minutes * 60 + seconds;
            } else if (parts.length === 2) {
                const minutes = parseInt(parts[0], 10) || 0;
                const seconds = parseFloat(parts[1]) || 0;
                return minutes * 60 + seconds;
            }
            
            return 0;
        } catch (error) {
            console.warn('parseTimeToSecondsHelper 解析失败:', timeString, error);
            return 0;
        }
    }, []);

    // 预处理字幕数据：创建targetText的Map以便快速查找，并预处理时间戳
    // 使用useMemo确保与targetText同步更新，避免数据不同步问题
    const processedSubtitlesData = useMemo(() => {
        if (!videoData?.subtitles || !targetText) {
            return {
                processed: [] as Array<{
                    subtitle: Task.SubtitlesLegacy;
                    startTime: number;
                    endTime: number;
                    id: number;
                }>,
                targetMap: new Map<number, { timeRange: string }>()
            };
        }

        // 创建targetText的Map（使用最新的targetText数据）
        const targetMap = new Map<number, { timeRange: string }>();
        targetText.forEach(item => {
            if (item.timeRange) {
                targetMap.set(item.id, { timeRange: item.timeRange });
            }
        });

        // 预处理字幕数据，解析时间戳并排序
        const processed: Array<{
            subtitle: Task.SubtitlesLegacy;
            startTime: number;
            endTime: number;
            id: number;
        }> = [];

        for (const subtitle of videoData.subtitles) {
            if (!subtitle.post_start_time || !subtitle.post_end_time) {
                continue;
            }

            // 优先使用targetText中的最新时间戳（确保与右侧台词编辑区同步）
            const targetData = targetMap.get(subtitle.id);
            let startTime: number;
            let endTime: number;

            if (targetData && targetData.timeRange) {
                // 使用targetText中的最新时间戳
                const timeRangeParts = targetData.timeRange.split(' → ');
                if (timeRangeParts.length === 2) {
                    startTime = parseTimeToSecondsHelper(timeRangeParts[0].trim());
                    endTime = parseTimeToSecondsHelper(timeRangeParts[1].trim());
                } else {
                    // timeRange格式错误，回退到videoData
                    startTime = parseTimeToSecondsHelper(subtitle.post_start_time);
                    endTime = parseTimeToSecondsHelper(subtitle.post_end_time);
                }
            } else {
                // targetText中没有该字幕，使用videoData中的时间戳
                startTime = parseTimeToSecondsHelper(subtitle.post_start_time);
                endTime = parseTimeToSecondsHelper(subtitle.post_end_time);
            }

            processed.push({
                subtitle,
                startTime,
                endTime,
                id: subtitle.id
            });
        }

        // 按开始时间排序，便于二分查找
        processed.sort((a, b) => a.startTime - b.startTime);

        return {
            processed,
            targetMap
        };
    }, [videoData?.subtitles, targetText, parseTimeToSecondsHelper]);

    // 使用ref存储预处理数据，便于在playTransAudioByTime中快速访问
    const processedSubtitlesRef = useRef(processedSubtitlesData.processed);
    const targetTextMapRef = useRef(processedSubtitlesData.targetMap);

    // 同步更新ref中的数据（确保ref始终使用最新的数据）
    useEffect(() => {
        processedSubtitlesRef.current = processedSubtitlesData.processed;
        targetTextMapRef.current = processedSubtitlesData.targetMap;
    }, [processedSubtitlesData]);

    // ======================
    // 翻译音频预取窗口（Prefetch Window）
    // 目标：边播放边预加载，始终维持后续 5 条翻译音频就绪
    // ======================
    const PREFETCH_COUNT = 5;
    const PREFETCH_CACHE_LIMIT = 15; // 防止内存膨胀，保留最近的若干条（含窗口+一定冗余）
    const PREFETCH_MAX_INFLIGHT = 5; // 限制并发：提升到5以确保窗口内5条能尽快进入缓存
    const PREFETCH_THROTTLE_MS = 250; // 节流，避免过于频繁触发

    const prefetchedTransAudiosRef = useRef<Map<string, HTMLAudioElement>>(new Map());
    const prefetchLoadingUrlsRef = useRef<Set<string>>(new Set());
    const prefetchInFlightRef = useRef<number>(0);
    const prefetchLastRunAtRef = useRef<number>(0);
    const currentTransAudioRef = useRef<string | null>(null);

    useEffect(() => {
        currentTransAudioRef.current = currentTransAudio;
    }, [currentTransAudio]);

    const maintainPrefetchWindow = useCallback((currentTimeSec: number) => {
        const now = Date.now();
        if (now - prefetchLastRunAtRef.current < PREFETCH_THROTTLE_MS) return;
        prefetchLastRunAtRef.current = now;

        const processed = processedSubtitlesRef.current || [];
        if (processed.length === 0) return;

        // 找到第一个 startTime >= currentTime 的下标（二分）
        let lo = 0;
        let hi = processed.length;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (processed[mid].startTime < currentTimeSec) {
                lo = mid + 1;
            } else {
                hi = mid;
            }
        }

        const windowItems = processed.slice(lo, lo + PREFETCH_COUNT);
        if (windowItems.length === 0) return;

        const requiredUrls: string[] = [];
        for (const item of windowItems) {
            const transAudio = item.subtitle?.trans_audio;
            if (!transAudio || !transAudio.trim()) continue;
            requiredUrls.push(processTransAudioUrl(transAudio));
        }

        const requiredSet = new Set<string>(requiredUrls);
        // 保护：不要把当前正在播放/当前 audio 元素 src 淘汰掉
        const playingUrl = currentTransAudioRef.current;
        if (playingUrl) requiredSet.add(playingUrl);
        const currentAudioSrc = transAudioRef.current?.src;
        if (currentAudioSrc) requiredSet.add(currentAudioSrc);

        const map = prefetchedTransAudiosRef.current;
        const loading = prefetchLoadingUrlsRef.current;

        // 确保 window.prefetchedTransAudios 始终指向同一个 Map，供现有播放逻辑复用
        if (!window.prefetchedTransAudios || window.prefetchedTransAudios !== map) {
            window.prefetchedTransAudios = map;
        }

        const finalizeLoading = (url: string) => {
            if (loading.has(url)) {
                loading.delete(url);
                prefetchInFlightRef.current = Math.max(0, prefetchInFlightRef.current - 1);
            }
        };

        const ensurePrefetched = (url: string) => {
            if (!url) return;

            // LRU：命中则把它挪到 Map 尾部
            if (map.has(url)) {
                const existing = map.get(url);
                if (existing) {
                    map.delete(url);
                    map.set(url, existing);
                }
                return;
            }
            if (loading.has(url)) return;
            if (prefetchInFlightRef.current >= PREFETCH_MAX_INFLIGHT) return;

            loading.add(url);
            prefetchInFlightRef.current += 1;

            const audio = new Audio();
            audio.preload = 'auto';
            audio.src = url;

            // 先写入缓存，方便立刻复用（即使还没完全 ready）
            map.set(url, audio);

            const onReady = () => finalizeLoading(url);
            const onError = () => finalizeLoading(url);
            // 用 canplay 即可认为“已具备播放所需数据”，比 canplaythrough 更早释放并发占用
            audio.addEventListener('canplay', onReady, { once: true });
            audio.addEventListener('error', onError, { once: true });

            // 超时兜底：避免网络异常导致 inFlight 永久占用
            window.setTimeout(() => {
                finalizeLoading(url);
            }, 10_000);

            // 开始加载（低优先级；不阻塞主流程）
            try {
                audio.load();
            } catch (e) {
                finalizeLoading(url);
            }
        };

        // 预取窗口内的 URL（按顺序，直到并发上限）
        for (const url of requiredUrls) {
            if (prefetchInFlightRef.current >= PREFETCH_MAX_INFLIGHT) break;
            ensurePrefetched(url);
        }

        // 缓存淘汰：优先删除不在 requiredSet 的最旧条目
        if (map.size > PREFETCH_CACHE_LIMIT) {
            for (const key of map.keys()) {
                if (map.size <= PREFETCH_CACHE_LIMIT) break;
                if (requiredSet.has(key)) continue;
                if (loading.has(key)) continue;
                const audio = map.get(key);
                map.delete(key);
                // 尝试释放资源
                if (audio) {
                    try {
                        audio.pause();
                        audio.src = '';
                        audio.load();
                    } catch {
                        // ignore
                    }
                }
            }
        }
        // 兜底：仍然超限则直接按最旧删除
        while (map.size > PREFETCH_CACHE_LIMIT) {
            const firstKey = map.keys().next().value as string | undefined;
            if (!firstKey) break;
            if (loading.has(firstKey) || requiredSet.has(firstKey)) {
                // 如果最老的是必需项，跳过它（避免死循环）
                break;
            }
            const audio = map.get(firstKey);
            map.delete(firstKey);
            if (audio) {
                try {
                    audio.pause();
                    audio.src = '';
                    audio.load();
                } catch {
                    // ignore
                }
            }
        }
    }, []);

    // 播放过程中动态维护预取窗口（timeupdate/seeked/play 触发；内部已节流）
    useEffect(() => {
        const video = sourceVideoRef.current;
        if (!video) return;

        const handleTick = () => {
            maintainPrefetchWindow(video.currentTime || 0);
        };

        video.addEventListener('timeupdate', handleTick);
        video.addEventListener('seeked', handleTick);
        video.addEventListener('play', handleTick);

        // 初始预取：以当前时间为基准
        handleTick();

        return () => {
            video.removeEventListener('timeupdate', handleTick);
            video.removeEventListener('seeked', handleTick);
            video.removeEventListener('play', handleTick);
        };
    }, [videoData?.id, maintainPrefetchWindow]);

    // 使用 ref 存储最新的 playTransAudioByTime 函数引用，避免闭包问题
    const playTransAudioByTimeRef = useRef<((currentTime: number, options?: { forceReplay?: boolean }) => void) | null>(null);
    const safeStopTransAudioRef = useRef<(() => void) | null>(null);
    
    // 根据时间戳播放翻译音频（优化版本：使用预处理数据和二分查找）
    const playTransAudioByTime = useCallback((currentTime: number, options?: { forceReplay?: boolean }) => {
        const forceReplay = options?.forceReplay || false;
        
        // --- 新增：识别 Seek 跳变 ---
        const lastVideoTime = lastAudioCheckVideoTimeRef.current;
        const timeDiff = Math.abs(currentTime - lastVideoTime);
        const isSeekJump = timeDiff > 0.5; // 如果跳转超过 0.5s，视为主动 Seek
        lastAudioCheckVideoTimeRef.current = currentTime;
        
        // --- 调试监控已移除 ---
        
        // 如果是点击台词导致的跳转，不自动播放音频
        if (isSubtitleClickSeekRef.current && !forceReplay) {
            return;
        }
        
        // 如果视频没有在播放，不自动播放翻译音频（除非是强制重播）
        // 用 ref 兜底：刚点播放时 isPlaying 可能尚未更新，以视频实际播放状态为准，避免“暂停→点台词→点播放”播不出当前句
        const videoActuallyPlaying = !!(sourceVideoRef.current && !sourceVideoRef.current.paused);
        if (!isPlaying && !forceReplay && !videoActuallyPlaying) {
            return;
        }
        
        // 如果是强制重播，跳过某些限制检查
        if (forceReplay) {
            // 强制重播模式：跳过部分限制检查
        } else {
            // 如果正在播放台词（左侧或右侧），不自动播放翻译音频
            if (subtitlePlayEndTime !== null) {
                return;
            }

            // 如果刚刚结束台词播放，不立即播放翻译音频
            if (justFinishedSubtitlePlay) {
                return;
            }
        }

        if (!videoData || !videoData.subtitles) {
            return;
        }

        // 频率限制：每100ms最多检查一次（减少计算）
        // 但如果是强制重播，跳过频率限制
        if (!forceReplay && currentTime - lastAudioCheckTimeRef.current < 0.1) {
            return;
        }
        if (!forceReplay) {
            lastAudioCheckTimeRef.current = currentTime;
        }

        // 使用预处理的数据进行二分查找（优化性能）
        // 注意：processedSubtitlesRef.current 通过 useMemo 和 useEffect 自动与 targetText 同步更新
        // 确保二分查找使用的时间戳与右侧台词编辑区的时间戳保持一致
        const processed = processedSubtitlesRef.current;
        if (processed.length === 0) {
            return;
        }

        // 二分查找：找到第一个开始时间 <= currentTime 的字幕
        let left = 0;
        let right = processed.length - 1;
        let currentSubtitle: Task.SubtitlesLegacy | null = null;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const item = processed[mid];

            // 使用 < endTime 而不是 <= endTime，避免在边界处同时匹配两个字幕
            // startTime - 0.05 容错：配合 togglePlay 抢跑（actualTime + 0.1）时稳定命中当前句
            if (currentTime >= item.startTime - 0.05 && currentTime < item.endTime) {
                // 找到匹配的字幕
                currentSubtitle = item.subtitle;
                break;
            } else if (currentTime < item.startTime - 0.05) {
                // 当前时间在字幕开始时间之前，向左查找
                right = mid - 1;
            } else {
                // 当前时间在字幕结束时间之后，向右查找
                left = mid + 1;
            }
        }

        // 如果二分查找没找到，可能是因为字幕之间有重叠或间隙，使用线性查找作为兜底
        if (!currentSubtitle) {
            for (const item of processed) {
                // 使用 < endTime 而不是 <= endTime，避免在边界处同时匹配两个字幕；startTime - 0.05 容错配合抢跑
                if (currentTime >= item.startTime - 0.05 && currentTime < item.endTime) {
                    currentSubtitle = item.subtitle;
                    break;
                }
                // 如果已经超过当前字幕的结束时间，且下一个字幕的开始时间更晚，可以提前退出
                if (currentTime < item.startTime - 0.05) {
                    break;
                }
            }
        }

        // 状态控制：如果已在播放当前字幕音频，直接跳过
        // 不再因为音频暂停/结束自动重新播放，避免在同一句时间范围内循环播放
        // 但如果是强制重播（如用户点击时间轴）或检测到时间跳变（Seek），则允许重新播放
        if (
            currentSubtitle && 
            currentSubtitle.id === currentPlayingSubtitleIdRef.current && 
            !forceReplay && 
            !isSeekJump // 关键：如果是跳变，不拦截
        ) {
            // 拦截：ID 相同（同一句台词），触发拦截退出
            return;
        }
        
        // 如果是强制重播或检测到时间跳变，且ID相同，清除当前播放状态，允许重新播放
        if ((forceReplay || isSeekJump) && currentSubtitle && currentSubtitle.id === currentPlayingSubtitleIdRef.current) {
            // 强制重播或时间跳变：清除当前播放状态，允许重新播放同一句台词
            // 停止当前音频
            if (transAudioRef.current && !transAudioRef.current.paused) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
            }
            // 清除状态
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
        }

        // 无匹配字幕处理逻辑
        // 需求：只在刚进入某个时间戳时决定播放哪句音频，不对 end 时间戳做约束，
        //      即便 currentTime 处于两句台词之间的空白区域，也允许上一句音频自然播完
        //      但如果用户主动跳转（Seek 或 Force），即使没找到新字幕，也要停止旧的音频
        if (!currentSubtitle) {
            // 拦截：未找到匹配的字幕
            
            // --- 新增：清理逻辑 ---
            // 如果是用户主动点击(forceReplay)或大跨度跳转(isSeekJump)，
            // 说明用户明确想改变位置。即使当前位置没台词，也必须打断之前的声音。
            if (forceReplay || isSeekJump) {
                // 主动跳转到无台词区域，停止残留台词音频
                
                // 1. 停止原生 Audio 实例
                if (transAudioRef.current && !transAudioRef.current.paused) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                
                // 2. 清除当前播放 ID 记录
                currentPlayingSubtitleIdRef.current = null;
                
                // 3. 停止 AudioManager (防止某些场景下 audioManager 在响)
                if (safeStopTransAudioRef.current) {
                    safeStopTransAudioRef.current();
                }
            }
            return;
        }

        if (!currentSubtitle.trans_audio) {
            // 如果没有翻译音频，显示错误提示
            console.error(`字幕ID ${currentSubtitle.id} 缺少目标台词音频数据`);
            if (safeStopTransAudioRef.current) {
                safeStopTransAudioRef.current();
            }
            currentPlayingSubtitleIdRef.current = null;
            return;
        }

        // 构建翻译音频URL（支持绝对路径和相对路径）
        // trans_audio 使用内容哈希命名，可以使用强缓存
        const transAudioUrl = processTransAudioUrl(currentSubtitle.trans_audio);
        
        // 如果音频URL没有变化，不需要重新播放（但可能ID不同，需要更新状态）
        // 但如果是强制重播或检测到时间跳变，允许重新播放
        if (!forceReplay && !isSeekJump && currentTransAudio === transAudioUrl && currentPlayingSubtitleIdRef.current === currentSubtitle.id) {
            // 拦截：音频 URL 相同且 ID 相同，触发拦截退出
            return;
        }
        
        // 如果是强制重播或检测到时间跳变，且URL和ID都相同，清除状态以允许重新播放
        if ((forceReplay || isSeekJump) && currentTransAudio === transAudioUrl && currentPlayingSubtitleIdRef.current === currentSubtitle.id) {
            // 强制重播或时间跳变：URL和ID相同，清除状态以允许重新播放
            // 停止当前音频
            if (transAudioRef.current && !transAudioRef.current.paused) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
            }
            // 清除状态
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
        }
        
        // 通过所有拦截检查，准备播放翻译音频

        // 在播放翻译音频之前，先停止原人声音频
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }

        // 播放新音频：先停掉之前的
        if (currentPlayingSubtitleIdRef.current !== null) {
            if (safeStopTransAudioRef.current) {
                safeStopTransAudioRef.current();
            }
        }
        currentPlayingSubtitleIdRef.current = currentSubtitle.id;

        // 获取targetText中的最新数据（用于计算时间戳）
        // 注意：直接使用targetText而不是缓存的targetTextMapRef，确保使用右侧台词编辑区的最新时间戳
        const currentSubtitleId = currentSubtitle?.id;
        const targetData = currentSubtitleId ? targetText.find(item => item.id === currentSubtitleId) : undefined;

        // 创建或更新音频元素，优先使用预取的音频
        if (!transAudioRef.current) {
            // 检查是否有预取的音频可以使用（优先使用组件内预取 Map；兼容 window.prefetchedTransAudios）
            const prefetchedMap = window.prefetchedTransAudios || prefetchedTransAudiosRef.current;
            if (prefetchedMap && prefetchedMap.has(transAudioUrl)) {
                // 使用预取的翻译音频
                const prefetchedAudio = prefetchedMap.get(transAudioUrl);
                if (prefetchedAudio) {
                    transAudioRef.current = prefetchedAudio;
                } else {
                    transAudioRef.current = new Audio();
                    transAudioRef.current.preload = 'auto';
                }
            } else {
                transAudioRef.current = new Audio();
                // 设置预加载属性以减少延迟
                transAudioRef.current.preload = 'auto';
            }
        }

        const transAudio = transAudioRef.current;
        if (!transAudio) {
            console.error('无法创建音频元素');
            return;
        }
        
        // 设置音频源
        if (transAudio.src !== transAudioUrl) {
            transAudio.src = transAudioUrl;
        }

        // 获取语速设置
        const audioSpeed = currentSubtitle.audio_speed || 1.0;

        // 设置音频属性
        transAudio.volume = 1.0;
        // 使用台词数据中的audio_speed值
        transAudio.playbackRate = audioSpeed;
        
        // 计算时间偏移：当前时间 - 台词开始时间 = 偏移量
        // 优先使用targetText中的最新时间戳（如果存在）
        let startTime: number;
        let endTime: number;
        
        if (targetData && targetData.timeRange) {
            // 使用targetText中的最新时间戳
            const timeRangeParts = targetData.timeRange.split(' → ');
            if (timeRangeParts.length === 2) {
                startTime = parseTimeToSecondsHelper(timeRangeParts[0].trim());
                endTime = parseTimeToSecondsHelper(timeRangeParts[1].trim());
            } else {
                // 回退到videoData中的数据
                startTime = parseTimeToSecondsHelper(currentSubtitle.post_start_time);
                endTime = parseTimeToSecondsHelper(currentSubtitle.post_end_time);
            }
        } else {
            // 使用videoData中的数据
            startTime = parseTimeToSecondsHelper(currentSubtitle.post_start_time);
            endTime = parseTimeToSecondsHelper(currentSubtitle.post_end_time);
        }
        
        // 原始偏移：视频当前时间相对句首的偏移
        const rawOffset = currentTime - startTime;
        // 带阈值：仅当 forceReplay 且靠近句首（<0.3s）时强制从 0 播防吞音；否则按偏移播，保证点台词中间时音画同步
        const timeOffset =
            forceReplay && rawOffset < 0.3 ? 0 : rawOffset;

        // 确保时间偏移在合理范围内
        const maxOffset = endTime - startTime;
        const safeTimeOffset = Math.max(0, Math.min(timeOffset, maxOffset));

        // 优化的音频播放逻辑，减少延迟
        // 保存最新的时间戳用于闭包中
        const latestStartTime = startTime;
        const latestEndTime = endTime;
        
        const playAudioWhenReady = () => {
            let hasPlayed = false; // 防止重复播放的标志
            
            const playAudio = () => {
                if (hasPlayed) return; // 如果已经播放过，直接返回
                hasPlayed = true;
                
                // 处理播放成功的回调
                const handlePlaySuccess = () => {
                    // 翻译音频从偏移位置开始播放
                    setCurrentTransAudio(transAudioUrl);
                    
                    // 添加音频播放状态监控，基于实际音频长度
                    // 优化：使用ended事件监听，确保音频完整播放
                    const checkAudioProgress = () => {
                        if (!transAudio || transAudio.paused) {
                            return;
                        }
                        
                        const currentAudioTime = transAudio.currentTime;
                        const audioDuration = transAudio.duration;
                        
                        // 检查音频是否真正播放完成（基于实际音频长度）
                        if (audioDuration > 0 && currentAudioTime >= audioDuration - 0.1) {
                            // 翻译音频播放完成
                            setCurrentTransAudio(null);
                            // 这里不再重置 currentPlayingSubtitleIdRef.current，
                            // 避免在同一句时间范围内自然播放结束后被再次自动触发播放。
                            return;
                        }
                        
                        // 继续监控（减少监控频率到200ms，降低性能开销）
                        setTimeout(checkAudioProgress, 200);
                    };
                    
                    // 使用ended事件确保音频完整播放（更可靠）
                    const handleAudioEnded = () => {
                        // 翻译音频播放完成（ended事件）
                        setCurrentTransAudio(null);
                        // 不再在这里重置 currentPlayingSubtitleIdRef，
                        // 避免在同一句时间范围内因 timeupdate 触发重复播放。
                        transAudio.removeEventListener('ended', handleAudioEnded);
                    };
                    transAudio.addEventListener('ended', handleAudioEnded);
                    
                    // 开始监控音频播放进度
                    setTimeout(checkAudioProgress, 200);
                };
                
                // 处理播放失败的回调
                const handlePlayError = (error: any) => {
                    // 如果是 AbortError，说明播放被 pause 中断，尝试重试一次
                    if (error.name === 'AbortError') {
                        // 翻译音频播放被中断（AbortError），等待后重试
                        // 等待一小段时间后重试
                        setTimeout(() => {
                            // 确保音频已停止
                            if (!transAudio.paused) {
                                transAudio.pause();
                            }
                            // 再次尝试播放
                            setTimeout(() => {
                                transAudio.play().then(() => {
                                    handlePlaySuccess();
                                }).catch((retryError: any) => {
                                    console.error('翻译音频播放重试失败:', retryError);
                                    // 播放失败时清除状态
                                    currentPlayingSubtitleIdRef.current = null;
                                    setCurrentTransAudio(null);
                                });
                            }, 20);
                        }, 30);
                    } else {
                        console.error('翻译音频播放失败:', error);
                        // 播放失败时清除状态
                        currentPlayingSubtitleIdRef.current = null;
                        setCurrentTransAudio(null);
                    }
                };
                
                // --- 播放指令保护：确保在 pause 之后才 play，避免 AbortError ---
                // 确保播放速率为台词数据中的audio_speed值
                transAudio.playbackRate = audioSpeed;
                
                // 从偏移位置开始播放，确保台词与画面同步
                transAudio.currentTime = safeTimeOffset;
                
                // 确保音频已完全停止，避免与之前的 pause 指令冲突
                if (!transAudio.paused) {
                    transAudio.pause();
                    // 等待 pause 完成后再播放，让浏览器清空任务队列
                    setTimeout(() => {
                        transAudio.play().then(() => {
                            handlePlaySuccess();
                        }).catch(handlePlayError);
                    }, 20); // 20ms 延迟，让浏览器清空任务队列
                } else {
                    // 音频已停止，可以直接播放，但仍需小延迟确保之前的 pause 指令已完成
                    setTimeout(() => {
                        transAudio.play().then(() => {
                            handlePlaySuccess();
                        }).catch(handlePlayError);
                    }, 10); // 10ms 延迟，确保之前的 pause 指令已完成
                }
            };
            
            // 优化：检查是否为预取音频，如果是则直接播放
            const prefetchedMap = window.prefetchedTransAudios || prefetchedTransAudiosRef.current;
            const isPrefetched = !!prefetchedMap && prefetchedMap.has(transAudioUrl);
            
            if (isPrefetched && transAudio.readyState >= 3) { // HAVE_FUTURE_DATA - 预取音频通常已完全加载
                // 预取音频已就绪，直接播放
                playAudio();
            } else if (transAudio.readyState >= 1) { // HAVE_METADATA - 有元数据就可以尝试播放
                playAudio();
            } else {
                // 等待音频加载，但减少超时时间
                const handleCanPlay = () => {
                    transAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                };
                transAudio.addEventListener('canplay', handleCanPlay);
                
                // 超时保护，如果1秒内没有加载完成，强制播放
                setTimeout(() => {
                    transAudio.removeEventListener('canplay', handleCanPlay);
                    playAudio();
                }, 1000); // 从3秒减少到1秒
            }
        };

        playAudioWhenReady();
    }, [isPlaying, subtitlePlayEndTime, justFinishedSubtitlePlay, videoData, currentTransAudio, targetText, parseTimeToSecondsHelper]);

    // 更新 ref 以存储最新的函数引用（每次渲染都更新，打破闭包陷阱）
    // 关键：不使用依赖数组，确保每次组件渲染时都更新 ref，这样即使事件监听器是旧的，它指向的 ref 内容也是最新的
    useEffect(() => {
        playTransAudioByTimeRef.current = playTransAudioByTime;
    });

    // 停止翻译音频
    const stopTransAudio = () => {
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
            setCurrentTransAudio(null);
            // 翻译音频已停止
        }
    };

    // 播放目标台词音频（trans_audio）
    const playTargetSubtitle = async (timeRange: string, subtitleId?: number) => {
        // 检查当前轨道，如果需要切换则先切换，等待切换完成后再播放
        const currentTrack = timelineEditorRef.current?.activeTrack;
        const targetTrack = 'translated';
        
        if (currentTrack !== targetTrack) {
            // 目标台词播放：检测到需要切换轨道
            handleSwitchTrack(targetTrack);
            // 等待轨道切换完成（使用 requestAnimationFrame 确保 DOM 更新完成）
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // 再次确认轨道已切换
                    if (timelineEditorRef.current?.activeTrack === targetTrack) {
                        // 目标台词播放：轨道已切换，开始播放
                        executePlayTargetSubtitle(timeRange, subtitleId);
                    } else {
                        // 目标台词播放：轨道切换失败，但仍继续播放
                        executePlayTargetSubtitle(timeRange, subtitleId);
                    }
                });
            });
        } else {
            // 目标台词播放：当前已在目标轨道，直接播放
            executePlayTargetSubtitle(timeRange, subtitleId);
        }
    };
    
    // 执行目标台词播放逻辑
    const executePlayTargetSubtitle = async (timeRange: string, subtitleId?: number) => {
        // 全局唯一播放实例管理：先停止当前播放实例
        stopCurrentPlayingInstance();
        
        // 若当前整体处于播放状态，先统一暂停，避免状态冲突
        if (isPlaying) {
            if (sourceVideoRef.current) sourceVideoRef.current.pause();
            if (subtitleDemoRef.current) try { subtitleDemoRef.current.pause(); } catch {}
            if (backgroundAudioRef.current) backgroundAudioRef.current.pause();
            stopTransAudio();
            stopOriginAudio();
            // 停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
            setIsPlaying(false);
        }
        // 在播放前同步待更新的时间轴数据
        if (timelineEditorRef.current && pendingTimelineUpdates.size > 0) {
            timelineEditorRef.current.syncPendingUpdatesToVideoData(pendingTimelineUpdates);
        }
        // 将时间轴刻度线与视图定位到该台词开始时间
        try {
            const [startTimeStr] = timeRange.split(' → ');
            const startTime = parseTimeToSeconds(startTimeStr);
            if (timelineEditorRef.current) {
                timelineEditorRef.current.currentTime = startTime;
                timelineEditorRef.current.updatePlayheadPosition();
                if (typeof (timelineEditorRef.current as any).scrollToTime === 'function') {
                    (timelineEditorRef.current as any).scrollToTime(startTime);
                }
            }
            // 严格等待左右视频都seek到位
            if (sourceVideoRef.current) {
                sourceVideoRef.current.currentTime = startTime;
                sourceVideoRef.current.pause();
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.seekToTime(startTime); } catch {}
                try { subtitleDemoRef.current.pause(); } catch {}
            }
            await waitBothSeeked(startTime);
        } catch {}
        
        // 目标台词播放：开始播放目标台词
        
        // 如果正在播放台词，先停止当前播放，然后开始新的播放
        if (subtitlePlayEndTime !== null) {
            // 目标台词播放：检测到正在播放其他台词，先停止当前播放
            
            // 停止所有正在播放的媒体
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            if (subtitleDemoRef.current) {
                try { subtitleDemoRef.current.pause(); } catch {}
            }
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            // 停止所有音频
            stopTransAudio();
            stopOriginAudio();
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
            
            // 停止通过 AudioManager 播放的音频
            try {
                if (currentPlayingSubtitleIdRef.current !== null) {
                    const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                    audioManager.stopSound(audioId);
                    audioManager.destroySound(audioId);
                }
            } catch (error) {
                // 停止 AudioManager 音频时出错
            }
            
            // 清除定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
            
            // 清除播放状态
            setSubtitlePlayEndTime(null);
            setIsPlaying(false);
            setCurrentTransAudio(null);
            currentPlayingSubtitleIdRef.current = null;
            
            // 目标台词播放：已停止当前播放，准备播放新台词
        }
        
        if (!videoData || !videoData.subtitles) {
            // 没有字幕数据，无法播放目标台词音频
            return;
        }

        // 重置刚结束台词播放的标志
        setJustFinishedSubtitlePlay(false);

        // 停止原始台词音频播放
        stopOriginAudio();

        // 查找对应的字幕数据 - 优先使用字幕ID获取最新数据
        let matchingSubtitle: Subtitle | undefined;
        
        if (subtitleId) {
            // 使用字幕ID直接查找，确保获取最新数据
            matchingSubtitle = videoData.subtitles.find(subtitle => subtitle.id === subtitleId);
            // 目标台词播放：使用字幕ID查找
        } else {
            // 如果没有提供字幕ID，使用时间范围查找（兼容旧逻辑）
            matchingSubtitle = videoData.subtitles.find(subtitle => {
                // 尝试匹配原始时间范围
                const originTimeRange = `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`;
                if (originTimeRange === timeRange) {
                    return true;
                }
                
                // 尝试匹配目标时间范围（post时间）
                const targetTimeRange = `${subtitle.post_start_time || subtitle.origin_start_time} → ${subtitle.post_end_time || subtitle.origin_end_time}`;
                if (targetTimeRange === timeRange) {
                    return true;
                }
                
                return false;
            });
            // 目标台词播放：使用时间范围查找
        }

        if (!matchingSubtitle || !matchingSubtitle.trans_audio) {
            console.error('未找到对应的目标台词音频:', {
                requestedTimeRange: timeRange,
                foundMatchingSubtitle: !!matchingSubtitle,
                hasTransAudio: matchingSubtitle ? !!matchingSubtitle.trans_audio : false,
                transAudioUrl: matchingSubtitle ? matchingSubtitle.trans_audio : null
            });
            // 可用的字幕数据已记录
            return;
        }

        // 使用最新的时间戳计算时间范围
        const latestStartTime = matchingSubtitle.post_start_time || matchingSubtitle.origin_start_time;
        const latestEndTime = matchingSubtitle.post_end_time || matchingSubtitle.origin_end_time;
        const startTime = parseTimeToSeconds(latestStartTime);
        const endTime = parseTimeToSeconds(latestEndTime);
        
        // 目标台词播放：使用最新时间戳
        
        // 清除之前的定时器
        if (subtitlePlayTimeoutRef.current) {
            clearTimeout(subtitlePlayTimeoutRef.current);
        }
        
        // 使用精确同步机制前，严格等待左右视频seek完成
        (async () => {
            try {
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.currentTime = startTime;
                    sourceVideoRef.current.pause();
                }
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.seekToTime(startTime); } catch {}
                    try { subtitleDemoRef.current.pause(); } catch {}
                }
                await waitBothSeeked(startTime);
            } catch {}
        })();
        // 使用精确同步机制
        const waitForVideosReadyAndPlay = async () => {
            // 目标台词播放：开始精确同步
            
            // 更新进度条显示
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar && duration > 0) {
                const progressPercent = (startTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            setCurrentTime(startTime);
            
            // 使用精确同步启动
            await startPreciseSync(startTime);
            
            // 目标台词播放：精确同步完成，开始播放
            
            // 现在两个视频都精确同步了，真正同时开始播放
            // 目标台词播放：准备同时启动两个视频
            
            // 使用requestAnimationFrame确保在同一帧内启动
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    const playPromises: Promise<any>[] = [];
                    
                    if (sourceVideoRef.current) {
                        sourceVideoRef.current.muted = true;
                        playPromises.push(
                            sourceVideoRef.current.play().catch(error => {
                                console.error('目标台词播放：左侧视频播放失败:', error);
                            })
                        );
                    }
                    
                    if (subtitleDemoRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleDemoRef.current!.play();
                                // 给右侧视频一点时间启动
                                setTimeout(() => resolve(), 10);
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        // 目标台词播放：两个视频已真正同时开始播放，毫秒级同步已启动
                        resolve();
                    });
                });
            });
            
            // 播放目标台词音频（trans_audio）- 使用 Howler.js 实现真正的音量放大
            if (!matchingSubtitle) {
                console.error('matchingSubtitle is undefined');
                return;
            }
            
            // 构建目标台词音频URL（支持绝对路径和相对路径）
            // trans_audio 使用内容哈希命名，可以使用强缓存
            const targetAudioUrl = processTransAudioUrl(matchingSubtitle.trans_audio);
            // 播放目标台词音频

            // 获取音量增益设置（优先使用音量映射表中的最新值）
            const latestVolume = volumeMap.get(matchingSubtitle.id);
            const volumeGainDb = latestVolume !== undefined ? latestVolume : (matchingSubtitle.volume_gain_db || 1.0);
            const audioSpeed = matchingSubtitle.audio_speed || 1.0;
            
            // 目标台词播放：使用 Howler.js 播放音频

            // 使用 AudioManager 播放音频
            const audioId = `target_audio_${matchingSubtitle.id}`;
            
            try {
                // 创建音频实例（参考测试案例）
                // 使用台词数据中的audio_speed值
                audioManager.createSound(audioId, targetAudioUrl, {
                    volume: 1.0, // 基础音量，实际音量通过 gain 控制
                    rate: audioSpeed, // 使用台词数据中的audio_speed值
                    preload: true,
                    html5: true // 强制使用 HTML5 模式
                });

                // ================== 【必须加】全局变量赋值：立即赋值，不要等待 await！ ==================
                // 🔥 创建后，立马赋值给全局变量！不要等待 await！
                // 这样即使在 await 期间离开页面，清理函数也能找到这个音频 ID
                GLOBAL_PLAYING_AUDIO_ID = audioId;
                console.log('全局变量已赋值:', GLOBAL_PLAYING_AUDIO_ID);
                // ================== 【必须加】结束 ==================

                // 播放音频并设置音量增益
                await audioManager.playSound(audioId, volumeGainDb, 0);
                
                // 目标台词音频播放成功（Howler.js）
                
                // 设置当前播放的音频引用，用于后续控制
                setCurrentTransAudio(targetAudioUrl);
                currentPlayingSubtitleIdRef.current = matchingSubtitle.id;
                
                // 全局唯一播放实例管理：记录新的播放实例
                currentPlayingInstanceRef.current = {
                    type: 'target',
                    audioId: audioId,
                    subtitleId: matchingSubtitle.id,
                    timeRange: timeRange
                };
                // 全局播放管理：记录新的翻译后台词播放实例
                
            } catch (error) {
                console.error('目标台词音频播放失败（Howler.js）:', error);
                // 如果 Howler.js 播放失败，回退到原生 Audio
                // 回退到原生 Audio 播放
                
                // 创建或更新音频元素
                if (!transAudioRef.current) {
                    transAudioRef.current = new Audio();
                }

                const targetAudio = transAudioRef.current;
                
                // 设置音频源
                if (targetAudio.src !== targetAudioUrl) {
                    targetAudio.src = targetAudioUrl;
                }

                // 设置音频属性
                targetAudio.currentTime = 0;
                // 使用台词数据中的audio_speed值
                targetAudio.playbackRate = audioSpeed;
                // 注意：原生 Audio 的 volume 不能超过 1.0
                targetAudio.volume = Math.min(1.0, volumeGainDb);
                
                // 全局唯一播放实例管理：记录新的播放实例（回退模式）
                currentPlayingInstanceRef.current = {
                    type: 'target',
                    subtitleId: matchingSubtitle.id,
                    timeRange: timeRange
                };
                // 全局播放管理：记录新的翻译后台词播放实例（回退模式）
                // 目标台词播放：回退到原生 Audio

            // 等待音频加载完成后再播放
                // 等待音频加载完成后再播放（回退模式）
                const playTargetAudioWhenReady = () => {
                    let hasPlayed = false; // 防止重复播放的标志
                    
                    const playAudio = () => {
                        if (hasPlayed) return; // 如果已经播放过，直接返回
                        hasPlayed = true;
                        
                        // 在播放前重新设置语速和音量，确保使用最新值
                        if (!matchingSubtitle) return;
                        
                        // 使用台词数据中的audio_speed值
                        const currentAudioSpeed = matchingSubtitle.audio_speed || 1.0;
                        targetAudio.playbackRate = currentAudioSpeed;
                        
                        const currentLatestVolume = volumeMap.get(matchingSubtitle.id);
                        const currentVolumeGainDb = currentLatestVolume !== undefined ? currentLatestVolume : (matchingSubtitle.volume_gain_db || 1.0);
                        // 注意：原生 Audio 的 volume 不能超过 1.0
                        const currentFinalVolume = Math.min(1.0, Math.max(0, currentVolumeGainDb));
                        targetAudio.volume = currentFinalVolume;
                        
                        // 目标台词播放：播放前重新设置语速和音量（回退模式）
                        targetAudio.play().then(() => {
                            // 目标台词音频播放成功（回退模式）
                            setCurrentTransAudio(targetAudioUrl);
                            if (matchingSubtitle) {
                                currentPlayingSubtitleIdRef.current = matchingSubtitle.id;
                            }
                        }).catch(error => {
                            console.error('目标台词音频播放失败（回退模式）:', error);
                        });
                    };
                    
                    if (targetAudio.readyState >= 2) { // HAVE_CURRENT_DATA
                        playAudio();
                    } else {
                        // 等待音频加载
                        const handleCanPlay = () => {
                            targetAudio.removeEventListener('canplay', handleCanPlay);
                            playAudio();
                        };
                        targetAudio.addEventListener('canplay', handleCanPlay);
                        
                        // 超时保护，如果5秒内没有加载完成，强制播放
                        setTimeout(() => {
                            targetAudio.removeEventListener('canplay', handleCanPlay);
                            if (!hasPlayed) {
                                // 音频加载超时，强制播放（回退模式）
                                playAudio();
                            }
                        }, 5000);
                    }
                };
                
                playTargetAudioWhenReady();
            }
            
            // 播放目标台词时，暂停背景音，只播放台词
            // 播放目标台词：暂停背景音，只播放台词音频
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            // 目标台词播放：所有媒体已同时开始播放
        };
        
        // 开始等待并播放流程
        waitForVideosReadyAndPlay();
        
        // 标记为用户手动操作
        userInteractionRef.current = true;
        
        // 立即同步音频到开始时间
        syncAudioToVideo();
        
        // 设置播放状态
        setIsPlaying(true);
        
        // 设置结束时间，用于在时间更新时检查是否需要暂停
        setSubtitlePlayEndTime(endTime);
        
        // 目标台词播放：已设置结束时间
    };

    // 只播放背景音的函数
    const playBackgroundAudioOnly = () => {
        if (!videoData) {
            // videoData为空，无法播放背景音
            return;
        }

        // 判断背景音开关状态，如果关闭则不播放背景音
        if (!backgroundAudioEnabled) {
            // 背景音开关已关闭，跳过播放背景音
            return;
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;
        
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // console.log('播放背景音，视频时间:', videoTime);

        // 只播放背景音
        if (videoData.backgroud_audio_hls_url) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 在创建HLS实例前，再次检查是否应该继续（可能在异步过程中已被暂停）
                            if (!audioPlayRequestedRef.current) {
                                // 背景音HLS：播放请求已取消，跳过创建HLS实例
                                return;
                            }
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 在清单解析完成时，再次检查是否应该播放（可能在解析期间已被暂停）
                                if (!audioPlayRequestedRef.current) {
                                    // 背景音HLS清单解析完成，但播放请求已取消，跳过播放
                                    return;
                                }
                                // 再次检查HLS实例是否还存在（可能在解析期间已被销毁）
                                if (!backgroundHlsRef.current || backgroundHlsRef.current !== hls) {
                                    // 背景音HLS清单解析完成，但HLS实例已被销毁，跳过播放
                                    return;
                                }
                                // 立即同步到视频时间
                                backgroundAudioRef.current!.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                backgroundAudioRef.current!.volume = 1.0;
                                backgroundAudioRef.current!.play().then(() => {
                                    // 再次检查音频就绪状态
                                    checkAudioReady();
                                }).catch(error => {
                                    // 静默处理播放失败，不输出错误日志
                                });
                            });
                            hls.on(Hls.default.Events.ERROR, (event, data) => {
                                // 静默处理HLS错误，不输出错误日志
                            });
                        } else {
                            console.error('浏览器不支持HLS.js');
                        }
                    }).catch(error => {
                        console.error('加载HLS.js失败:', error);
                    });
                } else {
                    // 非HLS格式，直接播放
                    if (backgroundAudioRef.current) {
                        backgroundAudioRef.current.src = backgroundAudioUrl;
                        backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                            // 立即同步到视频时间
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current.volume = 1.0;
                                    backgroundAudioRef.current.play().then(() => {
                                    }).catch(error => {
                                        // 静默处理播放失败
                                    });
                                }
                            }
                        });
                        backgroundAudioRef.current.load();
                    }
                }
            }
        }
    };

    // 优化的音频播放函数 - 立即同步时间并播放
    const playAudioTracksWithSync = () => {
        if (!videoData) {
            // videoData为空，无法播放音频
            return;
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;
        
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // 立即同步音频播放

        // 播放背景音（根据开关状态决定是否播放）
        if (videoData.backgroud_audio_hls_url && backgroundAudioEnabled) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 准备播放背景音
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 检测到HLS格式的背景音，尝试使用HLS.js播放
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 背景音HLS清单解析完成
                                // 立即同步到视频时间
                                backgroundAudioRef.current!.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                // 检查是否还在播放状态，避免在解析期间被暂停
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current!.volume = 1.0;
                                    backgroundAudioRef.current!.play().then(() => {
                                        // 背景音HLS播放成功，已同步到时间
                                        // 再次检查音频就绪状态
                                        checkAudioReady();
                                    }).catch(error => {
                                        console.error('背景音HLS播放失败:', error);
                                    });
                                }
                            });
                        } else {
                            // 浏览器不支持HLS，尝试直接播放
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.src = backgroundAudioUrl;
                                backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                                    // 背景音加载完成
                                    // 立即同步到视频时间
                                    if (backgroundAudioRef.current) {
                                        backgroundAudioRef.current.currentTime = videoTime;
                                        // 更新音频就绪状态
                                        checkAudioReady();
                                        
                                        if (audioPlayRequestedRef.current) {
                                            backgroundAudioRef.current.volume = 1.0;
                                            backgroundAudioRef.current.play().then(() => {
                                                // 背景音播放成功，已同步到时间
                                            }).catch(error => {
                                                console.error('背景音播放失败:', error);
                                            });
                                        }
                                    }
                                });
                                backgroundAudioRef.current.load();
                            }
                        }
                    });
                } else {
                    // 非HLS格式，直接播放
                    if (backgroundAudioRef.current) {
                        backgroundAudioRef.current.src = backgroundAudioUrl;
                        backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                            // 背景音加载完成
                            // 立即同步到视频时间
                            if (backgroundAudioRef.current) {
                                backgroundAudioRef.current.currentTime = videoTime;
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                if (audioPlayRequestedRef.current) {
                                    backgroundAudioRef.current.volume = 1.0;
                                    backgroundAudioRef.current.play().then(() => {
                                        // 背景音播放成功，已同步到时间
                                    }).catch(error => {
                                        console.error('背景音播放失败:', error);
                                    });
                                }
                            }
                        });
                        backgroundAudioRef.current.load();
                    }
                }
            }
        }

        // 注意：翻译语音现在通过时间戳动态播放，不再使用HLS流
        
        // 根据选中的轨道初始化原人声音频（如果选中"原视频"轨道）
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
        if (currentActiveTrack === 'original' && videoData.vocals_hls_url) {
            initVocalsAudio();
        }
    };
    
    // 初始化原人声音频（vocals_hls_url）
    const initVocalsAudio = () => {
        if (!videoData || !videoData.vocals_hls_url) {
            // videoData为空或没有原人声音频，无法播放
            return;
        }

        // 在播放原人声音频之前，先停止翻译音频
        stopTransAudio();
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
            setCurrentTransAudio(null);
        }

        const videoTime = sourceVideoRef.current?.currentTime || 0;
        // 构建原人声音频URL（接口已添加前缀，这里再做一次保险处理，兼容相对路径和绝对路径）
        const vocalsAudioUrl = processVideoUrl(videoData.vocals_hls_url as any);
        // 初始化原人声音频

        // 创建或获取原人声音频元素
        if (!vocalsAudioRef.current) {
            vocalsAudioRef.current = new Audio();
        }

        // 检查是否是HLS格式
        if (vocalsAudioUrl.includes('.m3u8')) {
            // 检测到HLS格式的原人声音频，尝试使用HLS.js播放
            import('hls.js').then((Hls) => {
                if (Hls.default.isSupported()) {
                    // 清理之前的HLS实例
                    if (vocalsHlsRef.current) {
                        vocalsHlsRef.current.destroy();
                    }
                    const hls = new Hls.default({
                        enableWorker: true,
                        lowLatencyMode: true,
                    });
                    vocalsHlsRef.current = hls;
                    hls.loadSource(vocalsAudioUrl);
                    hls.attachMedia(vocalsAudioRef.current!);
                    hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                        // 原人声音频HLS清单解析完成
                        // 立即同步到视频时间
                        vocalsAudioRef.current!.currentTime = videoTime;
                        
                        // 检查是否还在播放状态，避免在解析期间被暂停
                        const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                        if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                            vocalsAudioRef.current!.volume = 1.0;
                            vocalsAudioRef.current!.play().then(() => {
                                // 原人声音频HLS播放成功，已同步到时间
                            }).catch(error => {
                                console.error('原人声音频HLS播放失败:', error);
                            });
                        }
                    });
                    hls.on(Hls.default.Events.ERROR, (event, data) => {
                        console.error('原人声音频HLS错误:', data);
                    });
                } else {
                    // 浏览器不支持HLS，尝试直接播放
                    if (vocalsAudioRef.current) {
                        vocalsAudioRef.current.src = vocalsAudioUrl;
                        vocalsAudioRef.current.addEventListener('canplaythrough', () => {
                            // 原人声音频加载完成
                            if (vocalsAudioRef.current) {
                                vocalsAudioRef.current.currentTime = videoTime;
                                const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                                if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                                    vocalsAudioRef.current.volume = 1.0;
                                    vocalsAudioRef.current.play().then(() => {
                                        // 原人声音频播放成功，已同步到时间
                                    }).catch(error => {
                                        console.error('原人声音频播放失败:', error);
                                    });
                                }
                            }
                        });
                        vocalsAudioRef.current.load();
                    }
                }
            }).catch(error => {
                console.error('加载HLS.js失败:', error);
            });
        } else {
            // 非HLS格式，直接播放
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.src = vocalsAudioUrl;
                vocalsAudioRef.current.addEventListener('canplaythrough', () => {
                    if (vocalsAudioRef.current) {
                        vocalsAudioRef.current.currentTime = videoTime;
                        const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                        if (audioPlayRequestedRef.current && isCurrentlyPlaying) {
                            vocalsAudioRef.current.volume = 1.0;
                            vocalsAudioRef.current.play().then(() => {
                                // 原人声音频播放成功，已同步到时间
                            }).catch(error => {
                                console.error('原人声音频播放失败:', error);
                            });
                        }
                    }
                });
                vocalsAudioRef.current.load();
            }
        }
    };
    
    // 播放音频轨道
    const playAudioTracks = () => {
        if (!videoData) {
            // videoData为空，无法播放音频
            return;
        }

        // 获取当前激活的轨道
        const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';

        // 根据当前轨道，先停止其他轨道的音频
        if (currentActiveTrack === 'original') {
            // 切换到原视频轨道：停止翻译音频
            stopTransAudio();
            if (transAudioRef.current) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
                setCurrentTransAudio(null);
            }
        } else if (currentActiveTrack === 'translated') {
            // 切换到翻译后轨道：停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
        }

        // 设置音频播放请求标记
        audioPlayRequestedRef.current = true;

        // 开始播放音频轨道

        // 播放背景音（根据开关状态决定是否播放）
        if (videoData.backgroud_audio_hls_url && backgroundAudioEnabled) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 准备播放背景音
            if (backgroundAudioRef.current) {
                // 检查是否是HLS格式
                if (backgroundAudioUrl.includes('.m3u8')) {
                    // 检测到HLS格式的背景音，尝试使用HLS.js播放
                    // 对于HLS格式，我们需要使用HLS.js
                    import('hls.js').then((Hls) => {
                        if (Hls.default.isSupported()) {
                            // 在创建HLS实例前，再次检查是否应该继续（可能在异步过程中已被暂停）
                            if (!audioPlayRequestedRef.current) {
                                // 背景音HLS：播放请求已取消，跳过创建HLS实例
                                return;
                            }
                            // 清理之前的HLS实例
                            if (backgroundHlsRef.current) {
                                backgroundHlsRef.current.destroy();
                            }
                            const hls = new Hls.default({
                                enableWorker: true,
                                lowLatencyMode: true, // 启用低延迟模式
                            });
                            backgroundHlsRef.current = hls;
                            hls.loadSource(backgroundAudioUrl);
                            hls.attachMedia(backgroundAudioRef.current!);
                            hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                                // 背景音HLS清单解析完成
                                // 在清单解析完成时，再次检查是否应该播放（可能在解析期间已被暂停）
                                if (!audioPlayRequestedRef.current) {
                                    // 背景音HLS清单解析完成，但播放请求已取消，跳过播放
                                    return;
                                }
                                // 再次检查HLS实例是否还存在（可能在解析期间已被销毁）
                                if (!backgroundHlsRef.current || backgroundHlsRef.current !== hls) {
                                    // 背景音HLS清单解析完成，但HLS实例已被销毁，跳过播放
                                    return;
                                }
                                // 更新音频就绪状态
                                checkAudioReady();
                                
                                // 场景3：音频轨道初始化播放 - 同步到视频时间
                                backgroundAudioRef.current!.currentTime = sourceVideoRef.current?.currentTime || 0;
                                backgroundAudioRef.current!.volume = 1.0;
                                backgroundAudioRef.current!.play().then(() => {
                                    // 背景音HLS播放成功
                                    // 再次检查音频就绪状态
                                    checkAudioReady();
                                }).catch(error => {
                                    console.error('背景音HLS播放失败:', error);
                                });
                            });
                            hls.on(Hls.default.Events.ERROR, (event, data) => {
                                console.error('背景音HLS错误:', data);
                            });
                        } else {
                            console.error('浏览器不支持HLS.js');
                        }
                    }).catch(error => {
                        console.error('加载HLS.js失败:', error);
                    });
                } else {
                    // 普通音频格式
                    backgroundAudioRef.current.src = backgroundAudioUrl;
                    
                    // 监听音频加载完成事件
                    backgroundAudioRef.current.addEventListener('canplaythrough', () => {
                        // 背景音加载完成
                        checkAudioReady();
                    });
                    
                    // 场景3：音频轨道初始化播放 - 同步到视频时间
                    backgroundAudioRef.current.currentTime = sourceVideoRef.current?.currentTime || 0;
                    backgroundAudioRef.current.volume = 1.0;
                    backgroundAudioRef.current.play().then(() => {
                        // 背景音播放成功
                        checkAudioReady();
                    }).catch(error => {
                        console.error('背景音播放失败:', error);
                    });
                }
                // 播放背景音
            } else {
                console.error('backgroundAudioRef.current为空');
            }
        } else {
            // 没有背景音URL
        }

        // 根据当前激活的轨道，初始化对应的人声音频
        if (currentActiveTrack === 'original' && videoData.vocals_hls_url) {
            // 原视频轨道：初始化原人声音频
            initVocalsAudio();
        }
        // 翻译后轨道的人声音频会在时间更新时自动播放（通过 playTransAudioByTime）

    };

    // 检查音频缓冲状态
    const checkAudioBuffer = useCallback(() => {
        const results = {
            backgroundBuffered: false,
            translatedBuffered: false,
            backgroundBufferTime: 0,
            translatedBufferTime: 0
        };

        if (backgroundAudioRef.current) {
            const buffered = backgroundAudioRef.current.buffered;
            const currentTime = backgroundAudioRef.current.currentTime;
            
            for (let i = 0; i < buffered.length; i++) {
                if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
                    results.backgroundBuffered = true;
                    results.backgroundBufferTime = buffered.end(i) - currentTime;
                    break;
                }
            }
        }


        return results;
    }, []);

    // 统一的音视频同步函数 - 使用useCallback优化
    const syncAudioToVideo = useCallback(() => {
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        
        // console.log('同步音频到视频时间:', videoTime);
        
        // 检查音频缓冲状态
        const bufferStatus = checkAudioBuffer();
        // console.log('音频缓冲状态:', bufferStatus);
        
        // 同步背景音 - 无论是否在播放都要同步时间
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.currentTime = videoTime;
            // console.log('背景音已同步到时间:', videoTime);
        }
        
    }, [checkAudioBuffer]);

    // 监听左右视频就绪状态，实现预加载完成检测
    useEffect(() => {
        if (leftVideoReady && rightVideoReady) {
            // 预加载完成：左右视频都已就绪，可以立即播放
            setIsPreloaded(true);
        } else {
            // 预加载状态检查
            setIsPreloaded(false);
        }
    }, [leftVideoReady, rightVideoReady]);

    // 视频播放控制函数 - 使用useCallback优化
    const togglePlay = useCallback(() => {
        if (sourceVideoRef.current) {
            // 播放按钮点击
            
            // 标记为用户手动操作
            userInteractionRef.current = true;
            
            if (isPlaying) {
                // 暂停视频和音频
                sourceVideoRef.current.pause();
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.pause(); } catch {}
                }
                
                // 停止所有音频
                // 1. 停止背景音（只暂停，不销毁HLS实例，以便快速恢复播放）
                if (backgroundAudioRef.current) {
                    backgroundAudioRef.current.pause();
                }
                // 不销毁 backgroundHlsRef，保持HLS实例以便快速恢复播放
                
                // 2. 停止翻译音频
                stopTransAudio();
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                
                // 3. 停止原始台词音频
                stopOriginAudio();
                if (originAudioRef.current) {
                    originAudioRef.current.pause();
                    originAudioRef.current.currentTime = 0;
                    originAudioRef.current.src = '';
                }
                
                // 4. 停止原人声音频（只暂停，不销毁HLS实例，以便快速恢复播放）
                if (vocalsAudioRef.current) {
                    vocalsAudioRef.current.pause();
                    vocalsAudioRef.current.currentTime = 0;
                }
                // 不销毁 vocalsHlsRef，保持HLS实例以便快速恢复播放
                
                // 5. 停止通过 AudioManager 播放的所有音频（翻译后台词播放）
                if (currentPlayingSubtitleIdRef.current !== null) {
                    try {
                        const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                        audioManager.stopSound(audioId);
                        audioManager.destroySound(audioId);
                        // 主控制：停止 AudioManager 音频
                    } catch (error) {
                        console.error('停止 AudioManager 音频时出错:', error);
                    }
                    currentPlayingSubtitleIdRef.current = null;
                }
                // 5b. 按所有字幕 ID 遍历停止 AudioManager，避免 ref 滞后或多种前缀漏停
                try {
                    const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
                    allAudioIds.forEach(prefix => {
                        if (videoData?.subtitles) {
                            videoData.subtitles.forEach(subtitle => {
                                const audioId = `${prefix}_${subtitle.id}`;
                                if (audioManager.isPlaying(audioId)) {
                                    audioManager.stopSound(audioId);
                                    audioManager.destroySound(audioId);
                                }
                            });
                        }
                    });
                } catch (error) {
                    console.error('停止 AudioManager 音频时出错:', error);
                }
                
                // 6. 清除单句台词播放实例和状态
                stopCurrentPlayingInstance();
                setSubtitlePlayEndTime(null);
                
                // 重置音频播放请求标记
                audioPlayRequestedRef.current = false;
                // 主控制：暂停左侧视频和所有音频
            } else {
                // 用户主动点击播放：清除“点击台词跳转”标记，避免播不出当前句（暂停时点台词再点播放的场景）
                isSubtitleClickSeekRef.current = false;
                // 清除防重复播放锁，避免“同一句已播”误判导致不播当前句
                currentPlayingSubtitleIdRef.current = null;
                lastAudioCheckVideoTimeRef.current = 0;
                // 底部播放按钮播放：清除单句台词播放实例和状态
                stopCurrentPlayingInstance();
                setSubtitlePlayEndTime(null);
                setIsPlaying(false);

                // 【抢跑】在视频真正播放前，用略超前的虚拟时间触发一次台词音频，补偿启动延迟，避免句首吞音（进度线保持对齐句首）
                const actualTime = sourceVideoRef.current?.currentTime ?? 0;
                playTransAudioByTime(actualTime + 0.1, { forceReplay: true });

                // 获取当前播放进度
                const currentProgress = sourceVideoRef.current.currentTime;
                // 播放按钮点击：获取当前进度
                
                // 先跳转到当前位置
                if (subtitleDemoRef.current) {
                    subtitleDemoRef.current.seekToTime(currentProgress);
                    // 右侧视频跳转到指定时间
                }
                
                // 检查预加载状态
                if (isPreloaded) {
                    // 快速播放：预加载完成，立即同时播放左右视频
                    
                    // 同时播放左右视频，无需等待
                    const playPromises = [];
                    
                    // 播放左侧视频
                    sourceVideoRef.current.muted = true;
                    playPromises.push(
                        sourceVideoRef.current.play().catch(error => {
                            if (error.name !== 'AbortError') {
                                console.error('左侧视频播放失败:', error);
                            }
                        })
                    );
                    
                    // 同时播放右侧视频
                    if (subtitleDemoRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleDemoRef.current!.play();
                                resolve();
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        // 快速播放：左右视频同时播放成功
                        
                        // 播放音频轨道（人声+背景音）
                        playAudioTracks();
                        syncAudioToVideo();
                    });
                    
                } else {
                    // 传统播放：预加载未完成，使用传统播放方式
                    
                    // 传统播放方式（兼容性处理）
                    sourceVideoRef.current.muted = true;
                    sourceVideoRef.current.play().catch(error => {
                        if (error.name !== 'AbortError') {
                            console.error('左侧视频播放失败:', error);
                        }
                    });
                    
                    // 播放音频轨道（人声+背景音）
                    playAudioTracks();
                    syncAudioToVideo();
                }
                
                // 主控制：播放左侧视频（静音）和音频轨道
            }
            setIsPlaying(!isPlaying);
            // 注意：右侧视频的播放状态会通过externalIsPlaying属性自动同步
        }
    }, [isPlaying, playAudioTracks, syncAudioToVideo, videoData, id, isPreloaded, stopCurrentPlayingInstance]);

    // 监听时间轴片段选择事件：刻度线定位到台词开始位置并跳转视频时间
    // 对于翻译轨道，执行暂停-定位-播放的序列操作
    useEffect(() => {
        const handleSegmentSelected = (e: any) => {
            const time = (e && e.detail && typeof e.detail.time === 'number') ? e.detail.time : 0;
            const trackType = (e && e.detail && e.detail.trackType) ? e.detail.trackType : 'original';
            
            // 【核心修复】：如果是点击台词导致的跳转，不自动播放
            if (isSubtitleClickSeekRef.current) {
                // 只执行跳转，不播放
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.currentTime = time;
                }
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.seekToTime(time); } catch {}
                }
                // 同步音频进度（但不播放）
                if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                    backgroundAudioRef.current.currentTime = time;
                }
                if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                    vocalsAudioRef.current.currentTime = time;
                }
                return; // 直接返回，不执行后续播放逻辑
            }
            
            // 如果是翻译轨道，需要执行暂停-定位-播放的序列操作
            if (trackType === 'translated') {
                // 翻译轨道片段选中：开始暂停-定位-播放序列
                
                // 步骤1：暂停当前音频和视频
                const wasPlaying = isPlaying || (sourceVideoRef.current && !sourceVideoRef.current.paused);
                
                // 暂停所有音频
                if (transAudioRef.current) {
                    // 无论是否在播放，都强制重置 transAudio 状态（强行破冰）
                    if (!transAudioRef.current.paused) {
                        transAudioRef.current.pause();
                    }
                    // 重置音频进度，确保重新播放时从头开始
                    transAudioRef.current.currentTime = 0;
                    // 清除 transAudio 的播放状态，确保 seek 后能重新播放
                    setCurrentTransAudio(null);
                    currentPlayingSubtitleIdRef.current = null;
                    // 翻译轨道 seek：已清除 transAudio 状态，准备重新播放
                }
                if (originAudioRef.current && !originAudioRef.current.paused) {
                    originAudioRef.current.pause();
                }
                if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
                    vocalsAudioRef.current.pause();
                }
                if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
                    backgroundAudioRef.current.pause();
                }
                
                // 暂停视频
                if (sourceVideoRef.current && !sourceVideoRef.current.paused) {
                    sourceVideoRef.current.pause();
                }
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.pause(); } catch {}
                }
                
                // 步骤2：定位播放进度
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.currentTime = time;
                }
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.seekToTime(time); } catch {}
                }
                
                // 同步音频进度
                if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                    backgroundAudioRef.current.currentTime = time;
                }
                if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                    vocalsAudioRef.current.currentTime = time;
                }
                
                // 步骤3：如果之前正在播放，则恢复播放
                if (wasPlaying) {
                    // 使用 setTimeout 确保定位操作完成后再播放
                    setTimeout(() => {
                        if (sourceVideoRef.current) {
                            sourceVideoRef.current.play().catch(error => {
                                console.error('恢复视频播放失败:', error);
                            });
                        }
                        if (subtitleDemoRef.current) {
                            try { subtitleDemoRef.current.play(); } catch {}
                        }
                        if (backgroundAudioRef.current && backgroundAudioRef.current.src) {
                            backgroundAudioRef.current.play().catch(error => {
                                console.error('恢复背景音播放失败:', error);
                            });
                        }
                        if (vocalsAudioRef.current && vocalsAudioRef.current.src) {
                            vocalsAudioRef.current.play().catch(error => {
                                console.error('恢复人声音频播放失败:', error);
                            });
                        }
                        
                        // 强制触发翻译音频播放（强行破冰）
                        // 确保清除状态后立即触发播放，不等待 timeupdate 事件
                        // 触发时间轴点击，通过 Ref 发送强制指令
                        // 翻译轨道片段选中：强制触发翻译音频播放
                        // 使用 ref 确保调用最新的函数版本，打破闭包陷阱
                        if (playTransAudioByTimeRef.current) {
                            playTransAudioByTimeRef.current(time, { forceReplay: true });
                        } else {
                            console.error('!!! 错误：playTransAudioByTimeRef.current 为 null，无法调用播放函数');
                        }
                        
                        // 翻译轨道片段选中：已恢复播放
                    }, 50); // 50ms延迟确保定位操作完成
                } else {
                    // 即使未播放状态，也尝试触发翻译音频（用户可能想预览）
                    // 触发时间轴点击，通过 Ref 发送强制指令（未播放状态）
                    // 翻译轨道片段选中：定位完成（未播放状态），强制触发翻译音频
                    // 使用 forceReplay 绕过 isPlaying 检查，使用 ref 确保调用最新的函数版本，打破闭包陷阱
                    if (playTransAudioByTimeRef.current) {
                        playTransAudioByTimeRef.current(time, { forceReplay: true });
                    } else {
                        console.error('!!! 错误：playTransAudioByTimeRef.current 为 null，无法调用播放函数');
                    }
                }
            } else {
                // 原始轨道：只跳转时间，不自动播放
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.currentTime = time;
                }
                if (subtitleDemoRef.current) {
                    try { subtitleDemoRef.current.seekToTime(time); } catch {}
                }
                // 控制台输出跳转时间戳（秒）
                try {
                    const ts = Number.isFinite(time) ? time : 0;
                    // 原始轨道片段选中：跳转到时间戳
                } catch {
                    // 原始轨道片段选中：跳转到时间戳（回退）
                }
            }
        };
        window.addEventListener('timeline:segmentSelected', handleSegmentSelected);
        return () => window.removeEventListener('timeline:segmentSelected', handleSegmentSelected);
    }, [isPlaying]);

    // SubtitleDemo回调函数 - 使用useCallback优化
    const handleExternalPlay = useCallback(() => {
        // 同步控制左侧视频播放
        // SubtitleDemo请求播放，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.play().catch(error => {
                console.error('左侧视频播放失败:', error);
            });
        }
    }, []);
    
    // 右侧视频就绪状态回调
    const handleRightVideoReady = useCallback(() => {
        // 右侧视频已就绪
        setRightVideoReady(true);
    }, []);

    const handleExternalPause = useCallback(() => {
        // 同步控制左侧视频暂停
        // SubtitleDemo请求暂停，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
        }
    }, []);

    const handleExternalSeek = useCallback((time: number) => {
        // 同步控制左侧视频进度
        // SubtitleDemo请求跳转到时间，同步左侧视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = time;
            setCurrentTime(time);
        }
    }, []);

    // 右侧视频控制函数
    const toggleRightVideoPlay = useCallback(() => {
        if (subtitleDemoRef.current) {
            if (rightVideoIsPlaying) {
                subtitleDemoRef.current.pause();
                setRightVideoIsPlaying(false);
                // 右侧视频：暂停
            } else {
                subtitleDemoRef.current.play();
                setRightVideoIsPlaying(true);
                // 右侧视频：播放
            }
        }
    }, [rightVideoIsPlaying]);

    const handleRightVideoSeek = useCallback((time: number) => {
        if (subtitleDemoRef.current) {
            subtitleDemoRef.current.seekToTime(time);
            setRightVideoCurrentTime(time);
            // 右侧视频：跳转到时间
        }
    }, []);

    // 监听右侧视频状态变化
    useEffect(() => {
        const interval = setInterval(() => {
            if (subtitleDemoRef.current) {
                const currentTime = subtitleDemoRef.current.getCurrentTime();
                const duration = subtitleDemoRef.current.getDuration();
                const isPlaying = subtitleDemoRef.current.isPlaying();
                
                setRightVideoCurrentTime(currentTime);
                setRightVideoDuration(duration);
                setRightVideoIsPlaying(isPlaying);
            }
        }, 100); // 每100ms更新一次状态

        return () => clearInterval(interval);
    }, []);

    // 时间更新防抖
    const timeUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastUpdateTimeRef = useRef<number>(0);

    // 检查音频是否就绪
    const checkAudioReady = () => {
        const backgroundReady = backgroundAudioRef.current && 
            backgroundAudioRef.current.readyState >= 2 && // HAVE_CURRENT_DATA
            backgroundAudioRef.current.src;
        
        const newState = { backgroundReady: !!backgroundReady, vocalsReady: false };
        setAudioReadyState(newState);
        
        // console.log('音频就绪状态检查:', newState);
        return newState.backgroundReady;
    };

    // 暂停台词播放的统一函数
    const pauseSubtitlePlayback = () => {
        // 台词播放：执行暂停流程
        
        // 保存结束时间，用于定位视频和时间轴
        const endTime = subtitlePlayEndTime;
        
        // 清除音频等待定时器
        if (audioReadyTimeoutRef.current) {
            clearTimeout(audioReadyTimeoutRef.current);
            audioReadyTimeoutRef.current = null;
        }
        
        // 同时暂停左右视频和音频，并停在结束时间戳
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
            if (endTime !== null) {
                sourceVideoRef.current.currentTime = endTime;
            }
        }
        if (subtitleDemoRef.current) {
            subtitleDemoRef.current.pause();
            if (endTime !== null) {
                try {
                    subtitleDemoRef.current.seekToTime(endTime);
                } catch {}
            }
        }
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        // 停止原始台词音频和翻译音频
        stopOriginAudio();
        stopTransAudio();
        // 停止所有通过 AudioManager 播放的当句台词音频（与 togglePlay 一致）
        try {
            if (currentPlayingSubtitleIdRef.current !== null) {
                const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
            allAudioIds.forEach(prefix => {
                if (videoData?.subtitles) {
                    videoData.subtitles.forEach(subtitle => {
                        const audioId = `${prefix}_${subtitle.id}`;
                        if (audioManager.isPlaying(audioId)) {
                            audioManager.stopSound(audioId);
                            audioManager.destroySound(audioId);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('停止 AudioManager 音频时出错:', error);
        }
        currentPlayingSubtitleIdRef.current = null;
        
        // 停止原人声音频
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        
        // 清理HLS实例
        if (backgroundHlsRef.current) {
            backgroundHlsRef.current.destroy();
            backgroundHlsRef.current = null;
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        
        // 重置音频播放请求标记
        audioPlayRequestedRef.current = false;
        
        // 更新播放状态
        setIsPlaying(false);
        
        // 停止黑色刻度线动画，并定位到结束时间
        if (timelineEditorRef.current) {
            if (timelineEditorRef.current.playheadAnimationId) {
                cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                timelineEditorRef.current.playheadAnimationId = undefined;
            }
            // 将播放头定位到结束时间
            if (endTime !== null) {
                timelineEditorRef.current.currentTime = endTime;
                timelineEditorRef.current.updatePlayheadPosition();
            }
        }
        
        // 清除台词播放结束时间
        setSubtitlePlayEndTime(null);
        
        // 设置刚结束台词播放的标志，防止立即播放翻译音频
        setJustFinishedSubtitlePlay(true);
        
        // 延迟重置标志，给用户一些时间
        setTimeout(() => {
            setJustFinishedSubtitlePlay(false);
            // 台词播放：已重置播放标志，可以正常播放翻译音频
        }, 2000); // 2秒后重置
        
        console.log('台词播放：已暂停播放并同步右侧视频');
    };

    // 安全停止翻译音频 - 只在必要时停止
    const safeStopTransAudio = useCallback(() => {
        // 清除当前播放的字幕ID
        currentPlayingSubtitleIdRef.current = null;
        
        if (transAudioRef.current && !transAudioRef.current.paused) {
            // 检查音频是否真的需要停止（比如超出了字幕时间范围）
            const currentTime = sourceVideoRef.current?.currentTime || 0;
            
            if (!videoData || !videoData.subtitles) {
                transAudioRef.current.pause();
                setCurrentTransAudio(null);
                return;
            }
            
            // 使用与playTransAudioByTime相同的逻辑查找匹配的字幕
            let currentSubtitle: Task.SubtitlesLegacy | null = null;
            for (const subtitle of videoData.subtitles) {
                if (!subtitle.post_start_time || !subtitle.post_end_time) {
                    continue;
                }
                
                const subTargetData = targetText.find(item => item.id === subtitle.id);
                let subStartTime: number;
                let subEndTime: number;
                
                if (subTargetData && subTargetData.timeRange) {
                    const timeRangeParts = subTargetData.timeRange.split(' → ');
                    if (timeRangeParts.length === 2) {
                        subStartTime = parseTimeToSecondsHelper(timeRangeParts[0].trim());
                        subEndTime = parseTimeToSecondsHelper(timeRangeParts[1].trim());
                    } else {
                        subStartTime = parseTimeToSecondsHelper(subtitle.post_start_time);
                        subEndTime = parseTimeToSecondsHelper(subtitle.post_end_time);
                    }
                } else {
                    subStartTime = parseTimeToSecondsHelper(subtitle.post_start_time);
                    subEndTime = parseTimeToSecondsHelper(subtitle.post_end_time);
                }
                
                if (currentTime >= subStartTime && currentTime <= subEndTime) {
                    currentSubtitle = subtitle;
                    break;
                }
            }
            
            // 如果当前时间不在任何字幕范围内，或者没有对应的翻译音频，才停止播放
            if (!currentSubtitle || !currentSubtitle.trans_audio) {
                transAudioRef.current.pause();
                setCurrentTransAudio(null);
                console.log('安全停止翻译音频：当前时间不在字幕范围内');
            } else {
                console.log('保持翻译音频播放：当前时间在字幕范围内');
            }
        }
    }, [videoData, targetText, parseTimeToSecondsHelper]);
    
    // 更新 safeStopTransAudio ref 以存储最新的函数引用
    useEffect(() => {
        safeStopTransAudioRef.current = safeStopTransAudio;
    }, [safeStopTransAudio]);

    // 音频与视频校对函数
    const checkAndSyncAudio = () => {
        const videoTime = sourceVideoRef.current?.currentTime || 0;
        
        console.log('校对音频与视频时间，视频时间:', videoTime);
        
        // 检查背景音时间差异
        if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            const audioTime = backgroundAudioRef.current.currentTime;
            const timeDiff = Math.abs(audioTime - videoTime);
            console.log('背景音时间差异:', timeDiff, '音频时间:', audioTime, '视频时间:', videoTime);
            
            // 如果时间差异超过0.1秒，则同步
            if (timeDiff > 0.1) {
                backgroundAudioRef.current.currentTime = videoTime;
                console.log('背景音时间差异过大，已同步到视频时间:', videoTime);
            } else {
                console.log('背景音时间差异在允许范围内，无需同步');
            }
        }
        
    };

    // 自动播放时确保音频开始播放并校对
    const ensureAudioPlayAndSync = async () => {
        console.log('自动播放：确保音频开始播放并校对');
        
        // 检查音频是否已初始化（有音频源）
        const backgroundAudioInitialized = backgroundAudioRef.current && backgroundAudioRef.current.src;
        
        console.log('音频初始化状态:', {
            backgroundAudioInitialized,
            backgroundAudioSrc: backgroundAudioRef.current?.src,
        });
        
        // 如果音频未初始化，先初始化音频轨道
        if (!backgroundAudioInitialized) {
            console.log('自动播放：音频未初始化，先初始化音频轨道');
            playAudioTracks();
            
            // 等待音频初始化完成
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // 先确保音频开始播放
        const audioPromises = [];
        
        // 检查背景音（根据开关状态决定是否播放）
        if (backgroundAudioRef.current && backgroundAudioEnabled) {
            if (backgroundAudioRef.current.paused) {
                console.log('自动播放：启动背景音');
                audioPromises.push(backgroundAudioRef.current.play().catch(error => {
                    console.error('自动播放背景音失败:', error);
                }));
            } else {
                console.log('自动播放：背景音已在播放');
            }
        } else {
            if (!backgroundAudioEnabled) {
                console.log('自动播放：背景音开关已关闭，跳过播放背景音');
            } else {
                console.log('自动播放：背景音引用为空');
            }
        }
        
        
        // 等待音频开始播放
        if (audioPromises.length > 0) {
            await Promise.all(audioPromises);
            console.log('自动播放：音频已开始播放');
        }
        
        // 延迟一小段时间确保音频已经开始播放，然后进行校对
        setTimeout(() => {
            checkAndSyncAudio();
            // 注意：右侧视频现在通过等待机制同步，不需要额外的校对
        }, 10);
    };

    // 校对右侧视频与左侧视频的播放画面
    const syncRightVideoToLeft = () => {
        const leftVideoTime = sourceVideoRef.current?.currentTime || 0;
        console.log('校对右侧视频：同步到左侧视频时间', leftVideoTime);
        
        // 通过更新currentTime状态来触发右侧视频同步
        setCurrentTime(leftVideoTime);
        
        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (leftVideoTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        console.log('右侧视频已同步到左侧视频时间:', leftVideoTime);
    };

    // 增强的校对功能，确保画面完全一致
    const enhancedSyncRightVideoToLeft = () => {
        const leftVideoTime = sourceVideoRef.current?.currentTime || 0;
        console.log('增强校对：开始同步右侧视频到左侧视频时间', leftVideoTime);
        
        // 立即更新状态，触发右侧视频同步
        setCurrentTime(leftVideoTime);
        
        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (leftVideoTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        // 延迟再次同步，确保右侧视频完全同步
        setTimeout(() => {
            const currentLeftTime = sourceVideoRef.current?.currentTime || 0;
            console.log('增强校对：二次同步，当前左侧视频时间', currentLeftTime);
            
            // 再次更新状态，确保同步
            setCurrentTime(currentLeftTime);
            
            // 更新进度条
            if (progressBar && duration > 0) {
                const progressPercent = (currentLeftTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            console.log('增强校对：右侧视频二次同步完成', currentLeftTime);
        }, 100);
        
        console.log('增强校对：右侧视频同步完成', leftVideoTime);
    };

    // 台词播放功能
    const playSubtitle = (timeRange: string) => {
        // 检查当前轨道，如果需要切换则先切换，等待切换完成后再播放
        const currentTrack = timelineEditorRef.current?.activeTrack;
        const targetTrack = 'original';
        
        if (currentTrack !== targetTrack) {
            console.log('原台词播放：检测到当前轨道是', currentTrack, '，需要切换到', targetTrack);
            handleSwitchTrack(targetTrack);
            // 等待轨道切换完成（使用 requestAnimationFrame 确保 DOM 更新完成）
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // 再次确认轨道已切换
                    if (timelineEditorRef.current?.activeTrack === targetTrack) {
                        console.log('原台词播放：轨道已切换到', targetTrack, '，开始播放');
                        executePlaySubtitle(timeRange);
                    } else {
                        console.warn('原台词播放：轨道切换失败，但仍继续播放');
                        executePlaySubtitle(timeRange);
                    }
                });
            });
        } else {
            console.log('原台词播放：当前已在', targetTrack, '轨道，直接播放');
            executePlaySubtitle(timeRange);
        }
    };
    
    // 执行原台词播放逻辑
    const executePlaySubtitle = (timeRange: string) => {
        // 全局唯一播放实例管理：先停止当前播放实例
        stopCurrentPlayingInstance();
        
        // 先完全停止并销毁原人声音频，避免与单句原台词音频重叠
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
            vocalsAudioRef.current.currentTime = 0;
            vocalsAudioRef.current.src = ''; // 清空音频源，防止重新播放
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        // 设置标志，防止在播放单句原台词期间重新初始化原人声音频
        audioPlayRequestedRef.current = false;
        
        // 若当前整体处于播放状态，先统一暂停，避免状态冲突导致单句播放失效
        if (isPlaying) {
            if (sourceVideoRef.current) sourceVideoRef.current.pause();
            if (subtitleDemoRef.current) try { subtitleDemoRef.current.pause(); } catch {}
            if (backgroundAudioRef.current) backgroundAudioRef.current.pause();
            stopTransAudio();
            stopOriginAudio();
            setIsPlaying(false);
        }
        console.log('台词播放：开始播放台词', timeRange);
        
        // 重置刚结束台词播放的标志
        setJustFinishedSubtitlePlay(false);
        
        // 解析时间范围
        const [startTimeStr, endTimeStr] = timeRange.split(' → ');
        const startTime = parseTimeToSeconds(startTimeStr);
        const endTime = parseTimeToSeconds(endTimeStr);
        
        console.log('台词播放：时间范围', { startTime, endTime });
        
        // 清除之前的定时器
        if (subtitlePlayTimeoutRef.current) {
            clearTimeout(subtitlePlayTimeoutRef.current);
        }
        
        // 定位刻度线与时间轴视图
        try {
            if (timelineEditorRef.current) {
                timelineEditorRef.current.currentTime = startTime;
                timelineEditorRef.current.updatePlayheadPosition();
                if (typeof (timelineEditorRef.current as any).scrollToTime === 'function') {
                    (timelineEditorRef.current as any).scrollToTime(startTime);
                }
            }
        } catch {}
        // 使用精确同步机制
        const waitForVideosReadyAndPlay = async () => {
            console.log('台词播放：开始精确同步', startTime);
            
            // 更新进度条显示
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar && duration > 0) {
                const progressPercent = (startTime / duration) * 100;
                progressBar.style.setProperty('--progress', `${progressPercent}%`);
            }
            
            setCurrentTime(startTime);
            
            // 使用精确同步启动
            await startPreciseSync(startTime);
            
            console.log('台词播放：精确同步完成，开始播放');
            
            // 现在两个视频都精确同步了，真正同时开始播放
            console.log('台词播放：准备同时启动两个视频');
            
            // 使用requestAnimationFrame确保在同一帧内启动
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    const playPromises: Promise<any>[] = [];
                    
                    if (sourceVideoRef.current) {
                        sourceVideoRef.current.muted = true;
                        playPromises.push(
                            sourceVideoRef.current.play().catch(error => {
                                console.error('台词播放：左侧视频播放失败:', error);
                            })
                        );
                    }
                    
                    if (subtitleDemoRef.current) {
                        playPromises.push(
                            new Promise<void>((resolve) => {
                                subtitleDemoRef.current!.play();
                                // 给右侧视频一点时间启动
                                setTimeout(() => resolve(), 10);
                            })
                        );
                    }
                    
                    // 等待两个视频都开始播放
                    Promise.all(playPromises).then(() => {
                        console.log('台词播放：两个视频已真正同时开始播放，毫秒级同步已启动');
                        resolve();
                    });
                });
            });
            
            // 停止翻译音频播放
            stopTransAudio();
            
            // 播放原始台词音频（纯净人声）
            playOriginAudio(timeRange);
            
            // 全局唯一播放实例管理：记录新的播放实例
            currentPlayingInstanceRef.current = {
                type: 'origin',
                timeRange: timeRange
            };
            console.log('全局播放管理：记录新的原台词播放实例', timeRange);
            
            // 播放原始台词时，暂停背景音，只播放台词
            console.log('播放原始台词：暂停背景音，只播放台词音频');
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            
            console.log('台词播放：所有媒体已同时开始播放');
        };
        
        // 开始等待并播放流程
        waitForVideosReadyAndPlay();
        
        // 标记为用户手动操作
        userInteractionRef.current = true;
        
        // 立即同步音频到开始时间
        syncAudioToVideo();
        
        // 设置播放状态
        setIsPlaying(true);
        
        // 注意：右侧视频已通过直接控制同步，无需额外同步
        
        // 设置结束时间，用于在时间更新时检查是否需要暂停
        setSubtitlePlayEndTime(endTime);
        
        console.log('台词播放：已设置结束时间', endTime);
    };

    // 场景4：字幕编辑时的音频控制函数
    const pauseAudioForEditing = () => {
        console.log('字幕编辑：暂停所有视频和音频');
        
        // 停止当前播放实例
        stopCurrentPlayingInstance();
        
        // 暂停视频
        if (sourceVideoRef.current) {
            sourceVideoRef.current.pause();
        }
        if (subtitleDemoRef.current) {
            try { subtitleDemoRef.current.pause(); } catch {}
        }
        
        // 暂停背景音频
        if (backgroundAudioRef.current) {
            backgroundAudioRef.current.pause();
        }
        // 清理背景音频HLS实例（防止HLS实例继续播放）
        if (backgroundHlsRef.current) {
            backgroundHlsRef.current.destroy();
            backgroundHlsRef.current = null;
        }
        
        // 停止翻译音频
        stopTransAudio();
        if (transAudioRef.current) {
            transAudioRef.current.pause();
            transAudioRef.current.currentTime = 0;
        }
        
        // 停止原人声音频
        stopOriginAudio();
        if (originAudioRef.current) {
            originAudioRef.current.pause();
            originAudioRef.current.currentTime = 0;
        }
        
        // 停止原人声音频轨道
        if (vocalsAudioRef.current) {
            vocalsAudioRef.current.pause();
        }
        if (vocalsHlsRef.current) {
            vocalsHlsRef.current.destroy();
            vocalsHlsRef.current = null;
        }
        
        // 停止所有通过 AudioManager 播放的音频
        try {
            if (currentPlayingSubtitleIdRef.current !== null) {
                const audioId = `target_audio_${currentPlayingSubtitleIdRef.current}`;
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            // 停止所有可能的音频实例
            const allAudioIds = ['target_audio', 'segment_audio', 'origin_audio'];
            allAudioIds.forEach(prefix => {
                // 尝试停止所有可能的音频ID（通过遍历所有字幕ID）
                if (videoData?.subtitles) {
                    videoData.subtitles.forEach(subtitle => {
                        const audioId = `${prefix}_${subtitle.id}`;
                        if (audioManager.isPlaying(audioId)) {
                            audioManager.stopSound(audioId);
                            audioManager.destroySound(audioId);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('停止 AudioManager 音频时出错:', error);
        }
        
        // 清除播放状态
        setSubtitlePlayEndTime(null);
        setIsPlaying(false);
        setCurrentTransAudio(null);
        // 重置音频播放请求标记（防止异步创建的HLS实例自动播放）
        audioPlayRequestedRef.current = false;
        currentPlayingSubtitleIdRef.current = null;
        
        // 停止黑色刻度线动画
        if (timelineEditorRef.current && timelineEditorRef.current.playheadAnimationId) {
            cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
            timelineEditorRef.current.playheadAnimationId = undefined;
        }
    };

    const syncAudioAfterEditing = async () => {
        console.log('字幕编辑完成：同步音频并恢复播放');
        
        // 检查音频是否已初始化
        const backgroundAudioInitialized = backgroundAudioRef.current && backgroundAudioRef.current.src;
        
        // 如果音频未初始化，先初始化音频轨道
        if (!backgroundAudioInitialized) {
            console.log('字幕编辑完成：音频未初始化，先初始化音频轨道');
            playAudioTracks();
            
            // 等待音频初始化完成
            await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        syncAudioToVideo();
        // 恢复播放状态
        setIsPlaying(true);
        // 恢复视频播放
        if (sourceVideoRef.current) {
            sourceVideoRef.current.muted = true; // 保持静音
            sourceVideoRef.current.play().catch(error => {
                console.error('恢复视频播放失败:', error);
            });
        }
        // 恢复音频播放（根据开关状态决定是否播放）
        if (backgroundAudioRef.current && backgroundAudioEnabled) {
            backgroundAudioRef.current.play().catch(error => {
                console.error('恢复背景音播放失败:', error);
            });
        } else if (!backgroundAudioEnabled) {
            console.log('恢复播放：背景音开关已关闭，跳过播放背景音');
        }
        // 注意：右侧视频现在通过等待机制同步，不需要额外的校对
    };
    const handleTimeUpdate = () => {
        if (sourceVideoRef.current) {
            const currentTime = sourceVideoRef.current.currentTime;
            const duration = sourceVideoRef.current.duration;
            
            // 验证时间是否有效
            if (isNaN(currentTime) || currentTime < 0) {
                console.warn('检测到无效的当前时间:', currentTime);
                return;
            }
            
            // 检查台词播放是否结束
            if (subtitlePlayEndTime !== null && currentTime + END_EPSILON >= subtitlePlayEndTime) {
                console.log('台词播放：到达结束时间，检查音频状态', { 
                    currentTime, 
                    endTime: subtitlePlayEndTime,
                    audioReady: checkAudioReady(),
                    audioReadyState 
                });
                
                // 立即停止视频和时间轴，停在结束时间戳
                if (sourceVideoRef.current) {
                    sourceVideoRef.current.pause();
                    sourceVideoRef.current.currentTime = subtitlePlayEndTime;
                }
                if (subtitleDemoRef.current) {
                    subtitleDemoRef.current.pause();
                    try {
                        subtitleDemoRef.current.seekToTime(subtitlePlayEndTime);
                    } catch {}
                }
                
                // 全局唯一播放实例管理：清除播放实例记录
                if (currentPlayingInstanceRef.current?.type === 'origin') {
                    console.log('全局播放管理：原台词播放结束，清除播放实例记录');
                    currentPlayingInstanceRef.current = null;
                } else if (currentPlayingInstanceRef.current?.type === 'target') {
                    console.log('全局播放管理：翻译后台词播放结束，清除播放实例记录');
                    currentPlayingInstanceRef.current = null;
                }
                
                // 更新时间轴播放头到结束位置
                if (timelineEditorRef.current) {
                    timelineEditorRef.current.currentTime = subtitlePlayEndTime;
                    timelineEditorRef.current.updatePlayheadPosition();
                    // 停止播放头动画
                    if (timelineEditorRef.current.playheadAnimationId) {
                        cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                        timelineEditorRef.current.playheadAnimationId = undefined;
                    }
                }
                
                // 检查音频是否真正开始播放
                const backgroundPlaying = backgroundAudioRef.current && !backgroundAudioRef.current.paused;
                const hasAudioStarted = backgroundPlaying;
                
                // 如果音频还没开始播放，给一个缓冲时间
                if (!hasAudioStarted && audioReadyState.backgroundReady) {
                    console.log('台词播放：音频已就绪但未开始播放，等待音频启动');
                    
                    // 清除之前的超时定时器
                    if (audioReadyTimeoutRef.current) {
                        clearTimeout(audioReadyTimeoutRef.current);
                    }
                    
                    // 设置一个较短的等待时间，让音频有机会开始播放
                    audioReadyTimeoutRef.current = setTimeout(() => {
                        console.log('台词播放：音频启动等待超时，继续暂停流程');
                        pauseSubtitlePlayback();
                    }, 500); // 500ms等待时间
                    
                    return;
                }
                
                // 如果音频已经开始播放，或者没有音频轨道，直接暂停
                pauseSubtitlePlayback();
                return;
            }
            
            // 字幕渲染已移到rvfc帧内执行，避免阻塞主线程
            // renderSubtitleToCanvas(currentTime);
            
            // 根据选中的轨道播放不同的音频
            const currentActiveTrack = timelineEditorRef.current?.activeTrack || 'translated';
            
            if (currentActiveTrack === 'translated') {
                // 选中"翻译后"轨道：播放背景音+台词片段
            // 根据时间戳播放对应的翻译音频（减少调用频率）
            // 只有在时间变化较大时才重新检查音频播放
            // 如果是点击台词导致的跳转，不自动播放音频
            if (!isSubtitleClickSeekRef.current && Math.abs(currentTime - (lastAudioCheckTimeRef.current || 0)) > 0.1) {
                playTransAudioByTime(currentTime);
                lastAudioCheckTimeRef.current = currentTime;
                }
            } else if (currentActiveTrack === 'original') {
                // 选中"原视频"轨道：播放背景音+原人声音频（整个完整音频，不需要分段）
                // 如果正在播放单句原台词，不要自动播放原人声音频，避免两个声音重叠
                if (subtitlePlayEndTime === null) {
                    // 只有在没有播放单句原台词时，才自动播放原人声音频
                    // 确保原人声音频正在播放
                    const isCurrentlyPlaying = sourceVideoRef.current && !sourceVideoRef.current.paused;
                    if (vocalsAudioRef.current && vocalsAudioRef.current.paused && isCurrentlyPlaying) {
                        vocalsAudioRef.current.play().catch(error => {
                            console.error('原人声音频播放失败:', error);
                        });
                    }
                    // 如果原人声音频还没有初始化，初始化它
                    if (!vocalsAudioRef.current || !vocalsAudioRef.current.src) {
                        if (videoData?.vocals_hls_url) {
                            initVocalsAudio();
                        }
                    }
                }
            }
            
            // 同步音频轨道时间 - 已禁用以避免播放卡顿
            // if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            //     backgroundAudioRef.current.currentTime = currentTime;
            // }
            
            // 黑色刻度线移动改由独立的 PlayheadController 驱动，这里不再更新播放头

            // 防抖机制：减少状态更新频率（不影响黑色刻度线）
            const now = Date.now();
            if (now - lastUpdateTimeRef.current < 200) { // 200ms内不重复更新状态，进一步减少频率
                return;
            }

            // 清除之前的定时器
            if (timeUpdateTimeoutRef.current) {
                clearTimeout(timeUpdateTimeoutRef.current);
            }

            // 延迟更新状态，避免频繁同步
            timeUpdateTimeoutRef.current = setTimeout(() => {
                setCurrentTime(currentTime);
                lastUpdateTimeRef.current = Date.now();

                // 更新进度条的CSS变量
                const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
                if (progressBar && duration > 0) {
                    const progressPercent = (currentTime / duration) * 100;
                    progressBar.style.setProperty('--progress', `${progressPercent}%`);
                }

                // 根据当前时间高亮对应的目标台词
                updateTargetSubtitleHighlight(currentTime);
            }, 50); // 50ms延迟
        }
    };

    const handleLoadedMetadata = () => {
        if (sourceVideoRef.current) {
            const videoEl = sourceVideoRef.current;
            const duration = videoEl.duration;
            setDuration(duration);

            // 计算并记录左侧视频宽高比
            const videoWidth = videoEl.videoWidth || 0;
            const videoHeight = videoEl.videoHeight || 0;
            if (videoWidth > 0 && videoHeight > 0) {
                const ratio = Number((videoWidth / videoHeight).toFixed(6));
                // 约简比例（如 1920x1080 -> 16:9）
                const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
                const g = gcd(videoWidth, videoHeight);
                const simplified = `${Math.round(videoWidth / g)}:${Math.round(videoHeight / g)}`;
                setLeftVideoAspect({ width: videoWidth, height: videoHeight, ratio, simplified });
                // 左侧视频分辨率已获取
            }

            // 初始化进度条
            const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
            if (progressBar) {
                progressBar.style.setProperty('--progress', '0%');
            }
            
            // 重新计算时间轴总时长，确保使用视频的实际时长（不向上取整）
            if (timelineEditorRef.current) {
                // 视频元数据加载完成，重新计算时间轴总时长
                
                // 计算字幕的最大结束时间
                let maxEndTime = 0;
                if (videoData?.subtitles && videoData.subtitles.length > 0) {
                    videoData.subtitles.forEach((subtitle: any) => {
                        const endTimeStr = subtitle.post_end_time || subtitle.origin_end_time || '00:00:00,000';
                        const timeParts = endTimeStr.split(':');
                        const hours = parseInt(timeParts[0] || '0');
                        const minutes = parseInt(timeParts[1] || '0');
                        const secondsParts = timeParts[2] ? timeParts[2].split(',') : ['0', '0'];
                        const seconds = parseInt(secondsParts[0] || '0');
                        const milliseconds = parseInt(secondsParts[1] || '0');
                        const endTime = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
                        if (endTime > maxEndTime) {
                            maxEndTime = endTime;
                        }
                    });
                }
                
                // 使用视频实际时长作为上限，确保不超过视频实际时长（不向上取整）
                const totalTime = Math.max(20, duration);
                console.log('视频实际时长:', duration, '秒，字幕最大结束时间:', maxEndTime, '秒，计算得到的总时长:', totalTime, '秒');
                timelineEditorRef.current.totalTime = totalTime;
                timelineEditorRef.current.generateTimeMarks();
                timelineEditorRef.current.applyZoom();
                
                // 如果有字幕数据，重新初始化字幕片段
                if (videoData?.subtitles && videoData.subtitles.length > 0) {
                    timelineEditorRef.current.initSubtitles(videoData.subtitles);
                }
            }
        }
    };

    // 右侧视频元数据加载，记录右侧宽高比
    // 注意：右侧视频现在由SubtitleDemo组件管理，此函数暂时保留但不再使用targetVideoRef
    const handleRightLoadedMetadata = () => {
        // 右侧视频现在由SubtitleDemo组件管理，不需要直接操作targetVideoRef
        console.log('右侧视频由SubtitleDemo组件管理');
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = parseFloat(e.target.value);
        const duration = sourceVideoRef.current?.duration || 0;
        setCurrentTime(newTime);
        
        // 更新进度条显示
        const progressBar = e.target as HTMLInputElement;
        if (duration > 0) {
            const progressPercent = (newTime / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }
        
        if (sourceVideoRef.current) {
            sourceVideoRef.current.currentTime = newTime;
            // 主控制：跳转到时间
            
            // 场景1：用户主动跳转时间 - 同步音频
            syncAudioToVideo();
        }
        
        // 优化：直接控制右侧视频跳转，避免通过props传递的延迟
        if (subtitleDemoRef.current) {
            subtitleDemoRef.current.seekToTime(newTime);
            // 主控制：直接跳转右侧视频到时间
        }

        // 根据拖拽时间高亮对应的目标台词
        updateTargetSubtitleHighlight(newTime);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (sourceVideoRef.current) {
            sourceVideoRef.current.volume = newVolume;
        }
        // 注意：右侧视频现在由SubtitleDemo组件管理，音量控制通过回调函数处理
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // 格式化时间（带小数）
    const formatTimeWithDecimals = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = (time % 60).toFixed(1);
        return `${minutes.toString().padStart(2, '0')}:${seconds.padStart(4, '0')}`;
    };

    // 检查视频同步状态
    const checkVideoSync = () => {
        if (sourceVideoRef.current) {
            const leftTime = sourceVideoRef.current.currentTime;
            const leftDuration = sourceVideoRef.current.duration;
            const leftPlaying = !sourceVideoRef.current.paused;
            
            console.log(`视频同步状态检查:
                左侧视频: 时间=${leftTime.toFixed(2)}s, 总时长=${leftDuration.toFixed(2)}s, 播放中=${leftPlaying}
                右侧视频: 由SubtitleDemo管理, 外部播放状态=${isPlaying}, 外部时间=${currentTime.toFixed(2)}s`);
        }
    };

    // WebVTT 时间格式化（确保为 HH:MM:SS.mmm）
    const formatTimeStringForVtt = (timeString: string): string => {
        if (!timeString) return '00:00:00.000';
        const normalized = timeString.replace(',', '.');
        const parts = normalized.split(':');
        let hours = '00', minutes = '00', secondsMs = '00.000';
        if (parts.length === 3) {
            [hours, minutes] = parts;
            secondsMs = parts[2].includes('.') ? parts[2] : `${parts[2]}.000`;
        } else if (parts.length === 2) {
            [minutes] = parts;
            secondsMs = parts[1].includes('.') ? parts[1] : `${parts[1]}.000`;
        } else if (parts.length === 1) {
            secondsMs = parts[0].includes('.') ? parts[0] : `${parts[0]}.000`;
        }
        const pad2 = (v: string) => v.padStart(2, '0');
        const ensureMs = (v: string) => {
            const [s, ms = '000'] = v.split('.');
            return `${pad2(s)}.${ms.padEnd(3, '0').slice(0,3)}`;
        };
        return `${pad2(hours)}:${pad2(minutes)}:${ensureMs(secondsMs)}`;
    };

    // 基于字幕数组生成 WebVTT 文本，仅使用翻译后的目标文本
    const buildVttFromSubtitles = (subs: Array<Task.SubtitlesLegacy>): string => {
        const lines: string[] = ['WEBVTT', ''];
        subs.forEach((s, idx) => {
            const start = formatTimeStringForVtt(s.post_start_time || s.origin_start_time);
            const end = formatTimeStringForVtt(s.post_end_time || s.origin_end_time);
            const text = (s.post_text || s.origin_text || '').replace(/\r?\n/g, '\n');
            lines.push(String(idx + 1));
            lines.push(`${start} --> ${end}`);
            lines.push(text);
            lines.push('');
        });
        return lines.join('\n');
    };

    // 解析时间戳字符串为秒数
    const parseTimeToSeconds = (timeString: string): number => {
        // 支持格式: "00:00:22,566" 或 "00:00:22.566"
        const timeStr = timeString.replace(',', '.');
        const parts = timeStr.split(':');
        
        if (parts.length === 3) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const seconds = parseFloat(parts[2]);
            return hours * 3600 + minutes * 60 + seconds;
        } else if (parts.length === 2) {
            const minutes = parseInt(parts[0], 10);
            const seconds = parseFloat(parts[1]);
            return minutes * 60 + seconds;
        }
        
        return 0;
    };

    // 根据时间高亮对应的目标台词（同时检查原台词和目标台词的时间范围）
    // 重合区间内取“起始时间更晚”的那条，避免下键切到下一句时高亮被上一句抢回
    const updateTargetSubtitleHighlight = (timeInSeconds: number) => {
        try {
            if (!videoData?.subtitles || videoData.subtitles.length === 0) {
                return;
            }

            const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
            const useOriginTimes = activeTrack === 'original';

            // 遍历所有包含当前时间的字幕，保留起始时间最晚的那条（重合时选“更新”的那句）
            let matchedSubtitleId: number | null = null;
            let matchedText: string = '';
            let bestStart = -1;
            for (const subtitle of videoData.subtitles) {
                const originStart = parseTimeToSeconds(subtitle.origin_start_time);
                const originEnd = parseTimeToSeconds(subtitle.origin_end_time);
                const postStart = subtitle.post_start_time ? parseTimeToSeconds(subtitle.post_start_time) : null;
                const postEnd = subtitle.post_end_time ? parseTimeToSeconds(subtitle.post_end_time) : null;

                const isInOriginRange = timeInSeconds >= originStart && timeInSeconds < originEnd;
                const isInPostRange = postStart !== null && postEnd !== null &&
                                      timeInSeconds >= postStart && timeInSeconds < postEnd;

                if (isInOriginRange || isInPostRange) {
                    const segmentStart = useOriginTimes ? originStart : (postStart ?? originStart);
                    if (segmentStart > bestStart) {
                        bestStart = segmentStart;
                        matchedSubtitleId = subtitle.id;
                        matchedText = subtitle.post_text || subtitle.origin_text || '';
                    }
                }
            }

            // 空隙时段（当前时间不在任一句内）：高亮“下一句”（第一个 start >= 当前时间），避免列表无高亮、下键无效
            if (matchedSubtitleId === null) {
                const firstAhead = videoData.subtitles.findIndex(s => {
                    const os = parseTimeToSeconds(s.origin_start_time);
                    const ps = s.post_start_time ? parseTimeToSeconds(s.post_start_time) : null;
                    const start = useOriginTimes ? os : (ps ?? os);
                    return start >= timeInSeconds;
                });
                if (firstAhead >= 0) {
                    matchedSubtitleId = videoData.subtitles[firstAhead].id;
                    matchedText = videoData.subtitles[firstAhead].post_text || videoData.subtitles[firstAhead].origin_text || '';
                } else if (videoData.subtitles.length > 0) {
                    matchedSubtitleId = videoData.subtitles[videoData.subtitles.length - 1].id;
                    matchedText = videoData.subtitles[videoData.subtitles.length - 1].post_text || videoData.subtitles[videoData.subtitles.length - 1].origin_text || '';
                }
            }

            // 更新高亮状态
            if (matchedSubtitleId !== highlightedSubtitleId) {
                setHighlightedSubtitleId(matchedSubtitleId);
                
                // 滚动到高亮的台词
                if (matchedSubtitleId !== null) {
                    scrollToHighlightedSubtitle(matchedSubtitleId);
                }
            }

            // 注意：字幕渲染现在在handleTimeUpdate中立即执行，确保字幕跟随时间戳准确显示/消失

        } catch (error) {
            console.error('更新目标台词高亮失败:', error);
        }
    };

    // 渲染字幕到Canvas
    const renderSubtitleToCanvas = useCallback((timeInSeconds: number) => {
        const canvas = subtitleCanvasRef.current;
        if (!canvas || !videoData?.subtitles) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // 清空Canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // 查找当前时间段的字幕
        let currentSubtitle = null;
        for (const subtitle of videoData.subtitles) {
            const start = parseTimeToSeconds(subtitle.origin_start_time);
            const end = parseTimeToSeconds(subtitle.origin_end_time);
            
            if (timeInSeconds >= start && timeInSeconds < end) {
                currentSubtitle = subtitle;
                break;
            }
        }

        // 如果有当前字幕，则渲染
        if (currentSubtitle) {
            const text = currentSubtitle.post_text || currentSubtitle.origin_text || '';
            if (text.trim()) {
                renderSubtitleText(ctx, canvas, text);
            }
        }
    }, [videoData]);

    // 渲染字幕文本到Canvas
    const renderSubtitleText = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, text: string) => {
        // 设置字体样式 - 完全按照index.html的样式
        const fontSize = 18; // 固定18px，与index.html一致
        ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 设置最大宽度（Canvas宽度的90%）
        const maxWidth = canvas.width * 0.9;
        const padding = 16; // 固定16px内边距，与index.html一致
        const lineHeight = 24; // 固定24px行高，与index.html一致
        
        // 计算字幕框位置（底部居中，与index.html一致）
        const x = canvas.width / 2;
        const y = canvas.height * 0.8; // 距离底部20%的位置，与index.html一致

        // 处理文本换行
        const lines = wrapText(ctx, text, maxWidth);
        const totalHeight = lines.length * lineHeight;
        const bgWidth = maxWidth + padding * 2;
        const bgHeight = totalHeight + padding * 2;

        // 不绘制背景 - 与index.html一致（透明背景）
        // 只绘制文字，无背景色

        // 绘制文字（多行）
        ctx.fillStyle = '#ffffff';
        lines.forEach((line, index) => {
            const lineY = y - totalHeight / 2 + (index + 0.5) * lineHeight;
            ctx.fillText(line, x, lineY);
        });
    };

    // 使用字幕渲染控制器 - 在rvfc帧内执行，避免阻塞主线程
    useSubtitleRenderer(sourceVideoRef, renderSubtitleToCanvas);

    // 文本换行处理函数
    const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
        const words = text.split('');
        const lines: string[] = [];
        let currentLine = '';

        for (let i = 0; i < words.length; i++) {
            const testLine = currentLine + words[i];
            const metrics = ctx.measureText(testLine);
            
            if (metrics.width > maxWidth && currentLine !== '') {
                lines.push(currentLine);
                currentLine = words[i];
        } else {
                currentLine = testLine;
            }
        }
        
        if (currentLine) {
            lines.push(currentLine);
        }
        
        return lines;
    };

    // 调整Canvas尺寸
    // 注意：此函数现在不再使用，因为右侧视频由SubtitleDemo组件管理
    const resizeCanvas = () => {
        // console.log('resizeCanvas: 右侧视频现在由SubtitleDemo组件管理，此函数不再使用');
        // 右侧视频现在由SubtitleDemo组件管理，不需要调整Canvas尺寸
    };

    // 将目标字幕注入右侧视频，使用原生 WebVTT 轨道，字幕显示在视频底部并脱离文档流
    // 注意：此useEffect已完全移除，因为右侧视频由SubtitleDemo组件管理

    // 滚动到高亮的台词
    const scrollToHighlightedSubtitle = (subtitleId: number) => {
        const container = textListRef.current;
        if (!container) return;

        const targetElement = container.querySelector(`[data-subtitle-id="${subtitleId}"]`) as HTMLElement;
        if (targetElement) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();
            const offset = targetRect.top - containerRect.top;
            
            // 滚动到容器中间
            container.scrollTo({
                top: container.scrollTop + offset - container.clientHeight / 2 + targetElement.clientHeight / 2,
                behavior: 'smooth'
            });
        }
    };

    // 跳转到指定时间（用于点击台词编辑区域，不自动播放）
    // fromKeyboard：由上下键触发时传 true，会缩短“不自动播放”标记的清除延迟，避免按空格/点播放时播不出当前句
    // targetSubtitleId：上下键切换时传入目标台词 id，用于相接/重叠台词时始终按该句居中，否则按时间反推当前句
    const seekToTime = (timeRange: string, fromKeyboard?: boolean, targetSubtitleId?: number) => {
        const startTime = timeRange.split(' → ')[0];
        const timeInSeconds = parseTimeToSeconds(startTime);

        // 设置标记：这是点击台词导致的跳转，不自动播放
        isSubtitleClickSeekRef.current = true;

        // 取消未执行的 timeupdate 延迟回调，避免用旧时间覆盖本次跳转后的高亮（解决下键“弹回”）
        if (timeUpdateTimeoutRef.current) {
            clearTimeout(timeUpdateTimeoutRef.current);
            timeUpdateTimeoutRef.current = null;
        }

        // 【核心修复】：先停止所有正在播放的音频，避免"响一声"
        stopTransAudio();
        stopOriginAudio();
        stopCurrentPlayingInstance();

        // 停止背景音
        if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            backgroundAudioRef.current.pause();
        }

        // 停止原人声音频
        if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
            vocalsAudioRef.current.pause();
        }

        if (sourceVideoRef.current) {
            // 点击台词编辑区域时，先暂停视频，避免自动播放
            if (!sourceVideoRef.current.paused) {
                sourceVideoRef.current.pause();
            }
            sourceVideoRef.current.currentTime = timeInSeconds;
            setCurrentTime(timeInSeconds);
            console.log(`字幕点击跳转：跳转到时间 ${timeInSeconds}秒`);

            // 场景1：用户主动跳转时间 - 同步音频（但不播放）
            syncAudioToVideo();
        }

        // 优化：直接控制右侧视频跳转，避免通过props传递的延迟
        if (subtitleDemoRef.current) {
            // 右侧视频也暂停，避免自动播放
            try {
                if (subtitleDemoRef.current.isPlaying && subtitleDemoRef.current.isPlaying()) {
                    subtitleDemoRef.current.pause();
                }
            } catch (e) {
                // 如果 isPlaying 方法不存在或出错，直接尝试暂停
                try {
                    subtitleDemoRef.current.pause();
                } catch (pauseError) {
                    // 忽略暂停错误
                }
            }
            subtitleDemoRef.current.seekToTime(timeInSeconds);
            console.log(`字幕点击跳转：直接跳转右侧视频到时间 ${timeInSeconds}秒`);
        }

        // 确保播放状态为暂停
        setIsPlaying(false);
        setSubtitlePlayEndTime(null);

        // 同步更新时间轴滚动条位置
        if (timelineEditorRef.current) {
            // 延迟更新以确保时间轴内容已更新
            setTimeout(() => {
                timelineEditorRef.current?.updateScrollbar();
            }, 100);
            setTimeout(() => {
                timelineEditorRef.current?.updateScrollbar();
            }, 300);
        }

        // 更新进度条显示
        const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
        if (progressBar && duration > 0) {
            const progressPercent = (timeInSeconds / duration) * 100;
            progressBar.style.setProperty('--progress', `${progressPercent}%`);
        }

        // 同步更新时间轴的播放头位置
        if (timelineEditorRef.current) {
            timelineEditorRef.current.currentTime = timeInSeconds;
            timelineEditorRef.current.updatePlayheadPosition();
            console.log(`字幕点击跳转：同步更新时间轴播放头到时间 ${timeInSeconds}秒`);
        }

        // 高亮对应的目标台词
        updateTargetSubtitleHighlight(timeInSeconds);

        // 将时间轴滚动到对应位置：优先使当前台词片段水平居中，否则按时间百分比滚动
        if (timelineEditorRef.current) {
            if (targetSubtitleId != null && typeof timelineEditorRef.current.scrollToSegmentCenter === 'function') {
                timelineEditorRef.current.scrollToSegmentCenter(targetSubtitleId);
            } else {
                const subs = videoData?.subtitles;
                const activeTrack = timelineEditorRef.current.activeTrack || 'translated';
                const useOriginTimes = isFromPure || (activeTrack === 'original');
                const getStart = (s: { origin_start_time: string; post_start_time?: string }) =>
                    useOriginTimes ? s.origin_start_time : (s.post_start_time || s.origin_start_time);
                const getEnd = (s: { origin_end_time: string; post_end_time?: string }) =>
                    useOriginTimes ? s.origin_end_time : (s.post_end_time || s.origin_end_time);
                const matched = subs?.find(s => {
                    const start = parseTimeToSeconds(getStart(s));
                    const end = parseTimeToSeconds(getEnd(s));
                    return timeInSeconds >= start && timeInSeconds < end;
                });
                if (matched && typeof timelineEditorRef.current.scrollToSegmentCenter === 'function') {
                    timelineEditorRef.current.scrollToSegmentCenter(matched.id);
                } else {
                    timelineEditorRef.current.scrollToTime(timeInSeconds);
                }
            }
        }

        // 清除标记：允许后续按空格/点播放时正常播当前句。键盘跳转用较短延迟，点击台词用较长延迟
        const clearDelay = fromKeyboard ? 280 : 1000;
        setTimeout(() => {
            isSubtitleClickSeekRef.current = false;
        }, clearDelay);
    };

    // 上下键跳转上下句、空格键控制播放/暂停（字幕校对页按原台词，视频编辑页按翻译后台词）
    useEffect(() => {
        const handleVideoEditKeyDown = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
            const role = (e.target as HTMLElement)?.getAttribute?.('role');
            const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || role === 'textbox' || (e.target as HTMLElement)?.isContentEditable;
            if (isEditable) return;

            const subs = videoData?.subtitles;
            if (!subs || subs.length === 0) return;

            // 选中原台词用原台词时间，选中翻译后用翻译后时间（字幕校对页仅原台词，视频编辑页按 activeTrack 区分）
            const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
            const useOriginTimes = isFromPure || (activeTrack === 'original');
            const getStart = (s: { origin_start_time: string; origin_end_time: string; post_start_time?: string; post_end_time?: string }) =>
                useOriginTimes ? s.origin_start_time : (s.post_start_time || s.origin_start_time);
            const getEnd = (s: { origin_start_time: string; origin_end_time: string; post_start_time?: string; post_end_time?: string }) =>
                useOriginTimes ? s.origin_end_time : (s.post_end_time || s.origin_end_time);

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                const now = Date.now();
                if (now - lastVideoEditKeyTimeRef.current < 100) return;
                lastVideoEditKeyTimeRef.current = now;
                const ct = sourceVideoRef.current?.currentTime ?? currentTime;
                let prevIdx: number;
                const currentId = highlightedSubtitleId ?? null;
                if (currentId != null) {
                    const idx = subs.findIndex(s => s.id === currentId);
                    prevIdx = idx < 0 ? 0 : idx <= 0 ? 0 : idx - 1;
                } else {
                    const inRangeIdx = subs.findIndex(s => {
                        const start = parseTimeToSeconds(getStart(s));
                        const end = parseTimeToSeconds(getEnd(s));
                        return ct >= start && ct < end;
                    });
                    if (inRangeIdx >= 0) {
                        prevIdx = inRangeIdx <= 0 ? 0 : inRangeIdx - 1;
                    } else {
                        let lastPast = -1;
                        for (let i = subs.length - 1; i >= 0; i--) {
                            if (parseTimeToSeconds(getEnd(subs[i])) <= ct) {
                                lastPast = i;
                                break;
                            }
                        }
                        prevIdx = lastPast < 0 ? 0 : lastPast;
                    }
                }
                const prev = subs[prevIdx];
                const timeRange = `${getStart(prev)} → ${getEnd(prev)}`;
                seekToTime(timeRange, true, prev.id);
                setHighlightedSubtitleId(prev.id);
                scrollToHighlightedSubtitle(prev.id);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const now = Date.now();
                if (now - lastVideoEditKeyTimeRef.current < 100) return;
                lastVideoEditKeyTimeRef.current = now;
                const ct = sourceVideoRef.current?.currentTime ?? currentTime;
                let nextIdx: number;
                const currentId = highlightedSubtitleId ?? null;
                if (currentId != null) {
                    const idx = subs.findIndex(s => s.id === currentId);
                    nextIdx = idx < 0 ? 0 : Math.min(idx + 1, subs.length - 1);
                } else {
                    const inRangeIdx = subs.findIndex(s => {
                        const start = parseTimeToSeconds(getStart(s));
                        const end = parseTimeToSeconds(getEnd(s));
                        return ct >= start && ct < end;
                    });
                    if (inRangeIdx >= 0) {
                        nextIdx = Math.min(inRangeIdx + 1, subs.length - 1);
                    } else {
                        const firstAhead = subs.findIndex(s => parseTimeToSeconds(getStart(s)) >= ct);
                        nextIdx = firstAhead < 0 ? subs.length - 1 : firstAhead;
                    }
                }
                const next = subs[nextIdx];
                const timeRange = `${getStart(next)} → ${getEnd(next)}`;
                seekToTime(timeRange, true, next.id);
                setHighlightedSubtitleId(next.id);
                scrollToHighlightedSubtitle(next.id);
                return;
            }
            if (e.key === ' ') {
                e.preventDefault();
                togglePlay();
            }
        };
        document.addEventListener('keydown', handleVideoEditKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleVideoEditKeyDown, { capture: true } as any);
    }, [videoData?.subtitles, highlightedSubtitleId, currentTime, isFromPure, seekToTime, togglePlay]);

    const [currentEpisodeInfo, setCurrentEpisodeInfo] = useState({
        name: '',
        sourceLanguage: '',
        targetLanguage: '',
        subtitleErase: false
    });


    // 获取左侧视频URL的函数（类似右侧视频的fetchVideoUrl）
    const fetchLeftVideoUrl = useCallback(async (): Promise<string> => {
        try {
            // 优先使用当前的videoData
            if (videoData && videoData.video_hls_url) {
                console.log('使用当前videoData中的视频URL:', videoData.video_hls_url);
                return videoData.video_hls_url;
            }
            
            // 如果没有videoData但有任务ID，则通过API获取
            if (id) {
                console.log('videoData为空，通过API获取视频数据，任务ID:', id);
                const taskData = await getTaskStatusApi(parseInt(id));
                if (taskData && taskData.video_hls_url) {
                    console.log('通过API获取到视频URL:', taskData.video_hls_url);
                    return taskData.video_hls_url;
                }
            }
            
            // 如果都没有，返回空字符串
            console.log('未找到视频URL');
            return '';
        } catch (error) {
            console.error('获取左侧视频URL失败:', error);
            return '';
        }
    }, [videoData, id]); // 保持与右侧视频相同的依赖项

    // 初始化左侧视频（类似右侧视频的initVideo）
    const initLeftVideo = useCallback(async () => {
        console.log('【initLeftVideo】开始执行，检查条件:', {
            hasVideoElement: !!sourceVideoRef.current,
            hasVideoData: !!videoData,
            hasHlsUrl: !!videoData?.video_hls_url
        });
        
        const video = sourceVideoRef.current;
        if (!video) {
            console.log('【initLeftVideo】左侧视频元素不存在，跳过初始化');
            return;
        }
        
        try {
            const url = await fetchLeftVideoUrl();
            console.log('【initLeftVideo】获取到URL:', {
                url: url,
                urlLength: url.length,
                isM3u8: url.endsWith('.m3u8')
            });
            
            if (!url) {
                console.log('【initLeftVideo】左侧视频URL为空，跳过初始化');
                return;
            }
            
            // 检查URL是否发生变化
            if (currentVideoUrlRef.current === url) {
                console.log('【initLeftVideo】左侧视频URL未变化，跳过初始化:', url);
                return;
            }
            
            console.log('【initLeftVideo】开始初始化左侧视频播放器:', url);
            currentVideoUrlRef.current = url;
            
            // 不需要清理之前的HLS实例，像右侧视频一样让浏览器自动管理
            
            // 设置视频源（像右侧视频一样，不保存HLS实例引用）
            if (url.endsWith('.m3u8')) {
                console.log('【initLeftVideo】检测到m3u8格式，开始HLS处理');
                // HLS视频流
                if (video.canPlayType('application/vnd.apple.mpegurl')) {
                    // Safari原生支持HLS
                    video.src = url;
                    console.log('【initLeftVideo】左侧视频：使用Safari原生HLS支持');
                } else if (Hls.isSupported()) {
                    // 使用HLS.js库（像右侧视频一样，不保存引用）
                    console.log('【initLeftVideo】使用HLS.js库处理m3u8');
                    const Hls = (await import('hls.js')).default;
                    const hls = new Hls();
                    hls.loadSource(url);
                    hls.attachMedia(video);
                    console.log('【initLeftVideo】左侧视频：使用HLS.js库播放HLS视频流');
                } else {
                    console.error('【initLeftVideo】左侧视频：浏览器不支持HLS视频播放');
                    video.src = url; // 尝试直接设置，可能会失败
                }
            } else {
                // 普通视频文件
                video.src = url;
                console.log('【initLeftVideo】左侧视频：播放普通视频文件');
            }
            
            // 预加载视频元数据，确保readyState >= 1
            video.preload = 'metadata';
            video.load(); // 强制加载元数据
            
            // 监听视频就绪状态
            const handleCanPlay = () => {
                console.log('【initLeftVideo】左侧视频已就绪，可以播放');
                setLeftVideoReady(true);
                video.removeEventListener('canplay', handleCanPlay);
            };
            
            video.addEventListener('canplay', handleCanPlay);
            
            console.log('【initLeftVideo】左侧视频源已设置完成:', {
                url: url,
                hlsInstance: !!hlsRef.current,
                videoSrc: video.src
            });
            
        } catch (error) {
            console.error('【initLeftVideo】左侧视频初始化失败:', error);
        }
    }, [fetchLeftVideoUrl]);

    // 初始化左侧视频（等待右侧视频初始化完成后再初始化）
    useEffect(() => {
        const init = async () => {
            // 开始初始化左侧视频
            
            // 只有在右侧视频就绪、videoData存在且有HLS URL时才初始化
            if (rightVideoReady && videoData && videoData.video_hls_url) {
                // 如果HLS实例已经存在且URL没有变化，跳过初始化
                if (hlsRef.current && currentVideoUrlRef.current === videoData.video_hls_url) {
                    console.log('【左侧视频初始化】HLS实例已存在且URL未变化，跳过初始化');
                    return;
                }
                
                console.log('【左侧视频初始化】右侧视频已就绪，videoData和HLS URL都存在，开始初始化左侧视频');
                await initLeftVideo();
            } else {
                console.log('【左侧视频初始化】条件不满足，跳过初始化:', {
                    rightVideoReady,
                    hasVideoData: !!videoData,
                    hasHlsUrl: !!videoData?.video_hls_url
                });
            }
        };
        
        init();
        
        // 不需要清理函数，像右侧视频一样让浏览器自动管理HLS实例
    }, [rightVideoReady, videoData?.video_hls_url]); // 直接依赖具体值，而不是函数

    // 提前初始化音频轨道 - 在视频数据加载完成后立即初始化
    useEffect(() => {
        if (!videoData) {
            return;
        }

        // 开始提前初始化音频轨道
        
        // 检查是否需要初始化背景音
        if (videoData.backgroud_audio_hls_url && backgroundAudioRef.current && !backgroundAudioRef.current.src) {
            // backgroud_audio_hls_url 已经在 getTaskStatusApi 中处理过了，包含 /storage 前缀
            // 如果已经是完整URL或已包含代理前缀，直接使用；否则使用 processVideoUrl 处理
            const backgroundAudioUrl = videoData.backgroud_audio_hls_url.startsWith('http://') || videoData.backgroud_audio_hls_url.startsWith('https://') || videoData.backgroud_audio_hls_url.startsWith('/storage') || videoData.backgroud_audio_hls_url.startsWith('/media')
                ? videoData.backgroud_audio_hls_url
                : processVideoUrl(videoData.backgroud_audio_hls_url);
            // 预加载背景音
            
            if (backgroundAudioUrl.includes('.m3u8')) {
                // HLS格式音频预加载
                import('hls.js').then((Hls) => {
                    if (Hls.default.isSupported()) {
                        if (backgroundHlsRef.current) {
                            backgroundHlsRef.current.destroy();
                        }
                        const hls = new Hls.default({
                            enableWorker: true,
                            lowLatencyMode: true, // 启用低延迟模式
                        });
                        backgroundHlsRef.current = hls;
                        hls.loadSource(backgroundAudioUrl);
                        hls.attachMedia(backgroundAudioRef.current!);
                        hls.on(Hls.default.Events.MANIFEST_PARSED, () => {
                            // 背景音HLS清单解析完成，音频已预加载
                        });
                    }
                });
            } else {
                // 普通音频格式预加载
                backgroundAudioRef.current.src = backgroundAudioUrl;
                backgroundAudioRef.current.load();
            }
        }


        // 翻译音频预取窗口：由 maintainPrefetchWindow 动态维护（维持后续 5 条音频就绪）
        // 这里只做一次初始化触发，避免固定 slice(0,3) 的静态预取
        if (videoData.subtitles && videoData.subtitles.length > 0) {
            maintainPrefetchWindow(sourceVideoRef.current?.currentTime || 0);
        }
    }, [videoData]);

    // 视频数据变化时重新初始化HLS
    useEffect(() => {
        // 只有在videoData和HLS URL都存在时才重新初始化
        if (!videoData || !videoData.video_hls_url) {
            // videoData或HLS URL不存在，跳过重新初始化
            return;
        }
        
        if (sourceVideoRef.current) {
            console.log('视频数据已更新，重新初始化HLS播放器');
            // 延迟一点时间确保DOM已更新
            const timer = setTimeout(() => {
                // 触发HLS重新初始化
                const event = new Event('videoDataUpdated');
                window.dispatchEvent(event);
            }, 200);
            
            return () => clearTimeout(timer);
        }
    }, [videoData?.video_hls_url]);
    // 获取视频数据
    useEffect(() => {
        const geteriesData = async () => {
            if (seriesId) {
                  // 获取剧集信息（后端分页上限为 500，这里使用 500 避免超出限制）
                  const response = await getSeriesListApi({
                    pageNo: 1,
                    pageSize: 500
                });
                // 查找匹配的剧集
                const targetSeries = (response.data?.list as any[])?.find((series: any) => series.id === parseInt(seriesId!));

                if (targetSeries) {
                    // 更新抽屉信息
                    setCurrentEpisodeInfo({
                        name: targetSeries.name,
                        sourceLanguage: targetSeries.source_lang || '',
                        targetLanguage: targetSeries.target_lang || '',
                        subtitleErase: targetSeries.need_removal_subtitle || false
                    });
                }
            }
        }


        const fetchVideoData = async () => {
            if (!id) {
                console.warn('视频ID不存在');
                return;
            }

            try {
                setIsLoadingVideo(true);
                // 调用新的任务状态接口
                const taskData = await getTaskStatusApi(parseInt(id));
                
                // 确保返回的数据ID与当前ID一致
                if (taskData.id !== parseInt(id)) {
                    console.warn(`API返回的ID(${taskData.id})与当前ID(${id})不一致，强制设置为当前ID`);
                    taskData.id = parseInt(id);
                }
                
                // 使用任务级 volumeGainDb 初始化背景音量（如果后端有返回）
                const taskVolume = (taskData as any).volumeGainDb;
                if (typeof taskVolume === 'number') {
                    setBackgroundVolume(taskVolume);
                    await ensureBackgroundGainNode();
                    if (backgroundGainNodeRef.current) {
                        backgroundGainNodeRef.current.gain.value = taskVolume;
                    }
                }

                // 获取字幕列表（独立接口）
                let subtitlesLegacy: Task.SubtitlesLegacy[] = [];
                try {
                    const subtitlesResponse = await getSubtitlesListApi(parseInt(id));
                    if (subtitlesResponse.code === 0 && subtitlesResponse.data) {
                        // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，保持兼容性）
                        subtitlesLegacy = subtitlesResponse.data.map((subtitle: Task.Subtitles) => ({
                            task_id: subtitle.taskId,
                            origin_text: subtitle.originText,
                            post_text: subtitle.postText || '',
                            post_start_time: subtitle.postStartTime || '',
                            post_end_time: subtitle.postEndTime || '',
                            origin_start_time: subtitle.originStartTime,
                            origin_end_time: subtitle.originEndTime,
                            origin_post_time: subtitle.originPostTime,
                            is_episode_voice: subtitle.isEpisodeVoice || false,
                            origin_audio: subtitle.originAudio,
                            trans_audio: subtitle.transAudio || '',
                            subtitle_id: subtitle.subtitleId || subtitle.id,
                            score: subtitle.score,
                            audio_speed: subtitle.audioSpeed || 1.0,
                            volume_gain_db: subtitle.volumeGainDb || 1.0,
                            id: subtitle.id,
                            speakers: {
                                id: subtitle.speaker.id,
                                series_id: subtitle.speaker.seriesId,
                                task_id: subtitle.taskId,
                                speaker_name: subtitle.speaker.speakerName,
                                description: subtitle.speaker.description || '',
                                is_series_speaker: subtitle.speaker.isSeriesSpeaker
                            },
                            voice_info: {
                                origin_text: subtitle.originText,
                                origin_start_time: subtitle.originStartTime,
                                origin_end_time: subtitle.originEndTime,
                                origin_audio: subtitle.originAudio,
                                score: subtitle.score
                            },
                            back_translate_text: subtitle.backTranslateText || '',
                            words: subtitle.words?.map(word => ({
                                id: word.id,
                                subtitle_id: word.subtitleId,
                                position: word.position,
                                origin_char: word.originChar,
                                start_ms: word.startMs,
                                end_ms: word.endMs
                            })) || [],
                            notes: subtitle.remark || undefined // 兼容旧字段名（新接口使用 remark）
                        }));
                        // 成功获取字幕列表
                    }
                } catch (subtitleError) {
                    console.warn('【fetchVideoData】获取字幕列表失败:', subtitleError);
                }
                
                // 将字幕数据添加到 taskData 中（兼容旧代码）
                (taskData as any).subtitles = subtitlesLegacy;
                
                setVideoDataSafe(taskData);
                
                // 获取说话人列表
                const speakerRoles = await fetchSpeakersList(parseInt(id));
                
                // 如果API获取说话人列表失败，则从字幕数据中提取说话人信息作为备用方案
                if (speakerRoles.length === 0 && subtitlesLegacy.length > 0) {
                    const fallbackSpeakerRoles = extractSpeakersFromSubtitles(subtitlesLegacy);
                    const finalFallbackRoles = deduplicateRoles(fallbackSpeakerRoles);
                    setRoles(finalFallbackRoles);
                    console.log('🔄 使用备用方案设置说话人列表，数量:', finalFallbackRoles.length);
                }
                
                
                // 初始化每行的角色选择，使用字幕数据中的说话人
                if (subtitlesLegacy.length > 0) {
                    const initialRowRoles: { [key: number]: string } = {};
                    subtitlesLegacy.forEach(subtitle => {
                        if (subtitle.speakers && subtitle.speakers.speaker_name) {
                            initialRowRoles[subtitle.id] = subtitle.speakers.speaker_name;
                        }
                    });
                    setRowSelectedRoles(initialRowRoles);
                    // console.log('初始化角色选择:', initialRowRoles);
                }
            } catch (error: any) {
                // 构建详细的错误信息
                let errorMessage = '获取视频数据失败';
                let consoleMessage = '获取视频数据失败:';

                if (error?.status) {
                    errorMessage += ` (状态码: ${error.status})`;
                    consoleMessage += `\n状态码: ${error.status}`;
                }
                if (error?.statusText) {
                    consoleMessage += `\n状态文本: ${error.statusText}`;
                }
                if (error?.response?.message) {
                    errorMessage += ` - ${error.response.message}`;
                    consoleMessage += `\n服务器消息: ${error.response.message}`;
                }
                if (error?.response) {
                    consoleMessage += `\n响应数据: ${JSON.stringify(error.response)}`;
                }
                if (error?.message) {
                    errorMessage += ` - ${error.message}`;
                    consoleMessage += `\n错误消息: ${error.message}`;
                }
                consoleMessage += `\n请求URL: /translate/api/task/status/${id}`;

                // 在控制台输出详细错误信息
                console.error(consoleMessage);
                console.error('完整错误对象:', error);

                // 在页面上显示错误信息
                showError(errorMessage);

                // 如果API调用失败，使用默认数据
                const currentId = parseInt(id);
                const fallbackData: Task.GetTaskStatusResult = {
                    name: videoName,
                    disable_bgm: false,
                    has_user_srt: false,
                    has_user_script: false,
                    tag: 0,
                    descript: null,
                    id: currentId, // 确保使用当前正确的ID
                    series_id: 0,
                    user_id: 1,
                    status: 2,
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    file_url: "",
                    result_url: "",
                    translated_subtitle_url: "",
                    subtitle_url: "",
                    pure_video_url: "",
                    pure_video_hls_url: "",
                    video_hls_url: "",
                    cover_url: "",
                    backgroud_audio_url: "",
                    translated_vocals_hls_url: "",
                    backgroud_audio_hls_url: "",
                    vocals_url: "",
                    vocals_hls_url: "",
                    script_url: null,
                    subtitles: [],
                    // TaskStatus 必需属性
                    coverUrl: null,
                    fileUrl: "",
                    subtitleUrl: null,
                    videoHlsUrl: null,
                    pureVideoHlsUrl: null,
                    vocalsHlsUrl: null,
                    backgroudAudioHlsUrl: null,
                    hasUserSrt: false,
                    scriptUrl: null,
                    hasUserScript: false,
                    episodeIndex: 0,
                    langCode: "",
                    pureVideoUrl: "",
                    resultUrl: null,
                    duration: null,
                    createTime: new Date().toISOString(),
                    updateTime: new Date().toISOString(),
                    disableBgm: false
                };
                console.log(`使用回退数据，确保ID为: ${currentId}`, fallbackData);
                setVideoDataSafe(fallbackData);
            } finally {
                setIsLoadingVideo(false);
            }
        };

        geteriesData();
        fetchVideoData();
    }, [id, seriesId]); // 移除videoName依赖，避免location.state变化导致重复调用


    // 字幕数据缓存，避免重复计算
    const [lastSubtitlesHash, setLastSubtitlesHash] = useState<string>('');

    // 根据API数据同步字幕 - 优化版本，避免不必要的重新计算
    useEffect(() => {
        try {
            // 从新的数据结构中获取字幕数据：直接访问subtitles
            const subtitles = videoData?.subtitles;
            
            if (subtitles && Array.isArray(subtitles)) {
                // 计算字幕数据的哈希值，避免重复计算
                // 注意：必须包含 subtitle_id，因为更换音色后会改变 subtitle_id
                const subtitlesHash = JSON.stringify(subtitles.map(sub => ({
                    id: sub.id,
                    subtitle_id: sub.subtitle_id, // 添加 subtitle_id 到哈希计算中
                    origin_text: sub.origin_text,
                    post_text: sub.post_text,
                    origin_start_time: sub.origin_start_time,
                    origin_end_time: sub.origin_end_time,
                    post_start_time: sub.post_start_time,
                    post_end_time: sub.post_end_time
                })));

                // 如果字幕数据没有变化，跳过重新计算
                if (subtitlesHash === lastSubtitlesHash) {
                    // 字幕数据哈希值未变化，跳过更新
                    return;
                }
                
                // 字幕数据哈希值已变化，开始更新 sourcesText 和 targetText
                

                const startTime = performance.now();

                // 生成目标语言字幕数据（使用post_text作为翻译后的文本）
                // 优先使用targetText状态中的数据，避免丢失用户编辑的内容
                const generatedTargetText = subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
                    // 查找当前字幕在targetText状态中是否有用户编辑的数据
                    const existingTargetData = targetText.find(item => item.id === subtitle.id);
                    
                    return {
                        id: subtitle.id, // 确保使用真实的字幕ID，不使用fallback
                        subtitle_id: subtitle.subtitle_id, // 添加subtitle_id字段用于音色相关操作
                        // 优先使用targetText状态中的数据，如果不存在则使用subtitle.post_text
                        text: existingTargetData?.text || subtitle.post_text || '',
                        timeRange: `${subtitle.post_start_time || subtitle.origin_start_time} → ${subtitle.post_end_time || subtitle.origin_end_time}`
                    };
                });

                setTargetText(generatedTargetText);
                
                // 调试：输出更新后的 subtitle_id 信息
                console.log('✅ targetText 已更新，subtitle_id 映射:', 
                    generatedTargetText.map(item => ({ id: item.id, subtitle_id: item.subtitle_id }))
                );

                // 同时更新源语言字幕数据（使用origin_text作为原始文本）
                // 优先使用sourcesText状态中的数据，避免丢失用户编辑的内容
                const generatedSourceText = subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
                    // 查找当前字幕在sourcesText状态中是否有用户编辑的数据
                    const existingSourceData = sourcesText.find(item => item.id === subtitle.id);
                    
                    return {
                        id: subtitle.id, // 确保使用真实的字幕ID，不使用fallback
                        subtitle_id: subtitle.subtitle_id, // 添加subtitle_id字段用于音色相关操作
                        task_id: subtitle.task_id, // 添加task_id字段
                        speaker_id: subtitle.speakers?.id || 0, // 添加speaker_id字段
                        // 优先使用sourcesText状态中的数据，如果不存在则使用subtitle.origin_text
                        text: existingSourceData?.text || subtitle.origin_text || '',
                        timeRange: `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`
                    };
                });

                setSourcesText(generatedSourceText);
                
                // 调试：输出更新后的 subtitle_id 信息
                // sourcesText 已更新
                
                // 使用generatedTargetText和generatedSourceText的数据重新同步videoData.subtitles
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    
                    const updatedSubtitles = prev.subtitles.map((subtitle: Task.SubtitlesLegacy) => {
                        // 查找generatedTargetText和generatedSourceText中对应的数据
                        const targetData = generatedTargetText.find(item => item.id === subtitle.id);
                        const sourceData = generatedSourceText.find(item => item.id === subtitle.id);
                        
                        return {
                            ...subtitle,
                            // 使用generatedTargetText和generatedSourceText中的数据更新videoData.subtitles
                            post_text: targetData?.text || subtitle.post_text || '',
                            origin_text: sourceData?.text || subtitle.origin_text || ''
                        };
                    });
                    
                    // 使用generatedTargetText和generatedSourceText重新同步videoData.subtitles
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });
                
                // 字幕同步useEffect重新计算后 - 时间轴所有数据已更新
                
                // 更新时间轴显示，使用重新同步后的videoData.subtitles数据
                if (timelineEditorRef.current) {
                    // 重新生成时间轴片段，使用重新同步后的videoData.subtitles数据
                    timelineEditorRef.current.generateSegmentsFromSubtitles(subtitles);
                }
                
                // 更新缓存哈希值
                setLastSubtitlesHash(subtitlesHash);
                
                const endTime = performance.now();
            }

        } catch (error) {
            console.error('字幕数据同步出错:', error);
            // 如果出错，保持默认数据不变
        }
    }, [videoData?.subtitles, seriesId, lastSubtitlesHash]); // 添加lastSubtitlesHash依赖

    // 如果没有seriesId，尝试从视频数据的series_id获取语言信息
    useEffect(() => {
        const fetchSeriesLanguageInfo = async () => {
            // 如果有seriesId，语言信息已经在geteriesData中获取，不需要再次获取
            if (seriesId) {
                return;
            }
            
            // 如果没有videoData或series_id，无法获取语言信息
            if (!videoData || !videoData.series_id) {
                return;
            }
            
            try {
                // 从API获取剧集信息以获取语言信息（分页上限 500）
                const response = await getSeriesListApi({
                    pageNo: 1,
                    pageSize: 500
                });
                
                // 查找匹配的剧集
                const targetSeries = (response.data?.list as any[])?.find((series: any) => series.id === videoData.series_id);
                
                if (targetSeries) {
                    // 更新语言信息和剧集名称
                    setCurrentEpisodeInfo(prev => ({
                        ...prev,
                        name: targetSeries.name || prev.name,
                        sourceLanguage: targetSeries.source_lang || prev.sourceLanguage || 'xx',
                        targetLanguage: targetSeries.target_lang || prev.targetLanguage || 'xx',
                        subtitleErase: targetSeries.need_removal_subtitle !== undefined 
                            ? targetSeries.need_removal_subtitle 
                            : prev.subtitleErase
                    }));
                } else {
                    // 如果找不到剧集，使用默认值
                    setCurrentEpisodeInfo(prev => ({
                        ...prev,
                        sourceLanguage: prev.sourceLanguage || 'xx',
                        targetLanguage: prev.targetLanguage || 'xx'
                    }));
                }
            } catch (error) {
                console.error('获取剧集语言信息失败:', error);
                // 如果API调用失败，使用默认值
                setCurrentEpisodeInfo(prev => ({
                    ...prev,
                    sourceLanguage: prev.sourceLanguage || 'xx',
                    targetLanguage: prev.targetLanguage || 'xx'
                }));
            }
        };
        
        fetchSeriesLanguageInfo();
    }, [videoData?.series_id, seriesId]); // 依赖videoData.series_id和seriesId

    // 监听字幕数据变化，更新时间轴片段
    useEffect(() => {
        if (timelineEditorRef.current && videoData?.subtitles && videoData.subtitles.length > 0) {
            console.log('字幕数据变化，更新时间轴片段:', videoData.subtitles.length, '个字幕');
            timelineEditorRef.current.initSubtitles(videoData.subtitles);
        }
    }, [videoData?.subtitles]);


    // 监听视频播放状态变化
    useEffect(() => {
        const sourceVideo = sourceVideoRef.current;
        if (!sourceVideo) return;

        const handlePlay = () => {
            console.log('🎬 视频播放事件触发');
            setIsPlaying(true);
            
            // 启动黑色刻度线动画循环
            console.log('🔍 检查动画启动条件:', {
                hasTimelineEditor: !!timelineEditorRef.current,
                hasAnimationId: !!timelineEditorRef.current?.playheadAnimationId,
                hasSourceVideo: !!sourceVideoRef.current
            });
            
            if (timelineEditorRef.current && !timelineEditorRef.current.playheadAnimationId) {
                console.log('🚀 视频播放，启动黑色刻度线动画循环');
                const animatePlayhead = () => {
                    if (timelineEditorRef.current && sourceVideoRef.current && !sourceVideoRef.current.paused) {
                        // 实时获取视频当前时间
                        const realTime = sourceVideoRef.current.currentTime || 0;
                        // 若存在单句播放的结束时间，则进行时间夹紧，并在到达时立刻停止，避免越界后回跳
                        if (subtitlePlayEndTime !== null && realTime + END_EPSILON >= subtitlePlayEndTime) {
                            // 立刻将视频与播放头固定在结束时间
                            try { sourceVideoRef.current.pause(); } catch {}
                            timelineEditorRef.current.currentTime = subtitlePlayEndTime;
                            if (typeof (timelineEditorRef.current as any).updatePlayheadPositionOptimized === 'function') {
                                (timelineEditorRef.current as any).updatePlayheadPositionOptimized();
                            } else {
                                timelineEditorRef.current.updatePlayheadPosition();
                            }
                            // 终止动画循环
                            timelineEditorRef.current.playheadAnimationId = undefined;
                            return;
                        }
                        
                        timelineEditorRef.current.currentTime = realTime;
                        if (typeof (timelineEditorRef.current as any).updatePlayheadPositionOptimized === 'function') {
                            (timelineEditorRef.current as any).updatePlayheadPositionOptimized();
                        } else {
                            timelineEditorRef.current.updatePlayheadPosition();
                        }
                        timelineEditorRef.current.playheadAnimationId = requestAnimationFrame(animatePlayhead);
                    } else {
                        console.log('⏹️ 停止黑色刻度线动画循环');
                        if (timelineEditorRef.current) {
                            timelineEditorRef.current.playheadAnimationId = undefined;
                        }
                    }
                };
                timelineEditorRef.current.playheadAnimationId = requestAnimationFrame(animatePlayhead);
            } else {
                console.log('❌ 无法启动动画循环:', {
                    hasTimelineEditor: !!timelineEditorRef.current,
                    hasAnimationId: !!timelineEditorRef.current?.playheadAnimationId
                });
            }
            
            // 如果不是用户手动操作，则确保音频播放并校对
            if (!userInteractionRef.current) {
                // console.log('视频自动播放：确保音频播放并校对');
                ensureAudioPlayAndSync();
            }
            // 重置用户操作标记
            userInteractionRef.current = false;
        };

        const handlePause = () => {
            setIsPlaying(false);
            
            // 停止黑色刻度线动画
            if (timelineEditorRef.current && timelineEditorRef.current.playheadAnimationId) {
                cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                timelineEditorRef.current.playheadAnimationId = undefined;
            }
            
            // 如果不是用户手动操作，则暂停音频
            if (!userInteractionRef.current) {
                // console.log('视频自动暂停：暂停音频');
                if (backgroundAudioRef.current) {
                    backgroundAudioRef.current.pause();
                }
                // 停止正在播放的翻译音频
                if (transAudioRef.current) {
                    transAudioRef.current.pause();
                    transAudioRef.current.currentTime = 0;
                    setCurrentTransAudio(null);
                }
                // 停止原始台词音频
                if (originAudioRef.current) {
                    originAudioRef.current.pause();
                    originAudioRef.current.currentTime = 0;
                }
            }
            // 无论是否是用户手动操作，都要停止原人声音频
            // 确保在所有暂停情况下都能停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
            // 重置用户操作标记
            userInteractionRef.current = false;
        };

        const handleEnded = () => {
            setIsPlaying(false);
            
            // 停止黑色刻度线动画
            if (timelineEditorRef.current && timelineEditorRef.current.playheadAnimationId) {
                cancelAnimationFrame(timelineEditorRef.current.playheadAnimationId);
                timelineEditorRef.current.playheadAnimationId = undefined;
            }
            
            // 视频结束时暂停音频
            // console.log('视频播放结束：暂停音频');
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
            }
            // 停止正在播放的翻译音频
            if (transAudioRef.current) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
                setCurrentTransAudio(null);
            }
            // 停止原始台词音频
            if (originAudioRef.current) {
                originAudioRef.current.pause();
                originAudioRef.current.currentTime = 0;
            }
            // 停止原人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
            }
            if (vocalsHlsRef.current) {
                vocalsHlsRef.current.destroy();
                vocalsHlsRef.current = null;
            }
        };

        // 事件监听器已移到React的onPlay/onPause属性中，这里不再需要
        // sourceVideo.addEventListener('play', handlePlay);
        // sourceVideo.addEventListener('pause', handlePause);
        sourceVideo.addEventListener('ended', handleEnded);

        return () => {
            // sourceVideo.removeEventListener('play', handlePlay);
            // sourceVideo.removeEventListener('pause', handlePause);
            sourceVideo.removeEventListener('ended', handleEnded);
            
            // 清理定时器
            if (timeUpdateTimeoutRef.current) {
                clearTimeout(timeUpdateTimeoutRef.current);
            }
        };
    }, []);

    // Canvas尺寸调整和窗口大小变化监听
    useEffect(() => {
        const handleResize = () => {
            resizeCanvas();
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // 页面重新可见时重新调整Canvas尺寸
                setTimeout(resizeCanvas, 100);
            }
        };

        // 监听多种事件
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // 监听视频尺寸变化
        // 注意：右侧视频现在由SubtitleDemo组件管理，不需要在这里监听尺寸变化
        // console.log('右侧视频由SubtitleDemo组件管理，不需要监听尺寸变化');
        
        // 初始调整
        const timer = setTimeout(() => {
            resizeCanvas();
        }, 500);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // 注意：右侧视频现在由SubtitleDemo组件管理，不需要在这里清理事件监听器
            clearTimeout(timer);
            
            // 清理音频等待定时器
            if (audioReadyTimeoutRef.current) {
                clearTimeout(audioReadyTimeoutRef.current);
            }
        };
    }, [videoData, rightVideoAspect]);

    // 组件卸载时清理所有音频（处理浏览器回退等情况）
    useEffect(() => {
        return () => {
            // 组件卸载时强制停止所有音频播放实例
            // 1. 停止翻译音频
            if (transAudioRef.current) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
                transAudioRef.current.src = '';
            }
            
            // 2. 停止原始音频
            if (originAudioRef.current) {
                originAudioRef.current.pause();
                originAudioRef.current.currentTime = 0;
                originAudioRef.current.src = '';
            }
            
            // 3. 停止背景音频
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current.currentTime = 0;
                backgroundAudioRef.current.src = '';
            }
            
            // 4. 停止人声音频
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
                vocalsAudioRef.current.currentTime = 0;
                vocalsAudioRef.current.src = '';
            }
            
            // 5. 停止源视频
            if (sourceVideoRef.current) {
                sourceVideoRef.current.pause();
            }
            
            // 6. 停止字幕演示
            if (subtitleDemoRef.current) {
                try {
                    subtitleDemoRef.current.pause();
                } catch (e) {
                    // 忽略错误
                }
            }
            
            // 7. 停止当前播放实例（包括 AudioManager 管理的音频）
            if (currentPlayingInstanceRef.current) {
                const instance = currentPlayingInstanceRef.current;
                if (instance.type === 'target' && instance.audioId) {
                    try {
                        // 使用 AudioManager 停止音频
                        if (typeof audioManager !== 'undefined' && audioManager.stopSound) {
                            audioManager.stopSound(instance.audioId);
                            audioManager.destroySound(instance.audioId);
                            console.log('页面卸载：已停止 AudioManager 音频', instance.audioId);
                        }
                    } catch (error) {
                        console.error('页面卸载：停止 AudioManager 失败', error);
                    }
                }
                currentPlayingInstanceRef.current = null;
            }
            
            // ================== 【新增】开始 ==================
            // 8. 🔥【修改这里】优先检查全局变量：暴力停止全局音频
            // 全局变量不受 React 生命周期和异步等待的影响，即使组件卸载也能找到音频 ID
            if (GLOBAL_PLAYING_AUDIO_ID) {
                try {
                    if (typeof audioManager !== 'undefined') {
                        console.log('页面卸载：暴力停止全局音频:', GLOBAL_PLAYING_AUDIO_ID);
                        audioManager.stopSound(GLOBAL_PLAYING_AUDIO_ID);
                        audioManager.destroySound(GLOBAL_PLAYING_AUDIO_ID);
                    }
                } catch (e) {
                    console.error('页面卸载：停止全局音频失败', e);
                }
                GLOBAL_PLAYING_AUDIO_ID = null; // 停止后重置
            }
            
            // 9. 最后的保障：停止并销毁所有 AudioManager 管理的音频实例
            // 确保即使 currentPlayingInstanceRef 为 null，也能停止所有音频
            try {
                if (typeof audioManager !== 'undefined') {
                    // 先使用全局停止方法停止所有音频
                    if (audioManager.stopAll) {
                        audioManager.stopAll();
                        console.log('页面卸载：已停止所有 AudioManager 音频');
                    }
                    // 然后销毁所有音频实例
                    if (audioManager.destroyAllSounds) {
                        audioManager.destroyAllSounds();
                        console.log('页面卸载：已销毁所有 AudioManager 音频实例');
                    }
                }
            } catch (error) {
                console.error('页面卸载：停止/销毁所有 AudioManager 音频失败', error);
            }
            
            // 最激进的停止方法：直接操作 DOM 中的所有 audio 元素
            try {
                const allAudioElements = document.querySelectorAll('audio');
                allAudioElements.forEach((audio: HTMLAudioElement) => {
                    try {
                        audio.pause();
                        audio.currentTime = 0;
                        audio.src = '';
                    } catch (error) {
                        // 忽略错误
                    }
                });
                console.log('页面卸载：已停止所有 DOM audio 元素');
            } catch (error) {
                console.error('页面卸载：停止 DOM audio 元素失败', error);
            }
            // ================== 【新增】结束 ==================
            
            // 9. 清理定时器
            if (subtitlePlayTimeoutRef.current) {
                clearTimeout(subtitlePlayTimeoutRef.current);
                subtitlePlayTimeoutRef.current = null;
            }
        };
    }, []); // 空依赖数组，确保只在组件卸载时执行

    // ================== 【新增】开始 ==================
    // 监听浏览器回退事件，确保回退时停止所有音频
    useEffect(() => {
        const handlePopState = () => {
            // 浏览器回退时，立即停止所有音频
            
            // ================== 【必须加】优先检查全局变量：浏览器回退时，也检查全局变量 ==================
            if (GLOBAL_PLAYING_AUDIO_ID) {
                try {
                    if (typeof audioManager !== 'undefined') {
                        console.log('浏览器回退：暴力停止全局音频:', GLOBAL_PLAYING_AUDIO_ID);
                        audioManager.stopSound(GLOBAL_PLAYING_AUDIO_ID);
                        audioManager.destroySound(GLOBAL_PLAYING_AUDIO_ID);
                    }
                } catch (e) {
                    console.error('浏览器回退：停止全局音频失败', e);
                }
                GLOBAL_PLAYING_AUDIO_ID = null; // 停止后重置
            }
            // ================== 【必须加】结束 ==================
            
            try {
                if (typeof audioManager !== 'undefined') {
                    // 先使用全局停止方法停止所有音频
                    if (audioManager.stopAll) {
                        audioManager.stopAll();
                        console.log('浏览器回退：已停止所有 AudioManager 音频');
                    }
                    // 然后销毁所有音频实例
                    if (audioManager.destroyAllSounds) {
                        audioManager.destroyAllSounds();
                        console.log('浏览器回退：已销毁所有 AudioManager 音频实例');
                    }
                }
            } catch (error) {
                console.error('浏览器回退：停止/销毁所有 AudioManager 音频失败', error);
            }
            
            // 最激进的停止方法：直接操作 DOM 中的所有 audio 元素
            try {
                const allAudioElements = document.querySelectorAll('audio');
                allAudioElements.forEach((audio: HTMLAudioElement) => {
                    try {
                        audio.pause();
                        audio.currentTime = 0;
                        audio.src = '';
                    } catch (error) {
                        // 忽略错误
                    }
                });
                console.log('浏览器回退：已停止所有 DOM audio 元素');
            } catch (error) {
                console.error('浏览器回退：停止 DOM audio 元素失败', error);
            }
            
            // 停止所有原生音频
            if (transAudioRef.current) {
                transAudioRef.current.pause();
                transAudioRef.current.currentTime = 0;
                transAudioRef.current.src = '';
            }
            if (originAudioRef.current) {
                originAudioRef.current.pause();
                originAudioRef.current.currentTime = 0;
                originAudioRef.current.src = '';
            }
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current.currentTime = 0;
            }
            if (vocalsAudioRef.current) {
                vocalsAudioRef.current.pause();
                vocalsAudioRef.current.currentTime = 0;
            }
            
            // 清除播放状态
            setIsPlaying(false);
            setSubtitlePlayEndTime(null);
            currentPlayingInstanceRef.current = null;
        };

        // 监听 popstate 事件（浏览器回退/前进）
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);
    // ================== 【新增】结束 ==================

    // 返回按钮处理函数 - 返回到视频列表页
    const handleBack = () => {
        // 立即清除页面内播放的所有音频，不要播放任何音频
        
        // ================== 【必须加】优先检查全局变量：点击返回按钮时，也检查全局变量 ==================
        if (GLOBAL_PLAYING_AUDIO_ID) {
            try {
                if (typeof audioManager !== 'undefined') {
                    console.log('返回按钮：暴力停止全局音频:', GLOBAL_PLAYING_AUDIO_ID);
                    audioManager.stopSound(GLOBAL_PLAYING_AUDIO_ID);
                    audioManager.destroySound(GLOBAL_PLAYING_AUDIO_ID);
                }
            } catch (e) {
                console.error('返回按钮：停止全局音频失败', e);
            }
            GLOBAL_PLAYING_AUDIO_ID = null; // 停止后重置
        }
        // ================== 【必须加】结束 ==================
        
        // ================== 【修复】开始 ==================
        // 优先停止 AudioManager 管理的所有音频（Howler.js）
        // 使用 stopAll() 和 destroyAllSounds() 确保所有音频都被停止
        try {
            if (typeof audioManager !== 'undefined') {
                // 先使用全局停止方法停止所有音频
                if (audioManager.stopAll) {
                    audioManager.stopAll();
                    console.log('返回按钮：已停止所有 AudioManager 音频');
                }
                // 然后销毁所有音频实例
                if (audioManager.destroyAllSounds) {
                    audioManager.destroyAllSounds();
                    console.log('返回按钮：已销毁所有 AudioManager 音频实例');
                }
            }
        } catch (error) {
            console.error('返回按钮：停止/销毁所有 AudioManager 音频失败', error);
        }
        
        // 同时尝试停止特定的音频实例（三重保障）
        const instance = currentPlayingInstanceRef.current;
        if (instance && instance.type === 'target' && instance.audioId) {
            try {
                audioManager.stopSound(instance.audioId);
                audioManager.destroySound(instance.audioId);
                console.log('返回按钮：已停止特定 AudioManager 音频', instance.audioId);
            } catch (error) {
                // 忽略错误，因为 stopAll 和 destroyAllSounds 已经处理了
            }
        }
        
        // 最激进的停止方法：直接操作 DOM 中的所有 audio 元素
        try {
            const allAudioElements = document.querySelectorAll('audio');
            allAudioElements.forEach((audio: HTMLAudioElement) => {
                try {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.src = '';
                } catch (error) {
                    // 忽略错误
                }
            });
            console.log('返回按钮：已停止所有 DOM audio 元素');
        } catch (error) {
            console.error('返回按钮：停止 DOM audio 元素失败', error);
        }
        // ================== 【修复】结束 ==================
        
        // 停止翻译音频
        stopTransAudio();
        // 停止原始音频
        stopOriginAudio();
        // 停止当前播放实例
        stopCurrentPlayingInstance();
        // 停止背景音频
        if (backgroundAudioRef.current && !backgroundAudioRef.current.paused) {
            backgroundAudioRef.current.pause();
            backgroundAudioRef.current.currentTime = 0;
        }
        // 停止人声音频
        if (vocalsAudioRef.current && !vocalsAudioRef.current.paused) {
            vocalsAudioRef.current.pause();
            vocalsAudioRef.current.currentTime = 0;
        }
        // 停止源视频
        if (sourceVideoRef.current && !sourceVideoRef.current.paused) {
            sourceVideoRef.current.pause();
        }
        // 停止字幕演示
        if (subtitleDemoRef.current) {
            try { 
                subtitleDemoRef.current.pause(); 
            } catch (e) {}
        }
        // 清除播放状态
        setIsPlaying(false);
        setSubtitlePlayEndTime(null);
        
        // ================== 【新增】开始 ==================
        // 使用 setTimeout 确保音频停止操作完成后再导航
        // 这可以确保音频真正停止，而不是在导航过程中继续播放
        setTimeout(() => {
            // 如果有 seriesId 参数，带上它和剧集名称导航到对应的视频列表页
            const finalSeriesId = seriesId || videoData?.series_id;
            if (finalSeriesId) {
                const seriesNameParam = currentEpisodeInfo.name 
                    ? `&seriesName=${encodeURIComponent(currentEpisodeInfo.name)}` 
                    : '';
                // 保留语种参数（从多语种视频列表进入时必须带上，否则返回后标题与列表语种会丢失）
                const langCodeParam = urlLangCode ? `&langCode=${encodeURIComponent(urlLangCode)}` : '';
                // 如果是从纯净版进入的，返回时带上 isPure=true 参数
                const isPureParam = isFromPure ? '&isPure=true' : '';
                navigate(`/videoList?seriesId=${finalSeriesId}${seriesNameParam}${langCodeParam}${isPureParam}`);
            } else {
                // 否则导航到默认的视频列表页
                navigate('/videoList');
            }
        }, 50); // 50ms 延迟，确保音频停止操作完成
        // ================== 【新增】结束 ==================
    };

    // 纯净版“已校对”按钮处理函数
    const handleProofread = async () => {
        if (!videoData?.id) {
            showError('任务ID不存在，无法提交校对状态');
            return;
        }
        try {
            setIsGenerating(true);
            const res = await proofreadApi(videoData.id);
            if (res.code === 0) {
                showSuccess('已校对');
                // 接口成功后返回视频列表页
                handleBack();
            } else {
                showError(res.msg || '校对失败，请稍后重试');
            }
        } catch (error: any) {
            console.error('纯净版校对接口调用失败:', error);
            showError(error?.message || '校对失败，请稍后重试');
        } finally {
            setIsGenerating(false);
        }
    };

    // 生成按钮处理函数
    const handleGenerate = async () => {
        if (!videoData || !videoData.id) {
            showError('视频数据不存在，无法生成');
            return;
        }

        // 防止重复点击
        if (isGenerating) {
            return;
        }

        // 检查是否有目标台词但译文为空
        const emptyTargetSubtitles = targetText.filter(item => !item.text || !item.text.trim());
        if (emptyTargetSubtitles.length > 0) {
            showError('检测到部分句子缺少译文，请重新翻译配音后合成');
            return;
        }

        try {
            setIsGenerating(true);
            console.log('开始生成视频，任务ID:', videoData.id);
            
            // 调用新的生成视频接口
            const response = await generateVideoApi(videoData.id);
            
            if (response.code === 0) {
                console.log('视频生成成功');
                
                // 将提示信息存储到localStorage，在页面切换后显示
                localStorage.setItem('videoGenerateAlert', JSON.stringify({
                    type: 'success',
                    message: '合成成功',
                    timestamp: Date.now()
                }));
                
                // 请求完成后跳转到对应的视频列表页（与返回按钮逻辑保持一致）
                const finalSeriesId = seriesId || videoData?.series_id;
                if (finalSeriesId) {
                    const seriesNameParam = currentEpisodeInfo.name 
                        ? `&seriesName=${encodeURIComponent(currentEpisodeInfo.name)}` 
                        : '';
                    const langCodeParam = urlLangCode ? `&langCode=${encodeURIComponent(urlLangCode)}` : '';
                    // 如果是从纯净版进入的，返回时带上 isPure=true 参数
                    const isPureParam = isFromPure ? '&isPure=true' : '';
                    navigate(`/videoList?seriesId=${finalSeriesId}${seriesNameParam}${langCodeParam}${isPureParam}`);
                } else {
                    // 否则导航到默认的视频列表页
                    navigate('/videoList');
                }
            } else {
                console.error('视频生成失败:', response.msg);
                showError(response.msg || '视频生成失败，请重试');
                setIsGenerating(false);
            }
                
        } catch (error: any) {
            console.error('生成视频失败:', error);
            // 提取错误信息，优先使用 API 返回的 msg
            const errorMsg = error?.response?.data?.msg || 
                           error?.response?.msg || 
                           error?.msg || 
                           error?.message || 
                           '视频生成失败，请重试';
            showError(errorMsg);
            setIsGenerating(false);
        }
    };

    // 加载视频数据函数
    const loadVideoData = async () => {
        if (!id) return;
        
        try {
            setIsLoadingVideo(true);
            const taskData = await getTaskStatusApi(parseInt(id));
            setVideoData(taskData);
        } catch (error) {
            console.error('加载视频数据失败:', error);
            showError('加载视频数据失败，请重试');
        } finally {
            setIsLoadingVideo(false);
        }
    };

    // 全局重新翻译处理
    const handleGlobalRetranslate = async (customText?: string) => {
        if (!videoData || !videoData.subtitles || isGlobalRetranslating) {
            return;
        }

        setIsGlobalRetranslating(true);
        setShowGlobalRetranslateDropdown(false);
        
        // 步骤1: 过滤出目标台词(postText)为空的台词，创建触发列表
        const subtitlesToTranslate: Array<{
            subtitle: Task.SubtitlesLegacy;
            index: number;
        }> = [];
        
        videoData.subtitles.forEach((subtitle, index) => {
            // 判断目标台词是否为空（post_text为空、null、undefined或只包含空白字符）
            const postText = subtitle.post_text || '';
            const isEmpty = !postText.trim();
            
            if (isEmpty) {
                subtitlesToTranslate.push({
                    subtitle,
                    index
                });
            }
        });
        
        // 如果没有需要翻译的台词，直接返回
        if (subtitlesToTranslate.length === 0) {
            setIsGlobalRetranslating(false);
            showSuccess('所有台词都已翻译，无需重新翻译');
            console.log('✅ 所有台词都已翻译，跳过全局重新翻译');
            return;
        }
        
        console.log(`📋 过滤后的触发列表，共 ${subtitlesToTranslate.length} 条需要翻译的台词:`, 
            subtitlesToTranslate.map(item => ({
                id: item.subtitle.id,
                originText: item.subtitle.origin_text,
                postText: item.subtitle.post_text,
                index: item.index
            }))
        );
        
        // 步骤2: 初始化需要翻译的台词的处理状态
        const initialStates: Record<number, { isRetranslating: boolean; isRedubbing: boolean }> = {};
        subtitlesToTranslate.forEach(({ subtitle }) => {
            initialStates[subtitle.id] = { isRetranslating: true, isRedubbing: false };
        });
        setSubtitleProcessingStates(initialStates);
        
        // 确保状态更新完成后再开始处理
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            // 步骤3: 根据触发列表执行重新翻译
            const promises = subtitlesToTranslate.map(async ({ subtitle, index }) => {
                try {
                    // 调用翻译API，获取翻译结果（如果 customText 为空，使用默认值）
                    const response = await retranslateSubtitleApi(subtitle.id, customText || undefined);
                    
                    // 更新单个台词的状态为完成
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译完成，更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRetranslating: false }
                    }));
                    
                    // 同步更新右侧台词输入框（targetText）和底部时间轴
                    // 只更新这两个地方，确保按id更新，不混乱
                    if (response && response.post_text !== undefined) {
                        // 1. 更新 targetText 中的 post_text
                        setTargetText(prev => prev.map(item => {
                            if (item.id === subtitle.id) {
                                return {
                                    ...item,
                                    text: response.post_text || ''
                                };
                            }
                            return item;
                        }));
                        
                        // 2. 更新底部时间轴中的翻译后轨道
                        if (timelineEditorRef.current) {
                            // 构建完整的字幕数据用于更新时间轴
                            const subtitleData: Task.SubtitlesLegacy = {
                                ...subtitle,
                                post_text: response.post_text || '',
                                post_start_time: response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time,
                                post_end_time: response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time,
                                // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                trans_audio: ''
                            };
                            timelineEditorRef.current.updateSegment(subtitle.id, subtitleData);
                        }
                        
                        // 3. 更新 videoData.subtitles 中的 post_text / 时间戳 / trans_audio
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            return {
                                ...prev,
                                subtitles: prev.subtitles.map(sub => 
                                    sub.id === subtitle.id 
                                        ? {
                                            ...sub,
                                            post_text: response.post_text || '',
                                            post_start_time: response.post_start_time || sub.post_start_time || sub.origin_start_time,
                                            post_end_time: response.post_end_time || sub.post_end_time || sub.origin_end_time,
                                            // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                            trans_audio: ''
                                        }
                                        : sub
                                )
                            };
                        });
                        
                        console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译结果已同步到右侧输入框和时间轴`);
                    }
                    
                    return { subtitleId: subtitle.id, index, success: true };
                } catch (error) {
                    console.error(`重新翻译字幕 ${subtitle.id} (索引 ${index}) 失败:`, error);
                    // 更新单个台词的状态为完成（即使失败也要取消禁用状态）
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译失败，但仍更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRetranslating: false }
                    }));
                    return { subtitleId: subtitle.id, index, success: false, error };
                }
            });

            const results = await Promise.all(promises);
            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                showSuccess(`成功重新翻译 ${successCount} 条字幕${failCount > 0 ? `，${failCount} 条失败` : ''}`);
                console.log(`✅ 全局翻译完成，共处理 ${subtitlesToTranslate.length} 条目标台词为空的字幕，${successCount} 条成功，${failCount} 条失败`);
            } else {
                showError('重新翻译失败，请重试');
            }
        } catch (error) {
            console.error('全局重新翻译失败:', error);
            showError('重新翻译失败，请重试');
        } finally {
            setIsGlobalRetranslating(false);
            // 清除所有台词的处理状态
            setSubtitleProcessingStates({});
        }
    };

    // 刷新字幕列表（重新获取/video/subtitles/list接口）
    const handleRefreshSubtitles = useCallback(async () => {
        if (!id) {
            console.warn('视频ID不存在，无法刷新字幕列表');
            return;
        }

        try {
            const subtitlesResponse = await getSubtitlesListApi(parseInt(id));
            if (subtitlesResponse.code === 0 && subtitlesResponse.data) {
                // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，保持兼容性）
                const subtitlesLegacy: Task.SubtitlesLegacy[] = subtitlesResponse.data.map((subtitle: Task.Subtitles) => ({
                    task_id: subtitle.taskId,
                    origin_text: subtitle.originText,
                    post_text: subtitle.postText || '',
                    post_start_time: subtitle.postStartTime || '',
                    post_end_time: subtitle.postEndTime || '',
                    origin_start_time: subtitle.originStartTime,
                    origin_end_time: subtitle.originEndTime,
                    origin_post_time: subtitle.originPostTime,
                    is_episode_voice: subtitle.isEpisodeVoice || false,
                    origin_audio: subtitle.originAudio,
                    trans_audio: subtitle.transAudio || '',
                    subtitle_id: subtitle.subtitleId || subtitle.id,
                    score: subtitle.score,
                    audio_speed: subtitle.audioSpeed || 1.0,
                    volume_gain_db: subtitle.volumeGainDb || 1.0,
                    id: subtitle.id,
                    speakers: {
                        id: subtitle.speaker.id,
                        series_id: subtitle.speaker.seriesId,
                        task_id: subtitle.taskId,
                        speaker_name: subtitle.speaker.speakerName,
                        description: subtitle.speaker.description || '',
                        is_series_speaker: subtitle.speaker.isSeriesSpeaker
                    },
                    voice_info: {
                        origin_text: subtitle.originText,
                        origin_start_time: subtitle.originStartTime,
                        origin_end_time: subtitle.originEndTime,
                        origin_audio: subtitle.originAudio,
                        score: subtitle.score
                    },
                    back_translate_text: subtitle.backTranslateText || '',
                    words: subtitle.words?.map(word => ({
                        id: word.id,
                        subtitle_id: word.subtitleId,
                        position: word.position,
                        origin_char: word.originChar,
                        start_ms: word.startMs,
                        end_ms: word.endMs
                    })) || [],
                    notes: subtitle.remark || undefined
                }));

                // 更新videoData中的subtitles
                if (videoData) {
                    setVideoDataSafe({
                        ...videoData,
                        subtitles: subtitlesLegacy
                    });
                    console.log('✅ 字幕列表已更新，数量:', subtitlesLegacy.length);
                }
            }
        } catch (error) {
            console.error('❌ 重新获取字幕列表失败:', error);
            throw error;
        }
    }, [id, videoData, setVideoDataSafe]);

    // 全局重新配音处理
    const handleGlobalRedub = async () => {
        if (!videoData || !videoData.subtitles || isGlobalRedubbing) {
            return;
        }

        setIsGlobalRedubbing(true);
        
        // 步骤1: 过滤出目标音频(transAudio)为空的台词，创建触发列表
        const subtitlesToRedub: Array<{
            subtitle: Task.SubtitlesLegacy;
            index: number;
        }> = [];
        
        videoData.subtitles.forEach((subtitle, index) => {
            // 判断目标音频是否为空（trans_audio为空、null、undefined或只包含空白字符）
            const transAudio = subtitle.trans_audio || '';
            const isEmpty = !transAudio.trim();
            
            if (isEmpty) {
                subtitlesToRedub.push({
                    subtitle,
                    index
                });
            }
        });
        
        // 如果没有需要配音的台词，直接返回
        if (subtitlesToRedub.length === 0) {
            setIsGlobalRedubbing(false);
            showSuccess('所有台词都已配音，无需重新配音');
            console.log('✅ 所有台词都已配音，跳过全局重新配音');
            return;
        }
        
        console.log(`📋 过滤后的触发列表，共 ${subtitlesToRedub.length} 条需要配音的台词:`, 
            subtitlesToRedub.map(item => ({
                id: item.subtitle.id,
                originText: item.subtitle.origin_text,
                transAudio: item.subtitle.trans_audio,
                index: item.index
            }))
        );
        
        // 步骤2: 初始化需要配音的台词的处理状态
        const initialStates: Record<number, { isRetranslating: boolean; isRedubbing: boolean }> = {};
        subtitlesToRedub.forEach(({ subtitle }) => {
            initialStates[subtitle.id] = { isRetranslating: false, isRedubbing: true };
        });
        setSubtitleProcessingStates(initialStates);
        
        // 确保状态更新完成后再开始处理
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            // 步骤3: 根据触发列表执行重新配音
            const promises = subtitlesToRedub.map(async ({ subtitle, index }) => {
                try {
                    // 调用配音API，获取配音结果
                    const response = await audioRegenerateApi(subtitle.id);
                    
                    // 更新单个台词的状态为完成
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音完成，更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRedubbing: false }
                    }));
                    
                    // 同步更新右侧台词输入框（targetText）和底部时间轴
                    // 只更新这两个地方，确保按id更新，不混乱
                    if (response) {
                        // 1. 如果 post_text 有变化，更新 targetText 中的 post_text
                        if (response.post_text !== undefined && response.post_text !== subtitle.post_text) {
                            setTargetText(prev => prev.map(item => {
                                if (item.id === subtitle.id) {
                                    return {
                                        ...item,
                                        text: response.post_text || ''
                                    };
                                }
                                return item;
                            }));
                        }
                        
                        // 2. 如果时间戳有变化，更新 targetText 中的时间范围
                        const newTimeRange = `${response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time} → ${response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time}`;
                        setTargetText(prev => {
                            const currentTargetData = prev.find(item => item.id === subtitle.id);
                            if (currentTargetData && currentTargetData.timeRange !== newTimeRange) {
                                return prev.map(item => {
                                    if (item.id === subtitle.id) {
                                        return {
                                            ...item,
                                            timeRange: newTimeRange
                                        };
                                    }
                                    return item;
                                });
                            }
                            return prev;
                        });
                        
                        // 3. 更新底部时间轴中的翻译后轨道
                        if (timelineEditorRef.current) {
                            // 构建完整的字幕数据用于更新时间轴
                            const subtitleData: Task.SubtitlesLegacy = {
                                ...subtitle,
                                post_text: response.post_text || subtitle.post_text || '',
                                post_start_time: response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time,
                                post_end_time: response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time,
                                trans_audio: response.trans_audio || subtitle.trans_audio || ''
                            };
                            timelineEditorRef.current.updateSegment(subtitle.id, subtitleData);
                        }
                        
                        // 4. 更新 videoData.subtitles 中的 trans_audio 和其他可能变化的字段
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            
                            return {
                                ...prev,
                                subtitles: prev.subtitles.map(sub => {
                                    if (sub.id === subtitle.id) {
                                        return {
                                            ...sub,
                                            trans_audio: response.trans_audio || sub.trans_audio || '',
                                            post_text: response.post_text !== undefined ? response.post_text : sub.post_text,
                                            post_start_time: response.post_start_time || sub.post_start_time || sub.origin_start_time,
                                            post_end_time: response.post_end_time || sub.post_end_time || sub.origin_end_time
                                        };
                                    }
                                    return sub;
                                })
                            };
                        });
                        
                        console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音结果已同步到右侧输入框和时间轴`);
                    }
                    
                    return { subtitleId: subtitle.id, index, success: true };
                } catch (error) {
                    console.error(`重新配音字幕 ${subtitle.id} (索引 ${index}) 失败:`, error);
                    // 更新单个台词的状态为完成（即使失败也要取消禁用状态）
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音失败，但仍更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRedubbing: false }
                    }));
                    return { subtitleId: subtitle.id, index, success: false, error };
                }
            });

            const results = await Promise.all(promises);
            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                showSuccess(`成功重新配音 ${successCount} 条字幕${failCount > 0 ? `，${failCount} 条失败` : ''}`);
                console.log(`✅ 全局配音完成，共处理 ${subtitlesToRedub.length} 条目标音频为空的字幕，${successCount} 条成功，${failCount} 条失败`);
            } else {
                showError('重新配音失败，请重试');
            }
        } catch (error) {
            console.error('全局重新配音失败:', error);
            showError('重新配音失败，请重试');
        } finally {
            setIsGlobalRedubbing(false);
            // 清除所有台词的处理状态
            setSubtitleProcessingStates({});
        }
    };
    // 这些函数现在由每行的 RowItem 组件独立处理
    return (
        <div className="video-edit-page">
            {/* 倍速调节加载遮罩 */}
            {isAdjustingSpeed && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    pointerEvents: 'auto'
                }}>
                    <div className="spinner" style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '4px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                </div>
            )}
            
            {/* Alert消息 */}
            <AlertMessage
                config={alertConfig}
                onClose={hideAlert}
                zIndex={1001}
            />

            {/* 加载状态时显示骨架屏 */}
            {isLoadingVideo ? (
                <VideoEditSkeleton />
            ) : videoData?.status === 3 ? (
                /* 视频处理失败状态 */
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100vh - 60px)',
                    padding: '20px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        borderRadius: '8px',
                        padding: '40px',
                        maxWidth: '500px',
                        width: '100%'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            color: '#ff4d4f',
                            marginBottom: '20px'
                        }}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h2 style={{
                            color: '#ff4d4f',
                            marginBottom: '16px',
                            fontSize: '20px'
                        }}>
                            视频处理失败
                        </h2>
                        <p style={{
                            color: '#666',
                            marginBottom: '24px',
                            lineHeight: '1.6'
                        }}>
                            抱歉，该视频处理过程中出现错误，无法进入编辑页面。
                            <br />
                            请返回视频列表，重新上传或删除该视频。
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={handleBack}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                返回视频列表
                            </button>
                            <button
                                onClick={() => {
                                    // 刷新页面，重新获取视频状态
                                    window.location.reload();
                                }}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                重新检查状态
                            </button>
                        </div>
                    </div>
                </div>
            ) : videoData?.status === 0 || videoData?.status === 1 || videoData?.status === 4 ? (
                /* 视频处理中状态 */
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100vh - 60px)',
                    padding: '20px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: '8px',
                        padding: '40px',
                        maxWidth: '500px',
                        width: '100%'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            color: '#52c41a',
                            marginBottom: '20px'
                        }}>
                            <i className="fas fa-spinner fa-spin"></i>
                        </div>
                        <h2 style={{
                            color: '#52c41a',
                            marginBottom: '16px',
                            fontSize: '20px'
                        }}>
                            {videoData?.status === 0 ? '视频等待中' : 
                             videoData?.status === 4 ? '视频排队中' : '视频处理中'}
                        </h2>
                        <p style={{
                            color: '#666',
                            marginBottom: '24px',
                            lineHeight: '1.6'
                        }}>
                            {videoData?.status === 0 ? '视频正在等待处理，请稍候...' : 
                             videoData?.status === 4 ? '视频正在排队中，请稍候...' : '视频正在处理中，请稍候...'}
                            <br />
                            处理完成后即可进入编辑页面。
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={handleBack}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                返回视频列表
                            </button>
                            <button
                                onClick={() => {
                                    // 刷新页面，重新获取视频状态
                                    window.location.reload();
                                }}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#52c41a',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                刷新状态
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* 背景音乐音量调节弹窗 */}
                    <VolumeAdjustmentPopup
                        visible={showBackgroundVolumePopup}
                        onClose={() => setShowBackgroundVolumePopup(false)}
                        onConfirm={async (volume) => {
                            // 1. 先本地预览：通过 Web Audio 的 GainNode 放大背景音
                            setBackgroundVolume(volume);
                            await ensureBackgroundGainNode();
                            if (backgroundGainNodeRef.current) {
                                backgroundGainNodeRef.current.gain.value = volume;
                                console.log('🔊 背景音预览增益更新为:', volume);
                            }

                            // 2. 调用后端接口调节任务背景音音量
                            if (!videoData?.id) {
                                console.error('调节背景音失败：缺少任务ID');
                                showError('调节背景音失败：缺少任务ID');
                                return;
                            }

                            try {
                                // 这里将弹窗中的数值直接作为 volumeGainDb 传给后端
                                const response = await adjustTaskVolumeApi({
                                    id: videoData.id,
                                    volumeGainDb: volume,
                                });

                                if (response.code === 0 && response.data) {
                                    showSuccess('背景音音量已更新');
                                } else {
                                    console.error('调节背景音失败：', response);
                                    showError(response.msg || '调节背景音失败');
                                }
                            } catch (error: any) {
                                console.error('调用 /video/subtitles/adjust-task-volume 接口失败：', error);
                                showError(error?.message || '调节背景音失败，请稍后重试');
                            }
                        }}
                        initialVolume={backgroundVolume}
                        position={backgroundVolumePopupPosition}
                        loading={false}
                        showApplyToAll={false}
                    />

                    {/* 主要内容区域 */}
                    <div className='caozuo-title'>
                        <BackButton onClick={handleBack} />
                        <span>{videoName}</span>
                        <div className="generate-button-container">
                            {isFromPure ? (
                                <button 
                                    className="generate-btn" 
                                    onClick={handleProofread}
                                    disabled={isGenerating}
                                >
                                    {isGenerating && <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>}
                                    {isGenerating ? '校对中...' : '已校对'}
                                </button>
                            ) : (
                                <button 
                                    className="generate-btn" 
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                >
                                    {isGenerating && <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>}
                                    {isGenerating ? '合成中...' : '合成'}
                                </button>
                            )}
                        </div>
                    </div>
                    <main className="video-edit-content">
                        <div className="video-left">

                            <div className="top">视频预览</div>
                            <div className="video-show">
                                <div className="video-show-left video-show-item">
                                    {/* 只有非管理员用户才显示水印 */}
                                    {!isAdmin ? (
                                        <Watermark 
                                            content={displayName} 
                                            className="watermark"
                                            font={{ color: 'rgba(255, 255, 255, 0.27)', fontSize: 25 }}
                                            gap={[100, 100]}
                                            offset={[50, 50]}
                                        >
                                            <div style={{ width: '100%', height: '100%'}}>
                                                {/* <p>原始视频</p> */}
                                                {/* 视频播放器 - 始终渲染视频元素，像右侧视频一样 */}
                                                <video
                                        ref={sourceVideoRef}
                                        src={videoData?.video_hls_url?.includes('.m3u8') ? undefined : (videoData?.video_hls_url || undefined)}
                                        onTimeUpdate={handleTimeUpdate}
                                        onLoadedMetadata={handleLoadedMetadata}
                                        onPlay={() => {
                                            setIsPlaying(true);
                                            
                                            // 如果不是用户手动操作，则确保音频播放并校对
                                            if (!userInteractionRef.current) {
                                                ensureAudioPlayAndSync();
                                            }
                                            // 重置用户操作标记
                                            userInteractionRef.current = false;
                                        }}
                                        onPause={() => {
                                            setIsPlaying(false);
                                            
                                            // 如果不是用户手动操作，则暂停音频
                                            if (!userInteractionRef.current) {
                                                if (backgroundAudioRef.current) {
                                                    backgroundAudioRef.current.pause();
                                                }
                                            }
                                            // 无论是否是用户手动操作，都要停止原人声音频
                                            // 因为用户手动操作时，togglePlay已经处理了其他音频，但为了确保一致性，这里也处理
                                            if (vocalsAudioRef.current) {
                                                vocalsAudioRef.current.pause();
                                            }
                                            if (vocalsHlsRef.current) {
                                                vocalsHlsRef.current.destroy();
                                                vocalsHlsRef.current = null;
                                            }
                                            // 重置用户操作标记
                                            userInteractionRef.current = false;
                                        }}
                                        onError={(e) => {
                                            console.error('【左侧视频显示】左侧视频加载错误:', e);
                                            // 视频加载失败时，重新初始化视频
                                            if (sourceVideoRef.current && videoData?.video_hls_url) {
                                                // 左侧视频加载失败，重新初始化
                                                // 延迟一点时间后重新初始化
                                                setTimeout(() => {
                                                    // 重新初始化左侧视频
                                                    const event = new Event('videoDataUpdated');
                                                    window.dispatchEvent(event);
                                                }, 1000);
                                            }
                                        }}
                                        onLoadStart={() => {
                                            // 视频开始加载
                                        }}
                                        onCanPlay={() => {
                                            // 视频可以播放
                                        }}
                                        style={{ width: '96%', objectFit: 'contain' }}
                                    />
                                    
                                    {/* 加载状态覆盖层 */}
                                    {isLoadingVideo && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                            color: 'white',
                                            zIndex: 10
                                        }}>
                                            加载视频中...
                                        </div>
                                    )}
                                    
                                    {/* 无数据状态覆盖层 */}
                                    {!isLoadingVideo && !videoData?.video_hls_url && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                            color: 'white',
                                            zIndex: 10
                                        }}>
                                            <p style={{ marginBottom: '16px' }}>暂无视频数据</p>
                                            <button 
                                                onClick={() => {
                                                    const refetchVideoData = async () => {
                                                        try {
                                                            setIsLoadingVideo(true);
                                                            // 手动重新获取视频数据
                                                            const taskData = await getTaskStatusApi(parseInt(id!));
                                                            // 重新获取视频数据成功
                                                            setVideoDataSafe(taskData);
                                                            
                                                            // 延迟一点时间确保状态更新完成，然后重新初始化HLS
                                                            setTimeout(() => {
                                                                // 手动重新获取后数据更新完成，触发HLS重新初始化
                                                                const event = new Event('videoDataUpdated');
                                                                window.dispatchEvent(event);
                                                            }, 100);
                                                        } catch (error) {
                                                            console.error('【左侧视频显示】重新获取视频数据失败:', error);
                                                        } finally {
                                                            setIsLoadingVideo(false);
                                                        }
                                                    };
                                                    refetchVideoData();
                                                }}
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                重新获取视频
                                            </button>
                                        </div>
                                    )}
                                            </div>
                                        </Watermark>
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'end' }}>
                                            {/* <p>原始视频</p> */}
                                            {/* 视频播放器 - 始终渲染视频元素，像右侧视频一样 */}
                                            <video
                                                ref={sourceVideoRef}
                                                src={videoData?.video_hls_url?.includes('.m3u8') ? undefined : (videoData?.video_hls_url || undefined)}
                                                onTimeUpdate={handleTimeUpdate}
                                                onLoadedMetadata={handleLoadedMetadata}
                                                onPlay={() => {
                                                    setIsPlaying(true);
                                                    // 如果不是用户手动操作，则确保音频播放并校对
                                                    if (!userInteractionRef.current) {
                                                        // console.log('视频自动播放：确保音频播放并校对');
                                                        ensureAudioPlayAndSync();
                                                    }
                                                    // 重置用户操作标记
                                                    userInteractionRef.current = false;
                                                }}
                                                onPause={() => {
                                                    setIsPlaying(false);
                                                    // 如果不是用户手动操作，则暂停音频
                                                    if (!userInteractionRef.current) {
                                                        // console.log('视频自动暂停：暂停音频');
                                                        if (backgroundAudioRef.current) {
                                                            backgroundAudioRef.current.pause();
                                                        }
                                                    }
                                                    // 重置用户操作标记
                                                    userInteractionRef.current = false;
                                                }}
                                                onError={(e) => {
                                                    console.error('【左侧视频显示】左侧视频加载错误:', e);
                                                    // 视频加载失败时，重新初始化视频
                                                    if (sourceVideoRef.current && videoData?.video_hls_url) {
                                                        // 左侧视频加载失败，重新初始化
                                                        // 延迟一点时间后重新初始化
                                                        setTimeout(() => {
                                                            // 重新初始化左侧视频
                                                            const event = new Event('videoDataUpdated');
                                                            window.dispatchEvent(event);
                                                        }, 1000);
                                                    }
                                                }}
                                                onLoadStart={() => {
                                                    // 视频开始加载
                                                }}
                                                onCanPlay={() => {
                                                    // 视频可以播放
                                                }}
                                                style={{ width: '96%', objectFit: 'contain' }}
                                            />
                                            
                                            {/* 加载状态覆盖层 */}
                                            {isLoadingVideo && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    color: 'white',
                                                    zIndex: 10
                                                }}>
                                                    加载视频中...
                                                </div>
                                            )}
                                            
                                            {/* 无数据状态覆盖层 */}
                                            {!isLoadingVideo && !videoData?.video_hls_url && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    color: 'white',
                                                    zIndex: 10
                                                }}>
                                                    <p style={{ marginBottom: '16px' }}>暂无视频数据</p>
                                                    <button 
                                                        onClick={() => {
                                                            const refetchVideoData = async () => {
                                                                try {
                                                                    setIsLoadingVideo(true);
                                                                    // 手动重新获取视频数据
                                                                    const taskData = await getTaskStatusApi(parseInt(id!));
                                                                    // 重新获取视频数据成功
                                                                    setVideoDataSafe(taskData);
                                                                    
                                                                    // 延迟一点时间确保状态更新完成，然后重新初始化HLS
                                                                    setTimeout(() => {
                                                                        // 手动重新获取后数据更新完成，触发HLS重新初始化
                                                                        const event = new Event('videoDataUpdated');
                                                                        window.dispatchEvent(event);
                                                                    }, 100);
                                                                } catch (error) {
                                                                    console.error('【左侧视频显示】重新获取视频数据失败:', error);
                                                                } finally {
                                                                    setIsLoadingVideo(false);
                                                                }
                                                            };
                                                            refetchVideoData();
                                                        }}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#007bff',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        重新获取视频
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="video-show-right video-show-item">
                                    {/* 只有非管理员用户才显示水印 */}
                                    {!isAdmin ? (
                                        <Watermark 
                                            content={displayName} 
                                            className="watermark"
                                            font={{ color: 'rgba(255, 255, 255, 0.27)', fontSize: 25 }}
                                            gap={[100, 100]}
                                            offset={[50, 50]}
                                        >
                                            <div style={{ width: '100%', height: '100%' }}>
                                                {/* <p>翻译后视频</p> */}
                                                {isLoadingVideo ? (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        height: '100%',
                                                        color: '#666'
                                                    }}>
                                                        加载视频中...
                                                    </div>
                                                ) : (
                                                
                                                    <SubtitleDemo 
                                                        key={`subtitle-demo-${id || 'default'}`}
                                                        ref={subtitleDemoRef}
                                                        videoData={videoDataForDemo}
                                                        localSubtitles={localSubtitlesForDemo} // 使用内容更新但引用稳定的数组
                                                        targetText={targetTextForDemo} // 使用注入了最新数据的稳定数组引用
                                                        overlayTopPx={isAdmin ? 10 : 0}
                                                        externalIsPlaying={isPlaying}
                                                        externalCurrentTime={currentTime}
                                                        onExternalPlay={handleExternalPlay}
                                                        onExternalPause={handleExternalPause}
                                                        onExternalSeek={handleExternalSeek}
                                                        onRightVideoReady={handleRightVideoReady}
                                                    ></SubtitleDemo>
                                                )}
                                            </div>
                                        </Watermark>
                                    ) : (
                                        <div style={{ width: '100%', height: '100%' }}>
                                            {/* <p>翻译后视频</p> */}
                                            {isLoadingVideo ? (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    color: '#666'
                                                }}>
                                                    加载视频中...
                                                </div>
                                            ) : (
                                            
                                                <SubtitleDemo 
                                                    key={`subtitle-demo-${id || 'default'}`}
                                                    ref={subtitleDemoRef}
                                                    videoData={videoDataForDemo}
                                                    localSubtitles={localSubtitlesForDemo} // 使用内容更新但引用稳定的数组
                                                    targetText={targetTextForDemo} // 使用注入了最新数据的稳定数组引用
                                                    overlayTopPx={isAdmin ? 10 : 0}
                                                    externalIsPlaying={isPlaying}
                                                    externalCurrentTime={currentTime}
                                                    onExternalPlay={handleExternalPlay}
                                                    onExternalPause={handleExternalPause}
                                                    onExternalSeek={handleExternalSeek}
                                                    onRightVideoReady={handleRightVideoReady}
                                                ></SubtitleDemo>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="video-right">
                            <div className="top">{isFromPure ? '说话人与字幕校对' : '台词编辑'}</div>
                            {!isFromPure && (
                            <div className="video-title">
                                <div></div>
                                {/* 自动检测盒子 - 包含全局重新翻译按钮 */}
                                <div className='firstP-container'>
                                    <p className='firstP'>自动检测</p>
                                    <div className="global-retranslate-container">
                                        <div 
                                            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                                            onMouseEnter={() => {
                                                // 鼠标进入整个容器（包括箭头和下拉菜单）时显示下拉菜单
                                                setIsArrowHovered(true);
                                            }}
                                            onMouseLeave={() => {
                                                // 鼠标离开整个容器时隐藏下拉菜单
                                                setIsArrowHovered(false);
                                                setShowGlobalRetranslateDropdown(false);
                                            }}
                                        >
                                            <button
                                                className={`global-retranslate-btn ${isGlobalRetranslating ? 'disabled' : ''}`}
                                                onClick={() => {
                                                    // 点击按钮直接调用接口，传入默认的feedback
                                                    handleGlobalRetranslate("维持原语义，贴合场景");
                                                }}
                                                disabled={isGlobalRetranslating}
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '8px'
                                                }}
                                            >
                                                {isGlobalRetranslating ? '翻译中...' : '重新翻译'}
                                                {/* 箭头图标，在按钮内部 */}
                                                <i 
                                                    className={`fas ${isArrowHovered ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                                    onMouseEnter={() => {
                                                        // 鼠标进入箭头时显示下拉菜单
                                                        setIsArrowHovered(true);
                                                        setShowGlobalRetranslateDropdown(true);
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // 点击箭头不触发按钮的onClick
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s ease',
                                                        fontSize: '12px',
                                                        color: '#fff', // 箭头显示白色
                                                        pointerEvents: 'auto' // 确保箭头可以接收鼠标事件
                                                    }}
                                                />
                                            </button>
                                            
                                            {/* 悬浮框组件，放在容器内，距离按钮很近 */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 'calc(100% + 2px)', // 距离按钮2px，更近
                                                    left: '0',
                                                    zIndex: 1001,
                                                    pointerEvents: 'auto' // 确保下拉菜单可以接收鼠标事件
                                                }}
                                            >
                                                <TranslationAdjustment
                                                    showDropdown={showGlobalRetranslateDropdown}
                                                    isTranslating={isGlobalRetranslating}
                                                    onDropdownChange={setShowGlobalRetranslateDropdown}
                                                    onTranslate={handleGlobalRetranslate}
                                                    customText={globalCustomText}
                                                    onCustomTextChange={setGlobalCustomText}
                                                    buttonText="重新翻译"
                                                    buttonClassName="global-retranslate-btn"
                                                    dropdownOffset={{ top: '0', left: '0' }}
                                                    customCardOffset={{ top: '0', left: '120px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* 目标语言盒子 - 包含一键回译和全局重新配音按钮 */}
                                <div className='secondP-container'>
                                    <p className='secondP'>
                                        {getLanguageName(videoData?.langCode || videoData?.lang_code || currentEpisodeInfo.targetLanguage) || '未知语言'}
                                    </p>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <button
                                            className={`back-translate-btn ${isBackTranslating ? 'disabled' : ''}`}
                                            onClick={async () => {
                                                // 调用接口获取回译数据并更新到本地
                                                try {
                                                    if (videoData?.id) {
                                                        setIsBackTranslating(true);
                                                        const response = await backTranslateApi(videoData.id);
                                                        if (response.code === 0 && response.data) {
                                                            // 更新 backTranslateData
                                                            setBackTranslateData(response.data);
                                                            
                                                            // 同步更新 videoData.subtitles 中的 back_translate_text 字段
                                                            setVideoData(prev => {
                                                                if (!prev?.subtitles) return prev;
                                                                
                                                                const updatedSubtitles = prev.subtitles.map(subtitle => {
                                                                    const backTranslateItem = response.data.find(item => item.id === subtitle.id);
                                                                    if (backTranslateItem?.backTranslateText) {
                                                                        return {
                                                                            ...subtitle,
                                                                            back_translate_text: backTranslateItem.backTranslateText
                                                                        };
                                                                    }
                                                                    return subtitle;
                                                                });
                                                                
                                                                return {
                                                                    ...prev,
                                                                    subtitles: updatedSubtitles
                                                                };
                                                            });
                                                            
                                                            showSuccess('回译成功');
                                                        } else {
                                                            showError(response.msg || '回译失败');
                                                        }
                                                    }
                                                } catch (error: any) {
                                                    console.error('一键回译失败:', error);
                                                    showError(error?.message || '回译失败，请重试');
                                                } finally {
                                                    setIsBackTranslating(false);
                                                }
                                            }}
                                            disabled={isBackTranslating}
                                        >
                                            {isBackTranslating ? '回译中...' : '一键回译'}
                                        </button>
                                        <button
                                            className={`global-redub-btn ${isGlobalRedubbing ? 'disabled' : ''}`}
                                            onClick={handleGlobalRedub}
                                            disabled={isGlobalRedubbing}
                                        >
                                            {isGlobalRedubbing ? '配音中...' : '重新配音'}
                                        </button>
                                      </div>
                                  </div>
                                  
                                 <p className='thirdP'>留言</p>
                              </div>
                            )}
                            <div className="text-list" ref={textListRef}>
                                {sourcesText.length > 0 ? (
                                    <>
                                        {sourcesText.map((sourceItem, index) => {
                                            // 从videoData.subtitles中获取完整的字幕数据
                                            const fullSubtitleData = videoData?.subtitles?.find(sub => sub.id === sourceItem.id);
                                            const targetItem = targetText[index] || {
                                                id: sourceItem.id,
                                                text: '',
                                                timeRange: sourceItem.timeRange,
                                                subtitle_id: fullSubtitleData?.subtitle_id || fullSubtitleData?.id
                                            };
                                            
                                            return (
                                        <RowItem
                                            key={sourceItem.id}
                                            sourceData={sourceItem}
                                            targetData={targetItem}
                                            commentText={(fullSubtitleData as any)?.notes || ''}
                                            onSourceDataChange={handleSourceDataChange}
                                            onTargetDataChange={handleTargetDataChange}
                                            roles={roles}
                                            selectedRole={rowSelectedRoles[sourceItem.id] || (videoData?.subtitles?.find(sub => sub.id === sourceItem.id)?.speakers?.speaker_name) || '未知说话人'}
                                            onRoleSelect={handleRoleSelect}
                                            onNewSpeaker={handleNewSpeaker}
                                            onDeleteRole={handleDeleteRole}
                                            showNewSpeaker={true}
                                            subtitleData={fullSubtitleData as Subtitles.GetSubtitleResponse | undefined}
                                            onGetCurrentVolume={(subtitleId) => volumeMap.get(subtitleId) || fullSubtitleData?.volume_gain_db || 1.0}
                                            onUpdateVolume={handleUpdateVolume}
                                            onGetCurrentSpeed={(subtitleId) => speedMap.get(subtitleId) || fullSubtitleData?.audio_speed || 1.0}
                                            onUpdateSpeed={handleUpdateSpeed}
                                            editingRoleKey={editingRoleKey}
                                            editingRoleName={editingRoleName}
                                            onStartEditRole={handleStartEditRole}
                                            onSaveRoleEdit={handleSaveRoleEdit}
                                            onCancelRoleEdit={handleCancelRoleEdit}
                                            onEditingRoleNameChange={setEditingRoleName}
                                            seriesSpeakers={seriesSpeakers}
                                            onPromoteToSeriesSpeaker={handlePromoteToSeriesSpeaker}
                                            onSeekToTime={seekToTime}
                                            highlightedSubtitleId={highlightedSubtitleId}
                                            hideTargetAndComment={isFromPure}
                                            isPureMode={isFromPure}
                                            isGlobalRetranslating={isGlobalRetranslating}
                                            isGlobalRedubbing={isGlobalRedubbing}
                                            subtitleProcessingState={subtitleProcessingStates[sourceItem.id] || { isRetranslating: false, isRedubbing: false }}
                                            // 场景4：字幕编辑时的音频控制
                                            onAudioPause={pauseAudioForEditing}
                                            onAudioSync={syncAudioAfterEditing}
                                            // 剧集台词标记
                                            isSeriesSpeaker={seriesVoiceLibraryMarkers[sourceItem.id] || fullSubtitleData?.speakers?.is_series_speaker || false}
                                            // 台词播放功能
                                            onPlaySubtitle={playSubtitle}
                                            onPlayTargetSubtitle={playTargetSubtitle}
                                            // 更新videoData中的语速数据
                                            onUpdateVideoDataSpeed={handleUpdateVideoDataSpeed}
                                            // 字幕更新状态
                                            isUpdating={updatingSubtitles[sourceItem.id] || false}
                                            // 切换轨道回调函数
                                            onSwitchTrack={handleSwitchTrack}
                                            updateError={subtitleUpdateErrors[sourceItem.id]}
                                            // 剧集ID
                                            seriesId={videoData?.series_id}
                                            // 语言代码（用于获取指定语言的剧集说话人角色）
                                            langCode={videoData?.langCode || videoData?.lang_code || currentEpisodeInfo.targetLanguage}
                                            // 删除字幕回调
                                            onDeleteSubtitle={handleDeleteSubtitle}
                                            // 新增字幕回调
                                            onAddSubtitle={handleAddSubtitle}
                                            // 删除剧集说话人角色后的本地同步（添加至剧集音色库弹窗内）
                                            onDeleteSeriesSpeaker={(speakerId) => {
                                                setSeriesSpeakers(prev => prev.filter(s => s.id !== speakerId));
                                            }}
                                            // 回译相关（常显，优先使用 videoData 中的数据，如果没有则使用 backTranslateData）
                                            backTranslateText={
                                                (fullSubtitleData as any)?.back_translate_text || 
                                                backTranslateData.find(item => item.id === sourceItem.id)?.backTranslateText || 
                                                ''
                                            }
                                            // 字幕拆分更新回调
                                            onSubtitleUpdate={handleSubtitleUpdate}
                                            // 获取下一个台词信息回调
                                            onGetNextSubtitle={handleGetNextSubtitle}
                                            // 获取下一个字幕ID回调
                                            onGetNextSubtitleId={handleGetNextSubtitleId}
                                            // 获取完整时间轴数据回调
                                            onGetFullTimelineData={handleGetFullTimelineData}
                                            // 刷新字幕列表回调（更换音色后重新获取/video/subtitles/list接口）
                                            onRefreshSubtitles={handleRefreshSubtitles}
                                            // 历史记录面板相关
                                            isHistoryPanelOpen={openHistorySubtitleId === sourceItem.id}
                                            onOpenHistoryPanel={handleOpenHistoryPanel}
                                            onCloseHistoryPanel={handleCloseHistoryPanel}
                                        />
                                            );
                                        })}
                                        {/* 滚动到底部提示 */}
                                        <div className="scroll-end-indicator">
                                            ——&nbsp;&nbsp;已展示全部内容&nbsp;&nbsp;——
                                        </div>
                                    </>
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '200px',
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        {isLoadingVideo ? '加载字幕数据中...' : '暂无字幕数据'}
                                    </div>
                                )}
                            </div>
                        </div>


                    </main>
                    
                    {/* 播放控制器 */}
                    <div className="bofang">
                        {/* 左侧播放按钮 */}
                        <div className="left-controls">
                            <button
                                className="play-pause-btn"
                                onClick={togglePlay}
                            >
                                {isPlaying ? '⏸' : '▶'}
                            </button>
                            
                            {/* 当前时间和总时长 */}
                            <div className="time-display">
                                <div className="current-time">
                                    {formatTime(currentTime)}&nbsp;/
                                </div>
                                <div className="total-time">
                                    {formatTime(duration)}
                                </div>
                            </div>
                        </div>
                        
                        {/* 右侧控制区域 */}
                        <div className="right-controls">
                            {/* 缩放控制 */}
                            <div className="zoom-controls">
                                <button className="zoom-btn" onClick={handleZoomOut}>−</button>
                                <div className="zoom-slider-container">
                                    <Slider
                                        min={0}
                                        max={100}
                                        value={zoomLevel}
                                        onChange={handleZoomChange}
                                        onAfterChange={handleZoomAfterChange}
                                        tooltip={{ open: false }}
                                        style={{ width: '100px' }}
                                        step={1}
                                    />
                                </div>
                                <button className="zoom-btn" onClick={handleZoomIn}>+</button>
                            </div>
                            
                            {/* 背景音控制（纯净版隐藏） */}
                            {!isFromPure && (
                                <>
                                    <div className="audio-controls">
                                        <button 
                                            className={`audio-btn ${backgroundAudioEnabled ? 'active' : ''}`} 
                                            id="audioBtn" 
                                            onClick={toggleBackgroundAudio}
                                        >
                                            {backgroundAudioEnabled ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                                </svg>
                                            )}
                                        </button>
                                        <span className="audio-label">背景音</span>
                                    </div>
                                    
                                    {/* 背景音音量调节 */}
                                    <div
                                        className="background-audio-volume-controls"
                                        onClick={(e) => {
                                            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                            // 在按钮上方显示弹窗，略微偏上
                                            setBackgroundVolumePopupPosition({
                                                top: rect.top - 140,
                                                left: rect.left,
                                            });
                                            // 使用当前任务级背景音量初始化（由 /video/task/get 的 volumeGainDb 决定）
                                            const initialVolume = backgroundVolume || (videoData as any)?.volumeGainDb || 0;
                                            setBackgroundVolume(initialVolume);
                                            setShowBackgroundVolumePopup(true);
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                        </svg>
                                        <span className="volume-label">背景音音量调节</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="timeBox">
                        {/* 时间轴主体 */}
                        <div className="timeline-container">
                            {/* 左侧固定区域 - 轨道标签 */}
                            <div className="timeline-left-panel">
                                <div className="track-labels-container">
                                {!isFromPure && (
                                    <div className="track-label translated-track-label">
                                        <span>翻译后</span>
                                        <span className="track-audio-icon" aria-label="audio-indicator">
                                            {/* 音量图标（活动轨道显示） */}
                                            <svg className="icon-volume" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03z"/>
                                            </svg>
                                            {/* 静音图标（非活动轨道显示） */}
                                            <svg className="icon-muted" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM19 12c0 .94-.2 1.82-.54 2.62l1.5 1.5C20.62 14.96 21 13.52 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM2.1 3.51L1 4.61l6.36 6.36H3v4h4l5 5v-6.73l5.39 5.39 1.11-1.1L2.1 3.51z"/>
                                            </svg>
                                        </span>
                                    </div>
                                )}
                                    <div className="track-label original-track-label">
                                        <span>原视频</span>
                                        <span className="track-audio-icon" aria-label="audio-indicator">
                                            {/* 音量图标（活动轨道显示） */}
                                            <svg className="icon-volume" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03z"/>
                                            </svg>
                                            {/* 静音图标（非活动轨道显示） */}
                                            <svg className="icon-muted" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM19 12c0 .94-.2 1.82-.54 2.62l1.5 1.5C20.62 14.96 21 13.52 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM2.1 3.51L1 4.61l6.36 6.36H3v4h4l5 5v-6.73l5.39 5.39 1.11-1.1L2.1 3.51z"/>
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* 右侧滚动区域 - 时间轴刻度 */}
                            <div className="timeline-right-panel">
                                <div className="timeline-content">
                                    <div className="time-ruler">
                                        <div className="time-marks" id="timeMarks"></div>
                                    </div>
                                    
                                    <div className="tracks-container">
                                        {/* 播放进度线 */}
                                        <div className="playhead" id="playhead">
                                            <div className="playhead-line"></div>
                                        </div>
                                        {!isFromPure && (
                                            <div className="track" id="translatedTrack">
                                                <div className="track-content">
                                                    {/* 翻译后轨道片段将通过JavaScript动态生成 */}
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="track" id="originalTrack">
                                            <div className="track-content">
                                                {/* 原视频轨道片段将通过JavaScript动态生成 */}
                            </div>
                            {/* 帧同步驱动的播放头控制器 */}
                            <PlayheadController
                                videoRef={sourceVideoRef as React.RefObject<HTMLVideoElement | null>}
                                totalTime={timelineEditorRef.current?.totalTime || duration || 0}
                                timelineContentSelector=".timeline-content"
                            />
                        </div>
                    </div>
                                </div>
                                
                                {/* 底部滚动条 */}
                                <div className="timeline-scrollbar">
                                    <div className="scrollbar-track">
                                        <div className="scrollbar-thumb" id="scrollbarThumb"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* 生成中的遮罩层 */}
                    {isGenerating && (
                        <div 
                            className="generating-overlay"
                            onClick={(e) => e.preventDefault()}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseUp={(e) => e.preventDefault()}
                            onTouchStart={(e) => e.preventDefault()}
                            onTouchEnd={(e) => e.preventDefault()}
                        />
                    )}
                </>
            )}
            
            {/* 隐藏的音频元素 */}
            <audio 
                ref={backgroundAudioRef} 
                preload="auto" 
                crossOrigin="anonymous"
            />
            
            {/* 调试按钮 - 临时添加 */}
            {/* <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 9999, background: 'white', padding: '10px', border: '1px solid #ccc', minWidth: '300px' }}>
            
                <div style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>音频测试</h4>
                    <button onClick={playAudioTracks} style={{ margin: '5px', padding: '5px 10px' }}>
                        测试播放音频
                    </button>
                    <button onClick={() => {
                        if (backgroundAudioRef.current) {
                            backgroundAudioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
                            backgroundAudioRef.current.play().then(() => {
                                console.log('测试音频播放成功');
                            }).catch(error => {
                                console.error('测试音频播放失败:', error);
                            });
                        }
                    }} style={{ margin: '5px', padding: '5px 10px' }}>
                        测试简单音频
                    </button>
                </div>

            </div> */}
        </div>
    );
};

export default VideoEdit;